import { useState, useRef, useEffect, useLayoutEffect, useMemo, createContext, useContext } from "react";
import { createPortal } from "react-dom";
import { useTranslation, Trans } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import {
  listWorkspaces, createWorkspace, getWorkspaceFull,
  updateWorkspaceApi, addPropertyApi, updatePropertyApi,
  addUnitApi, deleteUnitApi, updateUnitApi, addViewingApi, updateViewingApi,
  type WsWorkspace, type WsProperty, type WsUnit, type WsViewing,
  type NegotiationOffer,
} from "@/lib/workspaceApi";
import { WorkspacePrefsBanner } from "@/components/WorkspacePrefsBanner";
import { WorkspaceSwitcher } from "@/components/WorkspaceSwitcher";
import { WorkspaceTour } from "@/components/WorkspaceTour";
import { ProjectDetailModal } from "@/components/ProjectDetailModal";
import { useIsMobile } from "@/hooks/useIsMobile";
import { STORAGE_EVENTS } from "@/constants/storage";
import { apiJson } from "@/lib/api";
import { renderMarkdown, stripMarkdown } from "@/lib/markdown";
import {
  BUDGET_BUCKETS, BUDGET_KEYS,
  PRIORITY_OPTS, TIMELINE_OPTS, bucketFromRange,
} from "@/lib/prefs-options";
import { areaLabel } from "@/lib/areaOptions";
import { AreaMultiSelect } from "@/components/AreaMultiSelect";

// ─── i18n context — avoids prop-drilling t() through every sub-component ─────
type TFn = (key: string, opts?: Record<string, unknown>) => string;
const WsTCtx = createContext<TFn>((k) => k);
const useWsT = () => useContext(WsTCtx);

// titleCaseSG — URA strings arrive UPPERCASE (e.g. "MEYERHOUSE",
// "ANG MO KIO", "KALLANG/WHAMPOA"). Convert at the display boundary
// per the project's titlecase rule; raw values stay untouched in state
// so they remain valid lookup keys against the data engine.
function titleCaseSG(s: string): string {
  if (!s) return s;
  return s
    .toLowerCase()
    .split(/(\s+|\/)/)
    .map((part) =>
      /^\s+$/.test(part) || part === "/" ? part : part.charAt(0).toUpperCase() + part.slice(1),
    )
    .join("");
}

// ─── Modal kinds ─────────────────────────────────────────────────────────────
type ModalKind =
  | { type: "addProperty" }
  | { type: "editWorkspace" }
  | { type: "addViewing"; propId?: string }
  | { type: "recordNotes"; viewingId: string }
  | { type: "addUnit"; propId: string }
  | { type: "editUnit"; propId: string; unitId: string }
  | { type: "addQuestion"; propId: string }
  | { type: "editQuestion"; propId: string; qIdx: number; prefillStatus?: string }
  | { type: "editProperty"; propId: string }
  | { type: "editLivability"; propId: string }
  | { type: "logMove"; propId: string; unitId: string };

// ─── Workspace data context ───────────────────────────────────────────────────
interface WsProject { name: string; intent: string; budget: string; budget_min: number | null; budget_max: number | null; areas: string; timeline: string; priorities: string[] }
interface WsData {
  project: WsProject;
  properties: Property[];
  viewings: Viewing[];
  openProperty: (id: string) => void;
  goToModule: (m: string) => void;
  openModal: (m: ModalKind) => void;
  updateProject: (patch: Partial<WsProject>) => void;
  addProperty: (p: Omit<Property, "id">) => void;
  updateProperty: (id: string, patch: Partial<Property>) => void;
  addViewing: (v: Omit<Viewing, "id">) => void;
  updateViewing: (id: string, patch: Partial<Viewing>) => void;
  addUnit: (propId: string, u: Unit) => void;
  updateUnit: (propId: string, unitId: string, patch: Partial<Unit>) => void;
  deleteUnit: (propId: string, unitId: string) => void;
  addQuestion: (propId: string, q: Question) => void;
  updateQuestion: (propId: string, qIdx: number, patch: Partial<Question>) => void;
  // AI compare summary served by the backend, plus the persisted
  // selection that drives it. setCompareUnitIds PATCHes the workspace
  // server-side; the next refresh loads the regenerated body.
  compareSummary: CompareSummary | null;
  compareUnitIds: string[];
  setCompareUnitIds: (ids: string[]) => void;
  // Manual refresh (used to poll for an AI summary regen completing
  // without doing a full route re-mount). Cheap — same GET that
  // bootstrap uses.
  refreshWorkspace: () => void;
}
const WsDataCtx = createContext<WsData>({
  project: { name: "", intent: "", budget: "—", budget_min: null, budget_max: null, areas: "—", timeline: "—", priorities: [] },
  properties: [], viewings: [],
  openProperty: () => {}, goToModule: () => {}, openModal: () => {},
  updateProject: () => {},
  addProperty: () => {}, updateProperty: () => {},
  addViewing: () => {}, updateViewing: () => {},
  addUnit: () => {}, updateUnit: () => {}, deleteUnit: () => {}, addQuestion: () => {}, updateQuestion: () => {},
  compareSummary: null, compareUnitIds: [], setCompareUnitIds: () => {},
  refreshWorkspace: () => {},
});
const useWsData = () => useContext(WsDataCtx);

// ─── Types ───────────────────────────────────────────────────────────────────

interface Question { q: string; status: string; units?: string[]; answer?: string }
interface Unit {
  id: string; label: string; floor: number; stack: string; facing: string;
  size: number; price: number; psf: number; maintenance: string; tenancy: string;
  tenancyEnd?: string; status: string;
  // Optional offer-history blob — present only when the unit is (or
  // has been) at status Negotiating. Server stores raw JSON; we keep
  // camelCase keys matching the Claude Design canvas.
  negotiation?: NegotiationOffer | null;
}
// MRTStation mirrors WsNearbyMRT from workspaceApi.ts. Kept local to
// Workspace.tsx (rather than imported) because the dossier consumes
// many small derived FE-side types here and keeping them colocated
// reads better.
interface MRTStation {
  station: string;
  type: string;       // "mrt_exit" | "lrt_exit"
  codes: string[];
  lines: string[];
  distance_m: number;
  walk_min: number;
  nearest_exit?: string;
}

interface Property {
  id: string; name: string; project: string; district: string; tenure: string;
  top: number; beds: string; mrt: string;
  // Structured replacement for `mrt` (the single freeform string).
  // Present after the dossier enricher fills it from project_details
  // + pois. The freeform `mrt` is kept for backward-compat with list
  // views that haven't been redesigned yet; the dossier uses
  // nearbyMRT directly.
  nearbyMRT?: MRTStation[];
  status: string; fitScore: number; aiSummary: string;
  pros: string[]; risks: string[];
  questions: Question[];
  units?: Unit[];
  livability?: { naturalLight?: string; noise?: string; layout?: string; wfh?: string };
  // Server-driven AI summary regen telemetry — see
  // doc/WORKSPACE_AI_SUMMARIES.md. summaryStale is true when the summary
  // worker has the row marked dirty (e.g. just after the user edited
  // pros/risks). FE renders a subtle "Generating…" hint until the next
  // GET refresh shows it cleared.
  summaryStale?: boolean;
  summaryGeneratedAt?: string | null;
}
interface MediaItem { type: "photo" | "video"; label: string; thumb: string; duration?: string }
interface Viewing {
  id: string; propId: string; unitLabel?: string; status: "scheduled" | "completed";
  date: string; time: string; agent: string;
  checklist?: { item: string; done: boolean }[];
  ratings?: { layout: number | null; light: number | null; noise: number | null; condition: number | null };
  tags?: string[]; notes?: string; aiSummary?: string; decision?: string | null;
  media?: MediaItem[];
  summaryStale?: boolean;
  summaryGeneratedAt?: string | null;
}

// Server-driven compare summary blurb. Body empty until the worker has
// produced one for the current selection. `stale` is true when the
// cached body is for a different selection or the worker is mid-regen.
interface CompareSummary {
  body: string;
  generated_at?: string | null;
  stale: boolean;
  unit_ids_hash: string;
}

// ─── Mock Data ────────────────────────────────────────────────────────────────

// ─── API → frontend type mappers ─────────────────────────────────────────────

function wsToProject(ws: WsWorkspace): WsProject {
  const bMin = ws.budget_min;
  const bMax = ws.budget_max;
  let budget = "—";
  if (bMin && bMax) budget = `$${(bMin / 1e6).toFixed(1)}M – $${(bMax / 1e6).toFixed(1)}M`;
  else if (bMax) budget = `Up to $${(bMax / 1e6).toFixed(1)}M`;
  else if (bMin) budget = `From $${(bMin / 1e6).toFixed(1)}M`;
  return { name: ws.name, intent: ws.intent, budget, budget_min: bMin, budget_max: bMax, areas: ws.areas || "—", timeline: ws.timeline || "—", priorities: ws.priorities ?? [] };
}

function mapProperty(p: WsProperty): Property {
  return {
    id: p.id, name: p.project_name, project: p.project_name,
    district: p.district, tenure: p.tenure, top: p.top_year ?? 0,
    beds: p.beds, mrt: p.mrt_desc,
    nearbyMRT: p.nearby_mrt,
    status: p.status,
    fitScore: p.fit_score ?? 5, aiSummary: p.ai_summary,
    pros: p.pros ?? [], risks: p.risks ?? [],
    questions: (p.questions ?? []).map(q => {
      const raw = q as { q: string; status: string; answer?: string; unit?: string; units?: string[] };
      const units = raw.units ?? (raw.unit ? [raw.unit] : undefined);
      return { q: raw.q, status: raw.status, answer: raw.answer, units } as Question;
    }),
    units: p.units?.map(mapUnit),
    livability: p.livability ?? undefined,
    summaryStale: p.summary_stale,
    summaryGeneratedAt: p.summary_generated_at ?? null,
  };
}

function mapUnit(u: WsUnit): Unit {
  return {
    id: u.id, label: u.label, floor: u.floor ?? 0,
    stack: u.stack, facing: u.facing,
    size: u.size_sqft ?? 0, price: u.price ?? 0, psf: u.psf ?? 0,
    maintenance: u.maintenance, tenancy: u.tenancy,
    tenancyEnd: u.tenancy_end || undefined,
    status: u.status || "New",
    negotiation: u.negotiation ?? undefined,
  };
}

function mapViewing(v: WsViewing): Viewing {
  return {
    id: v.id, propId: v.property_id ?? "",
    unitLabel: v.unit_label || undefined,
    status: v.status === "completed" ? "completed" : "scheduled",
    date: v.viewing_date, time: v.viewing_time, agent: v.agent_name,
    checklist: v.checklist?.length ? v.checklist : undefined,
    ratings: v.ratings ?? undefined,
    tags: v.tags?.length ? v.tags : undefined,
    notes: v.notes || undefined,
    aiSummary: v.ai_summary || undefined,
    decision: v.decision ?? null,
    media: v.media?.length ? (v.media as MediaItem[]) : undefined,
    summaryStale: v.summary_stale,
    summaryGeneratedAt: v.summary_generated_at ?? null,
  };
}

// ─── API-backed state hook ────────────────────────────────────────────────────
function useWsDataState(targetId?: string) {
  const navigate = useNavigate();
  const wsIdRef = useRef<string | null>(null);
  const [project, setProject] = useState<WsProject>({ name: "", intent: "", budget: "—", budget_min: null, budget_max: null, areas: "—", timeline: "—", priorities: [] });
  const [properties, setProperties] = useState<Property[]>([]);
  const [viewings, setViewings] = useState<Viewing[]>([]);
  const [compareSummary, setCompareSummary] = useState<CompareSummary | null>(null);
  const [compareUnitIds, setCompareUnitIdsState] = useState<string[]>([]);
  // wsMissing: listWorkspaces succeeded but returned no row we can render
  // (empty list, or targetId that doesn't resolve in a non-empty list).
  // Distinguish from "still loading" so the UI doesn't briefly flash the
  // empty-state card on a normal bootstrap. Network errors are swallowed
  // upstream and leave wsMissing untouched (matches the prior behaviour
  // of degrading silently rather than mis-surfacing as "no workspace").
  const [wsMissing, setWsMissing] = useState(false);

  useEffect(() => { void bootstrap(); }, [targetId]);

  // External refresh trigger: PrefsEditModal dispatches this after a
  // successful PATCH to pick up backend-seeded properties (which arrive
  // ~1-2s after the response, since seeding runs in a goroutine). Window-
  // level event keeps WorkspacePrefsBanner / PrefsEditModal decoupled from
  // this hook — neither needs a context handle to ours.
  useEffect(() => {
    const handler = () => { void bootstrap(); };
    window.addEventListener(STORAGE_EVENTS.workspaceRefresh, handler);
    return () => window.removeEventListener(STORAGE_EVENTS.workspaceRefresh, handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetId]);

  async function bootstrap() {
    try {
      const list = await listWorkspaces();
      let ws: WsWorkspace | undefined;
      if (targetId) {
        ws = list.find(w => w.id === targetId) ?? list[0];
      } else if (list.length > 0) {
        ws = list[0];
        // Keep URL in sync so bookmarks/sharing works
        navigate(`/workspace/${ws.id}`, { replace: true });
        return; // navigate will re-trigger with targetId set
      }
      if (!ws) {
        // Empty list (or targetId in an empty list) — surface as
        // "no workspace" so the page renders the empty-state CTA.
        // Creation is intent-driven (see EmptyWorkspaceCard) and never
        // a side effect of a read path.
        wsIdRef.current = null;
        setWsMissing(true);
        return;
      }
      wsIdRef.current = ws.id;
      setWsMissing(false);
      setProject(wsToProject(ws));
      const full = await getWorkspaceFull(ws.id);
      setProperties((full.properties ?? []).map(mapProperty));
      setViewings((full.viewings ?? []).map(mapViewing));
      setCompareSummary(full.compare_summary ?? null);
      setCompareUnitIdsState(full.compare_unit_ids ?? []);
    } catch (e) {
      console.error("workspace bootstrap failed", e);
    }
  }

  // createDefaultWorkspace: explicit, intent-driven creation triggered
  // by the empty-state CTA. Mirrors the legacy default name/intent so
  // first-time users land on a workspace identical to what the old
  // create-on-read path produced. Navigates to the new id, which
  // re-triggers bootstrap via the targetId effect.
  async function createDefaultWorkspace() {
    try {
      const ws = await createWorkspace("My Property Search", "Buy");
      navigate(`/workspace/${ws.id}`, { replace: true });
    } catch (e) {
      console.error("workspace create failed", e);
    }
  }

  // setCompareUnitIds: optimistic local update + PATCH to the
  // workspace. The server's summary worker reads compare_unit_ids and
  // regenerates compare_summary asynchronously; the UI picks up the new
  // body via the periodic stale-poll below (or the next manual refresh).
  //
  // The stale flag MUST be set on the client side here even when there's
  // no prior compareSummary (first-ever compare). Otherwise:
  //   - cmpStale stays false on the client
  //   - the stale-poll never fires
  //   - the worker writes a body server-side but the client never sees it
  //   - the UI gets stuck on the "no body yet" fallback hint
  // Stub shape mirrors WsCompareSummary; body is empty so the FE renders
  // the generating hint + pill, not the previous selection's text.
  const setCompareUnitIds = (ids: string[]) => {
    const next = [...ids].sort();
    const prev = [...compareUnitIds].sort();
    if (next.length === prev.length && next.every((v, i) => v === prev[i])) return;
    setCompareUnitIdsState(ids);
    setCompareSummary(prev => prev
      ? { ...prev, stale: true }
      : { body: "", stale: true, unit_ids_hash: "" });
    const wsId = wsIdRef.current;
    if (!wsId) return;
    void updateWorkspaceApi(wsId, { compare_unit_ids: ids });
  };

  // Stale-poll: when any AI summary surface is dirty, refetch the
  // workspace every 8s until they all clear. Cheap (one GET) and
  // bounded — stops as soon as nothing is stale. Avoids a websocket
  // for an MVP feature.
  useEffect(() => {
    const anyStale = (compareSummary?.stale ?? false) ||
      properties.some(p => p.summaryStale) ||
      viewings.some(v => v.summaryStale);
    if (!anyStale) return;
    const id = setInterval(() => { void bootstrap(); }, 8000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compareSummary?.stale, properties, viewings]);

  const addProperty = (p: Omit<Property, "id">) => {
    const wsId = wsIdRef.current;
    if (!wsId) return;
    // district / tenure / top_year / mrt_desc are NOT sent — they're
    // canonical project facts read from the data engine at API time.
    // The server's PropertyInput rejects them (column dropped), so
    // even if they survived in the local Property they'd be no-ops.
    void addPropertyApi(wsId, {
      project_name: p.name, beds: p.beds,
      fit_score: p.fitScore, status: p.status, ai_summary: p.aiSummary,
      pros: p.pros, risks: p.risks, questions: p.questions, livability: p.livability ?? {},
    }).then(r => setProperties(prev => [...prev, mapProperty(r)]));
  };

  const updateProperty = (id: string, patch: Partial<Property>) => {
    // Mirror the server's "summaryAffected" rule in store.go: a patch
    // that touches the buyer's stated content (pros / risks / questions
    // / livability) will mark the row dirty server-side, so reflect
    // that locally — drives the GeneratingPill and the stale-poll. The
    // user explicitly editing aiSummary text is excluded (that's the
    // human override path; we mustn't loop with the worker).
    const summaryAffected = patch.pros !== undefined || patch.risks !== undefined ||
      patch.questions !== undefined || patch.livability !== undefined;
    setProperties(prev => prev.map(p => p.id === id
      ? { ...p, ...patch, ...(summaryAffected ? { summaryStale: true } : {}) }
      : p));
    const wsId = wsIdRef.current;
    if (!wsId) return;
    const api: Record<string, unknown> = {};
    if (patch.name !== undefined) api.project_name = patch.name;
    if (patch.project !== undefined) api.project_name = patch.project;
    // patch.district / .tenure / .top / .mrt intentionally NOT forwarded —
    // the server PropertyUpdate dropped those fields when the columns
    // moved to the canonical data engine. Any caller still passing them
    // (legacy code paths) is silently a no-op on those keys.
    if (patch.beds !== undefined) api.beds = patch.beds;
    if (patch.fitScore !== undefined) api.fit_score = patch.fitScore;
    if (patch.status !== undefined) api.status = patch.status;
    if (patch.aiSummary !== undefined) api.ai_summary = patch.aiSummary;
    if (patch.pros !== undefined) api.pros = patch.pros;
    if (patch.risks !== undefined) api.risks = patch.risks;
    if (patch.questions !== undefined) api.questions = patch.questions;
    if (patch.livability !== undefined) api.livability = patch.livability;
    void updatePropertyApi(wsId, id, api);
  };

  const addViewing = (v: Omit<Viewing, "id">) => {
    const wsId = wsIdRef.current;
    if (!wsId) return;
    void addViewingApi(wsId, {
      property_id: v.propId || undefined, unit_label: v.unitLabel ?? "",
      viewing_date: v.date, viewing_time: v.time, agent_name: v.agent,
      checklist: v.checklist,
    })
      .then(r => setViewings(prev => [...prev, mapViewing(r)]))
      // Don't swallow a rejected write (e.g. a server 400): the modal has
      // already closed, so at least surface it instead of failing silently
      // and leaving the user thinking the viewing was saved. PROP-167.
      .catch(err => console.error("addViewing failed", err));
  };

  const updateViewing = (id: string, patch: Partial<Viewing>) => {
    // Mirror store.go's viewing summaryAffected rule: notes / ratings /
    // tags / checklist / decision OR a status flip TO completed. Editing
    // aiSummary itself is excluded (human override path).
    const summaryAffected = patch.notes !== undefined || patch.ratings !== undefined ||
      patch.tags !== undefined || patch.checklist !== undefined ||
      patch.decision !== undefined || patch.status === "completed";
    setViewings(prev => prev.map(v => v.id === id
      ? { ...v, ...patch, ...(summaryAffected ? { summaryStale: true } : {}) }
      : v));
    const wsId = wsIdRef.current;
    if (!wsId) return;
    const api: Record<string, unknown> = {};
    if (patch.propId !== undefined) api.property_id = patch.propId;
    if (patch.unitLabel !== undefined) api.unit_label = patch.unitLabel;
    if (patch.status !== undefined) api.status = patch.status;
    if (patch.date !== undefined) api.viewing_date = patch.date;
    if (patch.time !== undefined) api.viewing_time = patch.time;
    if (patch.agent !== undefined) api.agent_name = patch.agent;
    if (patch.checklist !== undefined) api.checklist = patch.checklist;
    if (patch.ratings !== undefined) api.ratings = patch.ratings;
    if (patch.tags !== undefined) api.tags = patch.tags;
    if (patch.notes !== undefined) api.notes = patch.notes;
    if (patch.aiSummary !== undefined) api.ai_summary = patch.aiSummary;
    if (patch.decision !== undefined) api.decision = patch.decision;
    void updateViewingApi(wsId, id, api);
  };

  const addUnit = (propId: string, u: Unit) => {
    const wsId = wsIdRef.current;
    if (!wsId) return;
    void addUnitApi(wsId, propId, {
      label: u.label, floor: u.floor || undefined, stack: u.stack, facing: u.facing,
      size_sqft: u.size || undefined, price: u.price || undefined, psf: u.psf || undefined,
      maintenance: u.maintenance, tenancy: u.tenancy, tenancy_end: u.tenancyEnd ?? "",
      status: u.status || "New",
    }).then(r => setProperties(prev => prev.map(p =>
      // Adding a unit changes the parent property's UNITS TRACKED list,
      // which the server marks dirty (store.go AddUnit). Reflect locally
      // so the GeneratingPill shows up and the stale-poll kicks in.
      p.id === propId
        ? { ...p, units: [...(p.units ?? []), mapUnit(r)], summaryStale: true }
        : p)));
  };

  const updateUnit = (propId: string, unitId: string, patch: Partial<Unit>) => {
    const wsId = wsIdRef.current;
    if (!wsId) return;
    // Mirror store.go's unit summaryAffected rule: label / floor / facing
    // / size / price / psf / status. Cosmetic fields (maintenance,
    // tenancy, notes, negotiation history) leave the snapshot alone.
    const summaryAffected = patch.label !== undefined || patch.floor !== undefined ||
      patch.facing !== undefined || patch.size !== undefined ||
      patch.price !== undefined || patch.psf !== undefined ||
      patch.status !== undefined;
    let oldLabel: string | undefined;
    let newLabel: string | undefined;
    setProperties(prev => prev.map(p => {
      if (p.id !== propId) return p;
      const units = (p.units ?? []).map(u => {
        if (u.id !== unitId) return u;
        oldLabel = u.label;
        const merged: Unit = { ...u, ...patch };
        if (merged.size > 0 && merged.price > 0) merged.psf = Math.round(merged.price / merged.size);
        if (patch.label !== undefined) newLabel = merged.label;
        return merged;
      });
      let questions = p.questions;
      if (newLabel !== undefined && oldLabel !== undefined && oldLabel !== newLabel) {
        const o = oldLabel, n = newLabel;
        let changed = false;
        questions = p.questions.map(q => {
          if (!q.units || !q.units.includes(o)) return q;
          changed = true;
          return { ...q, units: q.units.map(l => l === o ? n : l) };
        });
        if (changed) void updatePropertyApi(wsId, propId, { questions });
      }
      return { ...p, units, questions, ...(summaryAffected ? { summaryStale: true } : {}) };
    }));
    const apiPatch: Record<string, unknown> = {};
    if (patch.label !== undefined) apiPatch.label = patch.label;
    if (patch.floor !== undefined) apiPatch.floor = patch.floor;
    if (patch.stack !== undefined) apiPatch.stack = patch.stack;
    if (patch.facing !== undefined) apiPatch.facing = patch.facing;
    if (patch.size !== undefined) apiPatch.size_sqft = patch.size;
    if (patch.price !== undefined) apiPatch.price = patch.price;
    if (patch.psf !== undefined) apiPatch.psf = patch.psf;
    if (patch.maintenance !== undefined) apiPatch.maintenance = patch.maintenance;
    if (patch.tenancy !== undefined) apiPatch.tenancy = patch.tenancy;
    if (patch.tenancyEnd !== undefined) apiPatch.tenancy_end = patch.tenancyEnd ?? "";
    if (patch.status !== undefined) apiPatch.status = patch.status;
    // negotiation: forward as-is. `null` clears the column server-side
    // (handled by UnitUpdate.Negotiation `*json.RawMessage` decoding);
    // a NegotiationOffer object replaces the blob; undefined leaves the
    // column unchanged (key omitted from apiPatch).
    if (patch.negotiation !== undefined) apiPatch.negotiation = patch.negotiation;
    if (Object.keys(apiPatch).length > 0) void updateUnitApi(wsId, propId, unitId, apiPatch);
  };

  const deleteUnit = (propId: string, unitId: string) => {
    const wsId = wsIdRef.current;
    if (!wsId) return;
    setProperties(prev => prev.map(p => {
      if (p.id !== propId) return p;
      const removed = (p.units ?? []).find(u => u.id === unitId);
      const units = (p.units ?? []).filter(u => u.id !== unitId);
      let questions = p.questions;
      if (removed) {
        let questionsChanged = false;
        questions = p.questions.map(q => {
          if (!q.units || !q.units.includes(removed.label)) return q;
          questionsChanged = true;
          const next = q.units.filter(l => l !== removed.label);
          return { ...q, units: next.length ? next : undefined };
        });
        if (questionsChanged) void updatePropertyApi(wsId, propId, { questions });
      }
      // Deleting a unit changes the parent property's UNITS TRACKED
      // list, so the server marks it dirty (store.go DeleteUnit).
      return { ...p, units, questions, summaryStale: true };
    }));
    void deleteUnitApi(wsId, propId, unitId);
  };

  // addQuestion / updateQuestion bypass the updateProperty wrapper and go
  // straight to updatePropertyApi, but on the server side both go through
  // UpdateProperty's "questions" field which is in the summaryAffected
  // set. Set summaryStale locally to keep the UI honest.
  const addQuestion = (propId: string, q: Question) => {
    setProperties(prev => {
      const newProps = prev.map(p => p.id === propId
        ? { ...p, questions: [...p.questions, q], summaryStale: true }
        : p);
      const updated = newProps.find(p => p.id === propId);
      if (updated && wsIdRef.current) void updatePropertyApi(wsIdRef.current, propId, { questions: updated.questions });
      return newProps;
    });
  };

  const updateQuestion = (propId: string, qIdx: number, patch: Partial<Question>) => {
    setProperties(prev => {
      const newProps = prev.map(p => p.id === propId
        ? { ...p, questions: p.questions.map((q, i) => i === qIdx ? { ...q, ...patch } : q), summaryStale: true }
        : p);
      const updated = newProps.find(p => p.id === propId);
      if (updated && wsIdRef.current) void updatePropertyApi(wsIdRef.current, propId, { questions: updated.questions });
      return newProps;
    });
  };

  const updateProject = (patch: Partial<WsProject>) => {
    setProject(prev => {
      const next = { ...prev, ...patch };
      // recompute formatted budget string if raw values changed
      if (patch.budget_min !== undefined || patch.budget_max !== undefined) {
        const bMin = next.budget_min;
        const bMax = next.budget_max;
        if (bMin && bMax) next.budget = `$${(bMin / 1e6).toFixed(1)}M – $${(bMax / 1e6).toFixed(1)}M`;
        else if (bMax) next.budget = `Up to $${(bMax / 1e6).toFixed(1)}M`;
        else if (bMin) next.budget = `From $${(bMin / 1e6).toFixed(1)}M`;
        else next.budget = "—";
      }
      return next;
    });
    const wsId = wsIdRef.current;
    if (!wsId) return;
    void updateWorkspaceApi(wsId, {
      name: patch.name, intent: patch.intent,
      budget_min: patch.budget_min ?? undefined,
      budget_max: patch.budget_max ?? undefined,
      areas: patch.areas, priorities: patch.priorities, timeline: patch.timeline,
    });
  };

  return {
    project, properties, viewings,
    updateProject, addProperty, updateProperty,
    addViewing, updateViewing,
    addUnit, updateUnit, deleteUnit,
    addQuestion, updateQuestion,
    compareSummary, compareUnitIds, setCompareUnitIds,
    refreshWorkspace: () => { void bootstrap(); },
    wsMissing, createDefaultWorkspace,
  };
}


const STATUS_COLORS: Record<string, { bg: string; ink: string; dot: string }> = {
  "Worth Checking":    { bg: "#e0ebe2", ink: "#1d4a3a", dot: "#1d4a3a" },
  "Viewing Scheduled": { bg: "#ddeaf5", ink: "#1a3e6a", dot: "#1a3e6a" },
  "Viewed":            { bg: "#e8e4f8", ink: "#3a2e7a", dot: "#3a2e7a" },
  "New":               { bg: "#efebe1", ink: "#5a4a20", dot: "#8a7440" },
  "Watchlist":         { bg: "#f5ead1", ink: "#8a5a00", dot: "#8a5a00" },
  "Negotiating":       { bg: "#f1e0f1", ink: "#6a1a6a", dot: "#6a1a6a" },
  "Rejected":          { bg: "#f1dcd4", ink: "#7a2d1f", dot: "#7a2d1f" },
};


const MODULES = [
  { id: "overview",  label: "Overview",       badge: "" },
  { id: "shortlist", label: "Shortlist",      badge: "" },
  { id: "compare",   label: "Compare",        badge: "" },
  { id: "viewings",  label: "Viewings",       badge: "" },
  { id: "decision",  label: "Decision Board", badge: "" },
];
const VALID_MODULES = MODULES.map(m => m.id);

// ─── Micro-components ─────────────────────────────────────────────────────────

const STATUS_KEYS: Record<string, string> = {
  "Worth Checking": "worthChecking",
  "Viewing Scheduled": "viewingScheduled",
  "Viewed": "viewed",
  "New": "new",
  "Watchlist": "watchlist",
  "Negotiating": "negotiating",
  "Rejected": "rejected",
};

// Enum value → i18n key maps (PROP-216). Same value/label split as
// STATUS_KEYS: the English value stays canonical (stored in DB, used by
// cycle/sort logic and as the <option value>); only the *display* is
// localised. Unknown / legacy values and the "—" unknown-marker pass
// through untranslated so a stray value never renders blank.
const FACING_KEYS: Record<string, string> = {
  North: "north", South: "south", East: "east", West: "west",
  NE: "ne", NW: "nw", SE: "se", SW: "sw",
};
function facingLabel(t: TFn, v: string): string {
  const k = v ? FACING_KEYS[v] : undefined;
  return k ? t(`workspace.enum.facing.${k}`) : v;
}
const TENANCY_KEYS: Record<string, string> = {
  "Vacant": "vacant",
  "Tenanted": "tenanted",
  "Tenanted (ending soon)": "tenantedEnding",
  "Owner-occupied": "ownerOccupied",
};
function tenancyLabel(t: TFn, v: string): string {
  const k = v ? TENANCY_KEYS[v] : undefined;
  return k ? t(`workspace.enum.tenancy.${k}`) : v;
}
const DECISION_KEYS: Record<string, string> = {
  Proceed: "proceed", Deprioritise: "deprioritise", Rejected: "rejected",
};
function decisionLabel(t: TFn, v: string): string {
  const k = v ? DECISION_KEYS[v] : undefined;
  return k ? t(`workspace.enum.decision.${k}`) : v;
}
const QSTATUS_KEYS: Record<string, string> = {
  Open: "open", Asked: "asked", Answered: "answered",
};
function qStatusLabel(t: TFn, v: string): string {
  const k = v ? QSTATUS_KEYS[v] : undefined;
  return k ? t(`workspace.enum.qstatus.${k}`) : v;
}

// Precedence order — most engaged first. Drives both the rolled-up
// project status (highest-ranked unit wins; doc/WORKSPACE.md §8) AND
// the StatusPicker menu order. Matches the Claude Design canvas.
const STATUS_ORDER = [
  "Negotiating", "Viewed", "Viewing Scheduled", "Worth Checking",
  "New", "Watchlist", "Rejected",
];

function StatusBadge({ status, small }: { status: string; small?: boolean }) {
  const t = useWsT();
  const c = STATUS_COLORS[status] ?? { bg: "#efefef", ink: "#555", dot: "#999" };
  const key = STATUS_KEYS[status];
  const label = key ? t(`workspace.status.${key}`) : status;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      background: c.bg, color: c.ink,
      padding: small ? "2px 7px" : "3px 9px", borderRadius: 20,
      fontFamily: "var(--font-ui)", fontSize: small ? 10 : 11, fontWeight: 500,
      letterSpacing: "-0.005em", whiteSpace: "nowrap",
    }}>
      <span style={{ width: 5, height: 5, borderRadius: 5, background: c.dot, flexShrink: 0 }} />
      {label}
    </span>
  );
}

function FitScore({ score }: { score: number }) {
  const color = score >= 7.5 ? "var(--pos)" : score >= 6 ? "var(--warn)" : "var(--neg)";
  return <span style={{ fontFamily: "var(--font-mono)", fontSize: 11.5, fontWeight: 500, color, letterSpacing: "-0.01em" }}>{score.toFixed(1)}</span>;
}

// StatusPicker — inline 7-status picker. Shows the current status as a
// chip; reveals a chevron on hover/focus; opens a 7-row menu with
// label + color dot. Closes on outside-click and Esc; ↑/↓ to highlight,
// Enter to commit. Chip width does not change when the menu is open
// (so column layouts don't reflow). Ported from the Claude Design
// canvas (workspace.jsx :: StatusPicker).
function StatusPicker({
  value, onChange,
  size = "sm", align = "left", hideAffordanceWhenIdle = false,
}: {
  value: string;
  onChange: (next: string) => void;
  size?: "xs" | "sm" | "md";
  align?: "left" | "right";
  hideAffordanceWhenIdle?: boolean;
}) {
  const t = useWsT();
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [highlight, setHighlight] = useState(STATUS_ORDER.indexOf(value));
  const rootRef = useRef<HTMLSpanElement | null>(null);
  const c = STATUS_COLORS[value] ?? { bg: "#efefef", ink: "#555", dot: "#999" };
  const labelKey = STATUS_KEYS[value];
  const label = labelKey ? t(`workspace.status.${labelKey}`) : value;

  useEffect(() => {
    if (!open) return;
    setHighlight(Math.max(0, STATUS_ORDER.indexOf(value)));
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") { setOpen(false); e.stopPropagation(); }
      if (e.key === "ArrowDown") { setHighlight(h => (h + 1) % STATUS_ORDER.length); e.preventDefault(); }
      if (e.key === "ArrowUp")   { setHighlight(h => (h - 1 + STATUS_ORDER.length) % STATUS_ORDER.length); e.preventDefault(); }
      if (e.key === "Enter") { onChange(STATUS_ORDER[highlight]); setOpen(false); e.preventDefault(); }
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("keydown", onKey); };
  }, [open, highlight, value, onChange]);

  const padY = size === "xs" ? 1 : size === "sm" ? 2 : 3;
  const padX = size === "xs" ? 6 : size === "sm" ? 8 : 10;
  const fontSize = size === "xs" ? 10 : size === "sm" ? 10.5 : 11.5;
  const showChevron = open || hovered || !hideAffordanceWhenIdle;

  return (
    <span ref={rootRef} style={{ position: "relative", display: "inline-block" }}
      onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpen(true); } }}
        title={t("workspace.statusPicker.title", { status: label })}
        style={{
          display: "inline-flex", alignItems: "center", gap: 5,
          background: c.bg, color: c.ink,
          padding: `${padY}px ${padX}px`,
          paddingRight: showChevron ? padX - 2 : padX,
          borderRadius: 20,
          fontFamily: "var(--font-ui)", fontSize, fontWeight: 500,
          letterSpacing: "-0.005em", whiteSpace: "nowrap",
          border: "1px solid transparent",
          borderColor: open ? c.dot : "transparent",
          cursor: "pointer",
          transition: "border-color 120ms, background 120ms",
        }}>
        <span style={{ width: 5, height: 5, borderRadius: 5, background: c.dot, flexShrink: 0 }} />
        {label}
        <svg width="9" height="9" viewBox="0 0 12 12" fill="none"
          style={{
            marginLeft: 1, opacity: showChevron ? 0.7 : 0,
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
            transition: "opacity 120ms, transform 150ms",
            flexShrink: 0,
          }}>
          <path d="M3 4.5l3 3 3-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && (
        <div role="listbox"
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
          style={{
            position: "absolute", top: "calc(100% + 4px)",
            ...(align === "right" ? { right: 0 } : { left: 0 }),
            zIndex: 50, minWidth: 168,
            background: "var(--surface)",
            border: "1px solid var(--rule-strong)",
            borderRadius: 9,
            boxShadow: "0 10px 28px rgba(23,32,29,0.16)",
            padding: 4, fontFamily: "var(--font-ui)",
          }}>
          <div style={{
            fontSize: 9, fontWeight: 600, letterSpacing: "0.1em",
            textTransform: "uppercase", color: "var(--ink-4)",
            padding: "5px 8px 4px",
          }}>{t("workspace.statusPicker.heading")}</div>
          {STATUS_ORDER.map((s, i) => {
            const sc = STATUS_COLORS[s] ?? { bg: "#efefef", ink: "#555", dot: "#999" };
            const active = s === value;
            const hi = i === highlight;
            const sKey = STATUS_KEYS[s];
            const sLabel = sKey ? t(`workspace.status.${sKey}`) : s;
            return (
              <button key={s}
                onMouseEnter={() => setHighlight(i)}
                onClick={(e) => { e.stopPropagation(); onChange(s); setOpen(false); }}
                style={{
                  display: "flex", alignItems: "center", gap: 8,
                  width: "100%", textAlign: "left",
                  border: "none", background: hi ? "var(--accent-soft)" : "transparent",
                  padding: "6px 8px", borderRadius: 6, cursor: "pointer",
                  fontFamily: "var(--font-ui)", fontSize: 12,
                  color: "var(--ink)", fontWeight: active ? 600 : 400,
                }}>
                <span style={{ width: 7, height: 7, borderRadius: 7, background: sc.dot, flexShrink: 0 }} />
                <span style={{ flex: 1 }}>{sLabel}</span>
                {active && (
                  <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
                    <path d="M2.5 6.5l2.2 2.2L9.5 3.5" stroke="var(--accent)" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </button>
            );
          })}
        </div>
      )}
    </span>
  );
}

// RolledUpExplainer — small chip + tooltip that explains the
// project-level status precedence rule. Designed to be the only
// surface that teaches users *why* the project shows a particular
// status; pairs with the DecisionStatusSection on the dossier.
function RolledUpExplainer({ derivedStatus, sourceUnit }: { derivedStatus: string; sourceUnit?: Unit | null }) {
  const t = useWsT();
  const [hover, setHover] = useState(false);
  const c = STATUS_COLORS[derivedStatus] ?? { bg: "#efefef", ink: "#555", dot: "#999" };
  const labelKey = STATUS_KEYS[derivedStatus];
  const label = labelKey ? t(`workspace.status.${labelKey}`) : derivedStatus;
  return (
    <div
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      onFocus={() => setHover(true)} onBlur={() => setHover(false)}
      tabIndex={0}
      style={{
        position: "relative", display: "inline-flex", alignItems: "center", gap: 8,
        background: "var(--surface)", border: "1px solid var(--rule)",
        borderRadius: 8, padding: "6px 11px",
        cursor: "help", outline: "none",
      }}>
      <span style={{ fontFamily: "var(--font-ui)", fontSize: 10.5, color: "var(--ink-3)", letterSpacing: "-0.005em" }}>
        {t("workspace.decisionStatus.projectShowsAs")}
      </span>
      <span style={{
        display: "inline-flex", alignItems: "center", gap: 5,
        background: c.bg, color: c.ink,
        padding: "2px 8px", borderRadius: 20,
        fontFamily: "var(--font-ui)", fontSize: 11, fontWeight: 500,
      }}>
        <span style={{ width: 5, height: 5, borderRadius: 5, background: c.dot }} />
        {label}
      </span>
      <svg width="11" height="11" viewBox="0 0 14 14" fill="none">
        <circle cx="7" cy="7" r="5.4" stroke="var(--ink-4)" strokeWidth="1.2" />
        <path d="M7 6.5v3.5M7 4.2v0.6" stroke="var(--ink-3)" strokeWidth="1.4" strokeLinecap="round" />
      </svg>
      {hover && (
        <div role="tooltip" style={{
          position: "absolute", top: "calc(100% + 6px)", left: 0, zIndex: 30,
          width: 320, padding: "10px 12px",
          background: "var(--ink)", color: "#f4f1ea",
          borderRadius: 8, boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
          fontFamily: "var(--font-ui)", fontSize: 11.5, lineHeight: 1.5,
          letterSpacing: "-0.005em",
        }}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>
            {t("workspace.decisionStatus.tooltipTitle")}
          </div>
          <div style={{ opacity: 0.85 }}>
            {t("workspace.decisionStatus.tooltipBody")}
            {sourceUnit && (
              <> {t("workspace.decisionStatus.tooltipSource", { label: sourceUnit.label, status: label })}</>
            )}
          </div>
          <div style={{
            marginTop: 8, padding: "7px 9px",
            background: "rgba(255,255,255,0.06)", borderRadius: 5,
            fontSize: 10.5, lineHeight: 1.5,
          }}>
            <div style={{ opacity: 0.7, fontSize: 9.5, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 3 }}>
              {t("workspace.decisionStatus.precedence")}
            </div>
            {STATUS_ORDER.map(s => {
              const k = STATUS_KEYS[s];
              return k ? t(`workspace.status.${k}`) : s;
            }).join(" › ")}
          </div>
        </div>
      )}
    </div>
  );
}

// NegotiationPanel — full offer-history timeline + Gap-from-Ask /
// Next Step / Log move. Renders only when the active unit's status
// is Negotiating and a negotiation blob exists. Ported from the
// Claude Design canvas (workspace.jsx :: NegotiationPanel).
type NegotiationEvent = {
  kind: "asking" | "buyer" | "seller" | "call";
  labelKey: string;
  whoKey: string;
  price: number;
  date: string;
};
// Format a stored move date for display. New entries land as ISO
// yyyy-mm-dd from the date picker; legacy / hand-entered values may
// already be free-form ("6 May", "tomorrow"). ISO strings get
// localised to a short "6 May" / "5月6日" via Intl; anything else
// passes through as-is.
function formatMoveDate(s: string | undefined, locale: string): string {
  if (!s) return "—";
  const trimmed = s.trim();
  if (!trimmed) return "—";
  if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) {
    const d = new Date(trimmed);
    if (!isNaN(d.getTime())) {
      try {
        return d.toLocaleDateString(locale.startsWith("zh") ? "zh-CN" : "en-GB", { day: "numeric", month: "short" });
      } catch {
        // Fall through to raw on locale-data errors.
      }
    }
  }
  return trimmed;
}

function NegotiationPanel({ offer, onLogMove }: { offer: NegotiationOffer; onLogMove: () => void }) {
  const t = useWsT();
  const { i18n } = useTranslation();
  const fmt = (n: number) => "$" + (n / 1e6).toFixed(3) + "M";
  const fdate = (s: string | undefined) => formatMoveDate(s, i18n.language);
  const events: NegotiationEvent[] = [
    { kind: "asking", labelKey: "listedAsking", whoKey: "seller", price: offer.asking, date: fdate(offer.askingOn) },
  ];
  if (typeof offer.buyerOffer === "number") {
    events.push({ kind: "buyer", labelKey: "buyerOffer", whoKey: "you", price: offer.buyerOffer, date: fdate(offer.buyerOfferOn) });
  }
  if (typeof offer.sellerCounter === "number") {
    events.push({ kind: "seller", labelKey: "sellerCounter", whoKey: "seller", price: offer.sellerCounter, date: fdate(offer.sellerCounterOn) });
  }
  if (typeof offer.buyerCall === "number") {
    events.push({ kind: "call", labelKey: "yourCall", whoKey: "you", price: offer.buyerCall, date: fdate(offer.buyerCallOn) });
  }
  const latest = events[events.length - 1];
  const gapPct = ((offer.asking - latest.price) / offer.asking) * 100;
  const colorFor = (kind: NegotiationEvent["kind"]): string => ({
    asking: "var(--ink-3)",
    buyer:  "var(--accent)",
    seller: "var(--warn)",
    call:   "var(--pos)",
  })[kind];
  const negTone = STATUS_COLORS["Negotiating"] ?? { bg: "#f1e0f1", ink: "#6a1a6a", dot: "#6a1a6a" };
  const nextStepFallback =
    offer.buyerCall != null ? t("workspace.negotiation.nextAwaitingSeller")
    : offer.sellerCounter != null ? t("workspace.negotiation.nextYourMove")
    : t("workspace.negotiation.nextAwaitingCounter");
  return (
    <div style={{
      background: negTone.bg,
      border: `1px solid ${negTone.dot}33`,
      borderRadius: 12, padding: "16px 18px", marginBottom: 22,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
        <div style={{ width: 6, height: 6, borderRadius: 6, background: negTone.ink }} />
        <span style={{ fontFamily: "var(--font-ui)", fontSize: 9.5, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: negTone.ink }}>
          {t("workspace.negotiation.title")}
        </span>
        <span style={{ marginLeft: "auto", fontFamily: "var(--font-ui)", fontSize: 11, color: "var(--ink-3)" }}>
          {t("workspace.negotiation.movesLast", { count: events.length, date: latest.date })}
        </span>
      </div>

      <div style={{ position: "relative", background: "var(--surface)", border: "1px solid var(--rule)", borderRadius: 8, overflow: "hidden" }}>
        <div style={{ position: "absolute", left: 19, top: 14, bottom: 14, width: 1, background: "var(--rule)" }} />
        {events.map((e, i) => {
          const isLatest = i === events.length - 1;
          const delta = e.kind === "asking" ? null : ((e.price - offer.asking) / offer.asking * 100);
          return (
            <div key={i} style={{
              position: "relative",
              display: "flex", alignItems: "center", gap: 10,
              padding: "10px 12px 10px 14px",
              borderBottom: i < events.length - 1 ? "1px solid var(--rule)" : "none",
              background: isLatest ? "rgba(217,142,57,0.07)" : "transparent",
            }}>
              <div style={{
                width: 11, height: 11, borderRadius: 11, background: colorFor(e.kind),
                border: "2px solid var(--surface)", boxShadow: `0 0 0 1px ${colorFor(e.kind)}`,
                flexShrink: 0, zIndex: 1,
              }} />
              <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
                <div style={{ display: "flex", alignItems: "baseline", gap: 6, flexWrap: "wrap" }}>
                  <span style={{ fontFamily: "var(--font-ui)", fontSize: 12, fontWeight: 600, color: "var(--ink)" }}>
                    {t(`workspace.negotiation.event.${e.labelKey}`)}
                  </span>
                  {isLatest && (
                    <span style={{ fontFamily: "var(--font-ui)", fontSize: 8.5, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: negTone.ink }}>
                      {t("workspace.negotiation.current")}
                    </span>
                  )}
                </div>
                <div style={{ fontFamily: "var(--font-ui)", fontSize: 10.5, color: "var(--ink-3)" }}>
                  {t(`workspace.negotiation.who.${e.whoKey}`)} · {e.date}
                </div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 1, flexShrink: 0 }}>
                <span style={{ fontFamily: "var(--font-mono)", fontSize: 12.5, fontWeight: 600, color: "var(--ink)", whiteSpace: "nowrap" }}>{fmt(e.price)}</span>
                {delta !== null && (
                  <span style={{ fontFamily: "var(--font-mono)", fontSize: 10, color: delta < 0 ? "var(--neg)" : "var(--pos)", whiteSpace: "nowrap" }}>
                    {delta > 0 ? "+" : ""}{delta.toFixed(1)}% {t("workspace.negotiation.vsAsk")}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
        <div style={{ flex: "1 1 110px", minWidth: 100, background: "var(--surface)", border: "1px solid var(--rule)", borderRadius: 8, padding: "8px 10px" }}>
          <div style={{ fontFamily: "var(--font-ui)", fontSize: 9.5, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--ink-4)", marginBottom: 3 }}>
            {t("workspace.negotiation.gapFromAsk")}
          </div>
          <div style={{ fontFamily: "var(--font-mono)", fontSize: 13, fontWeight: 600, color: gapPct > 0 ? "var(--pos)" : "var(--neg)" }}>
            {gapPct > 0 ? "−" : "+"}{Math.abs(gapPct).toFixed(1)}%
          </div>
        </div>
        <div style={{ flex: "2 1 180px", minWidth: 160, background: "var(--surface)", border: "1px solid var(--rule)", borderRadius: 8, padding: "8px 10px" }}>
          <div style={{ fontFamily: "var(--font-ui)", fontSize: 9.5, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--ink-4)", marginBottom: 3 }}>
            {t("workspace.negotiation.nextStep")}
          </div>
          <div style={{ fontFamily: "var(--font-serif)", fontSize: 12, fontStyle: "italic", color: "var(--ink-2)", lineHeight: 1.4 }}>
            {offer.nextStep || nextStepFallback}
          </div>
        </div>
        <button onClick={onLogMove} style={{
          background: negTone.ink, color: "white", border: "none", borderRadius: 8,
          padding: "8px 14px", fontFamily: "var(--font-ui)", fontSize: 12, fontWeight: 600, cursor: "pointer",
          display: "flex", alignItems: "center", gap: 5, flexShrink: 0,
        }}>
          <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
            <path d="M6 1v10M1 6h10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
          </svg>
          {t("workspace.negotiation.logMove")}
        </button>
      </div>
    </div>
  );
}

// DecisionStatusSection — per-unit status rows + bulk-edit bar.
// Lives between AI Snapshot and Key Facts on the Dossier. Source of
// truth for unit status changes; updates flow through the existing
// updateUnit data path so the rollup runs server-side. Ported from
// the Claude Design canvas (workspace.jsx :: DecisionStatusSection).
function DecisionStatusSection({ prop }: { prop: Property }) {
  const t = useWsT();
  const { updateUnit, updateProperty } = useWsData();
  const units = prop.units ?? [];
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const toggle = (id: string) => setSelected(s => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const allChecked = units.length > 0 && selected.size === units.length;
  const toggleAll = () => setSelected(allChecked ? new Set() : new Set(units.map(u => u.id)));

  const rollupRank = (s: string) => STATUS_ORDER.indexOf(s);
  // Derived status mirrors the server's rollup: highest-ranked unit
  // wins. We compute it client-side too so the UI stays consistent
  // before the server response lands.
  const sourceUnit = units.length > 0
    ? [...units].sort((a, b) => rollupRank(a.status) - rollupRank(b.status))[0]
    : null;
  const derivedStatus = sourceUnit ? sourceUnit.status : prop.status;

  const setUnitStatus = (unitId: string, next: string) => {
    updateUnit(prop.id, unitId, { status: next });
  };
  const applyBulk = (next: string) => {
    selected.forEach(id => updateUnit(prop.id, id, { status: next }));
    setSelected(new Set());
  };

  return (
    <div style={{ marginBottom: 22 }}>
      <div style={{
        display: "flex", alignItems: "baseline", justifyContent: "space-between",
        gap: 12, paddingBottom: 8, borderBottom: "1px solid var(--rule)", marginBottom: 12,
      }}>
        <div style={{
          fontFamily: "var(--font-ui)", fontSize: 9.5, fontWeight: 600,
          letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--ink-4)",
        }}>{t("workspace.decisionStatus.title")}</div>
        <RolledUpExplainer derivedStatus={derivedStatus} sourceUnit={sourceUnit} />
      </div>

      {units.length === 0 ? (
        <div style={{
          background: "var(--surface)", border: "1px solid var(--rule)",
          borderRadius: 10, padding: "11px 14px",
          display: "flex", alignItems: "center", gap: 12,
        }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: "var(--font-ui)", fontSize: 12.5, fontWeight: 600, color: "var(--ink)" }}>{titleCaseSG(prop.name)}</div>
            <div style={{ fontFamily: "var(--font-ui)", fontSize: 11, color: "var(--ink-3)", marginTop: 1 }}>
              {t("workspace.decisionStatus.noUnitsHint")}
            </div>
          </div>
          <StatusPicker value={prop.status} onChange={(next) => updateProperty(prop.id, { status: next })} size="sm" align="right" />
        </div>
      ) : (
        <>
          <div style={{
            display: "flex", alignItems: "center", gap: 10,
            background: selected.size > 0 ? "var(--accent-soft)" : "var(--surface-2)",
            borderRadius: 8, padding: "6px 10px 6px 8px", marginBottom: 8,
            transition: "background 150ms",
          }}>
            <label style={{
              display: "inline-flex", alignItems: "center", gap: 7,
              cursor: "pointer", userSelect: "none",
              fontFamily: "var(--font-ui)", fontSize: 11.5,
              color: selected.size > 0 ? "var(--accent-ink)" : "var(--ink-3)",
              fontWeight: 500,
            }}>
              <span onClick={toggleAll} style={{
                width: 14, height: 14, borderRadius: 3,
                border: `1.5px solid ${selected.size > 0 ? "var(--accent)" : "var(--rule-strong)"}`,
                background: allChecked ? "var(--accent)" : selected.size > 0 ? "var(--surface)" : "transparent",
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                cursor: "pointer",
              }}>
                {allChecked && <svg width="9" height="9" viewBox="0 0 12 12" fill="none"><path d="M2.5 6.5l2.2 2.2L9.5 3.5" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>}
                {!allChecked && selected.size > 0 && <span style={{ width: 7, height: 1.6, background: "var(--accent)", borderRadius: 1 }} />}
              </span>
              <span onClick={toggleAll}>
                {selected.size === 0
                  ? t("workspace.decisionStatus.bulkPrompt", { count: units.length })
                  : t("workspace.decisionStatus.bulkSelected", { selected: selected.size, total: units.length })}
              </span>
            </label>
            <div style={{ flex: 1 }} />
            {selected.size > 0 && (
              <>
                <StatusPicker
                  value={(units.find(u => selected.has(u.id)) ?? units[0]).status}
                  onChange={(next) => applyBulk(next)}
                  size="sm" align="right"
                />
                <button onClick={() => setSelected(new Set())} style={{
                  background: "transparent", border: "none",
                  fontFamily: "var(--font-ui)", fontSize: 11, color: "var(--ink-3)",
                  cursor: "pointer", padding: "4px 6px",
                }}>{t("workspace.decisionStatus.clear")}</button>
              </>
            )}
          </div>

          <div style={{
            background: "var(--surface)", border: "1px solid var(--rule)",
            borderRadius: 10, overflow: "visible",
          }}>
            {units.map((u, i) => {
              const checked = selected.has(u.id);
              return (
                <div key={u.id} style={{
                  display: "flex", alignItems: "center", gap: 12,
                  padding: "10px 14px",
                  borderBottom: i < units.length - 1 ? "1px solid var(--rule)" : "none",
                  background: checked ? "var(--accent-soft)" : "transparent",
                  opacity: u.status === "Rejected" ? 0.65 : 1,
                  transition: "background 120ms",
                }}>
                  <span onClick={() => toggle(u.id)} style={{
                    width: 14, height: 14, borderRadius: 3, flexShrink: 0,
                    border: `1.5px solid ${checked ? "var(--accent)" : "var(--rule-strong)"}`,
                    background: checked ? "var(--accent)" : "transparent",
                    cursor: "pointer",
                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                  }}>
                    {checked && <svg width="9" height="9" viewBox="0 0 12 12" fill="none"><path d="M2.5 6.5l2.2 2.2L9.5 3.5" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>}
                  </span>
                  <div style={{ width: 70, flexShrink: 0 }}>
                    <div style={{ fontFamily: "var(--font-mono)", fontSize: 12, fontWeight: 600, color: "var(--ink)" }}>{u.label}</div>
                  </div>
                  <div style={{ flex: "1 1 auto", minWidth: 0, fontFamily: "var(--font-ui)", fontSize: 11, color: "var(--ink-3)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {t("workspace.facts.floor")} {u.floor || "—"} · {t("workspace.facts.stack")} {u.stack || "—"} · {u.facing && u.facing !== "—" ? facingLabel(t, u.facing) : t("workspace.decisionStatus.facingUnknown")}
                  </div>
                  <div style={{ flexShrink: 0, fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--ink)", fontWeight: 500, textAlign: "right" }}>
                    {u.price > 0 ? `$${(u.price / 1e6).toFixed(2)}M` : "—"}
                  </div>
                  <div onClick={(e) => e.stopPropagation()}>
                    <StatusPicker value={u.status} onChange={(next) => setUnitStatus(u.id, next)} size="sm" align="right" />
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

function SectionLabel({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <div style={{
      fontFamily: "var(--font-ui)", fontSize: 11, fontWeight: 600,
      letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--ink-4)",
      padding: "0 0 8px",
      ...style,
    }}>{children}</div>
  );
}

function Tag({ children, tone }: { children: React.ReactNode; tone: string }) {
  const tones: Record<string, { bg: string; ink: string }> = {
    pos: { bg: "var(--pos-bg)", ink: "var(--pos)" },
    neg: { bg: "var(--neg-bg)", ink: "var(--neg)" },
    warn: { bg: "var(--warn-bg)", ink: "var(--warn)" },
    neutral: { bg: "var(--surface-2)", ink: "var(--ink-2)" },
  };
  const t = tones[tone] ?? tones.neutral;
  return (
    <span style={{
      display: "inline-block", padding: "2px 7px", borderRadius: 4,
      fontFamily: "var(--font-ui)", fontSize: 12, fontWeight: 500,
      background: t.bg, color: t.ink,
    }}>{children}</span>
  );
}

function UnknownPill() {
  return <span style={{ color: "var(--ink-4)", fontStyle: "italic", fontSize: 11 }}>Unknown</span>;
}

function RatingDots({ value, max = 5 }: { value: number | null | undefined; max?: number }) {
  if (!value) return <span style={{ fontFamily: "var(--font-ui)", fontSize: 12, color: "var(--ink-4)", fontStyle: "italic" }}>—</span>;
  return (
    <div style={{ display: "flex", gap: 2, alignItems: "center" }}>
      {Array.from({ length: max }).map((_, i) => (
        <div key={i} style={{ width: 6, height: 6, borderRadius: 6, background: i < value ? "var(--accent)" : "var(--rule-strong)" }} />
      ))}
    </div>
  );
}

function AIStarIcon() {
  return (
    <svg width="11" height="11" viewBox="0 0 16 16" fill="none">
      <path d="M8 1L10.2 5.8L15 8L10.2 10.2L8 15L5.8 10.2L1 8L5.8 5.8Z" fill="var(--ai-accent)" />
    </svg>
  );
}

// ─── Nearest MRT section ─────────────────────────────────────────────────────

// MRT_LINE_PALETTE maps the canonical line abbreviation (NSL, EWL, etc.)
// to the official LTA brand colour + readable foreground. Keys mirror
// the values emitted by the dataengine's lineFromCodePrefix map. Unknown
// lines fall through to a neutral grey via mrtLineColors().
const MRT_LINE_PALETTE: Record<string, { bg: string; fg: string }> = {
  NSL:  { bg: "#D42E12", fg: "#FFFFFF" }, // North-South — red
  EWL:  { bg: "#009645", fg: "#FFFFFF" }, // East-West — green (incl. Changi branch CG)
  NEL:  { bg: "#9900AA", fg: "#FFFFFF" }, // North-East — purple
  CCL:  { bg: "#FA9E0D", fg: "#1A1A1A" }, // Circle — orange (incl. CE extension)
  DTL:  { bg: "#005EC4", fg: "#FFFFFF" }, // Downtown — blue
  TEL:  { bg: "#9D5B25", fg: "#FFFFFF" }, // Thomson-East Coast — brown
  BPL:  { bg: "#748477", fg: "#FFFFFF" }, // Bukit Panjang LRT — grey-green
  SLRT: { bg: "#748477", fg: "#FFFFFF" }, // Sengkang LRT
  PLRT: { bg: "#748477", fg: "#FFFFFF" }, // Punggol LRT
  JRL:  { bg: "#0099AA", fg: "#FFFFFF" }, // Jurong Region Line — teal (future)
  CRL:  { bg: "#97C616", fg: "#1A1A1A" }, // Cross Island — lime (future)
};

function mrtLineColors(line?: string): { bg: string; fg: string } {
  if (line && MRT_LINE_PALETTE[line]) return MRT_LINE_PALETTE[line];
  return { bg: "var(--ink-3)", fg: "#FFFFFF" };
}

// linePrefixToLine maps a station code's two-letter prefix to the
// canonical line abbreviation. Same shape as the backend's
// lineFromCodePrefix in dataengine/ingest/pois.go — duplicated here so
// the FE can colour a code badge even when LTA codes/lines weren't
// joined upstream (e.g. fallback path).
const linePrefixToLine: Record<string, string> = {
  NS: "NSL", EW: "EWL", CG: "EWL", NE: "NEL", CC: "CCL", CE: "CCL",
  DT: "DTL", TE: "TEL", BP: "BPL", SE: "SLRT", SW: "SLRT",
  PE: "PLRT", PW: "PLRT", JE: "JRL", JS: "JRL", JW: "JRL",
  CR: "CRL", CP: "CRL",
};

function lineForCode(code: string): string | undefined {
  if (code.length < 2) return undefined;
  return linePrefixToLine[code.slice(0, 2).toUpperCase()];
}

// LineBadge is a small colored pill carrying one station code (e.g.
// "EW15"). At an interchange a row renders multiple of these
// side-by-side. Line colour follows the LTA palette; unknown codes
// degrade to a neutral pill rather than disappearing — better to
// show "?" coloured than to lose the station entry.
function LineBadge({ code, line }: { code: string; line?: string }) {
  const resolvedLine = line ?? lineForCode(code);
  const { bg, fg } = mrtLineColors(resolvedLine);
  return (
    <span
      title={resolvedLine ? `${resolvedLine} · ${code}` : code}
      style={{
        display: "inline-flex",
        alignItems: "center",
        background: bg,
        color: fg,
        fontFamily: "var(--font-mono, var(--font-ui))",
        fontSize: 10.5,
        fontWeight: 700,
        letterSpacing: "0.04em",
        padding: "2px 6px",
        borderRadius: 4,
        whiteSpace: "nowrap",
        lineHeight: 1.1,
      }}
    >
      {code}
    </span>
  );
}

// formatMRTDistance renders metres into a compact display string.
// < 1000 → "380 m"; ≥ 1000 → "1.2 km" with one decimal. Stays in sync
// with the 80 m/min walking heuristic so a row reading "5 min · 380 m"
// doesn't surprise the reader.
function formatMRTDistance(m: number): string {
  if (!m || m < 0) return "";
  if (m < 1000) return `${m} m`;
  return `${(m / 1000).toFixed(1)} km`;
}

// formatExitLabel normalises the closest-exit label for display. The
// upstream data.gov.sg GeoJSON emits a mix of bare letters ("A"), bare
// numbers ("4"), and pre-formatted strings ("Exit 3"). Prefix the
// short forms with "Exit " so the cell reads naturally; leave the
// pre-formatted ones alone.
function formatExitLabel(raw?: string): string {
  if (!raw) return "";
  const v = raw.trim();
  if (v === "") return "";
  if (/^exit\b/i.test(v)) return v;
  return `Exit ${v}`;
}

// prettyStationName strips the verbose " MRT STATION" / " LRT STATION"
// suffix from the data.gov.sg upstream and converts to Title Case for
// display. Pure presentation transform — raw DB values stay UPPERCASE
// (same memo as URA strings: never mutate the canonical store, only
// the display boundary).
function prettyStationName(raw: string): string {
  if (!raw) return "";
  let s = raw.trim();
  // Case-insensitive suffix strip in case upstream varies.
  s = s.replace(/\s+MRT STATION$/i, "").replace(/\s+LRT STATION$/i, "");
  // Title Case each word. Keep "MRT"/"LRT" upper if they're still
  // present (e.g. names that don't have the " STATION" tail).
  return s.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase())
    .replace(/\bMrt\b/g, "MRT").replace(/\bLrt\b/g, "LRT");
}

// NearestMRTCell is the value-cell renderer for the "Nearest MRT" row
// in the dossier's Key Facts table. Deliberately compact — a single
// line so the row height matches the rest of the table. Shows the
// closest station's name, its line-code badge(s) (multiple at an
// interchange, color-coded by line), and the walk time. If there are
// additional nearby stations, a quiet "+N nearby" tail hints at that
// without spelling them out.
//
// Empty / pre-enrichment falls back to the legacy mrt_desc string when
// present, otherwise renders UnknownPill — matching the rest of the
// table's empty-cell convention.
function NearestMRTCell({ stations, fallback }: { stations?: MRTStation[]; fallback?: string }) {
  const { t } = useTranslation();
  if (!stations || stations.length === 0) {
    if (fallback && fallback.trim() !== "") {
      return <span>{fallback}</span>;
    }
    return <UnknownPill />;
  }
  const s = stations[0];
  const others = stations.slice(1);
  const exitLabel = formatExitLabel(s.nearest_exit);
  const dist = formatMRTDistance(s.distance_m);
  // Hide the badge entirely when codes haven't been ingested yet for
  // this station. Loud "?" pills add no signal — the station name +
  // walk time are still informative on their own.
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
      <span>{prettyStationName(s.station)}</span>
      {exitLabel && (
        <span style={{ color: "var(--ink-3)", fontSize: 11 }}>{exitLabel}</span>
      )}
      {s.codes.length > 0 && (
        <span style={{ display: "inline-flex", gap: 3 }}>
          {s.codes.map((c, i) => (
            <LineBadge key={c + i} code={c} line={s.lines[i]} />
          ))}
        </span>
      )}
      <span style={{ color: "var(--ink-3)" }}>
        · {t("workspace.facts.mrtMinWalk", { min: Math.max(1, s.walk_min) })}
        {dist && (
          <span style={{ color: "var(--ink-4)", marginLeft: 4 }}>({dist})</span>
        )}
      </span>
      {others.length > 0 && <NearbyMoreTooltip stations={others} />}
    </span>
  );
}

// NearbyMoreTooltip is the small "+N nearby" trigger that reveals the
// full list of additional stations on hover/focus. The popover content
// is rendered via React portal into document.body so it can't be
// clipped by ancestor overflow:hidden (the Key Facts wrapper has it,
// which was hiding the bottom half of the popover) and so it cleanly
// sits above all surrounding stacking contexts.
//
// Positioning uses getBoundingClientRect on the trigger — `position:
// fixed` coordinates are in viewport space, which matches what the
// rect call returns. Updated on scroll/resize while the popover is
// open so it doesn't drift away from the trigger.
function NearbyMoreTooltip({ stations }: { stations: MRTStation[] }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLSpanElement | null>(null);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);

  // Estimate the popover's actual rendered height from the station
  // count + whether each row will render an exit-label subtitle. The
  // previous flip-above logic used the maxHeight (260) as the
  // reserved bound, which pushed the popover ~100px too high when
  // there were only a handful of short rows. Capped at maxHeight so
  // long lists fall back to scrolling instead of overflowing upward.
  const estPopoverHeight = useMemo(() => {
    const containerPadY = 8; // 4 top + 4 bottom
    const rowGap = 1;        // border between rows
    const rowBase = 26;      // single line: name + badges + walk-min
    const rowExit = 14;      // additional line height when an exit label renders
    let h = containerPadY;
    stations.forEach((s, i) => {
      h += rowBase;
      if (s.nearest_exit && s.nearest_exit.trim() !== "") {
        h += rowExit;
      }
      if (i > 0) h += rowGap;
    });
    return Math.min(h, 260);
  }, [stations]);

  // Recompute on open + on viewport changes. useLayoutEffect to avoid
  // a one-frame flash where the popover renders at 0,0 before settling.
  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const update = () => {
      const r = triggerRef.current?.getBoundingClientRect();
      if (!r) return;
      // Default placement: just below the trigger. If that would clip
      // off the bottom of the viewport, flip above — but only by the
      // estimated height, not the full maxHeight, so a 5-row popover
      // doesn't sit halfway up the page.
      const popoverW = 300;
      let top = r.bottom + 4;
      if (top + estPopoverHeight > window.innerHeight - 8) {
        top = Math.max(8, r.top - estPopoverHeight - 4);
      }
      let left = r.left;
      if (left + popoverW > window.innerWidth - 8) {
        left = Math.max(8, window.innerWidth - popoverW - 8);
      }
      setCoords({ top, left });
    };
    update();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [open, estPopoverHeight]);

  return (
    <span
      ref={triggerRef}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
      tabIndex={0}
      style={{
        display: "inline-flex",
        alignItems: "center",
        color: "var(--ink-4)",
        fontSize: 11,
        cursor: "help",
        outline: "none",
        borderBottom: "1px dotted var(--ink-4)",
        lineHeight: 1.2,
      }}
    >
      +{stations.length} nearby
      {open && coords && typeof document !== "undefined" && createPortal(
        <div
          role="tooltip"
          style={{
            position: "fixed",
            top: coords.top,
            left: coords.left,
            zIndex: 1000,
            minWidth: 220,
            maxWidth: 300,
            maxHeight: 260,
            overflowY: "auto",
            padding: "4px 8px",
            background: "var(--surface)",
            color: "var(--ink)",
            border: "1px solid var(--rule)",
            borderRadius: 8,
            boxShadow: "0 6px 18px rgba(0,0,0,0.12)",
            fontFamily: "var(--font-ui)",
            fontSize: 11.5,
            lineHeight: 1.35,
            letterSpacing: "-0.005em",
            cursor: "default",
            // Pointer-events allow the user to scroll the popover when
            // there are many stations; the parent's onMouseLeave still
            // fires when the cursor leaves the trigger area.
            pointerEvents: "auto",
          }}
        >
          {stations.map((s, i) => {
            const exitLabel = formatExitLabel(s.nearest_exit);
            const dist = formatMRTDistance(s.distance_m);
            return (
              <div
                key={s.station + i}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "4px 0",
                  borderTop: i === 0 ? "none" : "1px solid var(--rule)",
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 4, flexWrap: "wrap" }}>
                    <span style={{ fontWeight: 500 }}>{prettyStationName(s.station)}</span>
                    {s.codes.length > 0 && (
                      <span style={{ display: "inline-flex", gap: 2 }}>
                        {s.codes.map((c, ci) => (
                          <LineBadge key={c + ci} code={c} line={s.lines[ci]} />
                        ))}
                      </span>
                    )}
                  </div>
                  {exitLabel && (
                    <span style={{ color: "var(--ink-4)", fontSize: 10.5 }}>{exitLabel}</span>
                  )}
                </div>
                <div style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                  <div style={{ color: "var(--ink-2)" }}>
                    {t("workspace.facts.mrtMinWalk", { min: Math.max(1, s.walk_min) })}
                  </div>
                  {dist && (
                    <div style={{ color: "var(--ink-4)", fontSize: 10.5 }}>{dist}</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>,
        document.body
      )}
    </span>
  );
}

// GeneratingPill: tiny visual cue beside an AI label when the server has
// the row marked stale (i.e. the summary worker is mid-regen). Shown on
// property snapshot, viewing summary, and compare summary surfaces. The
// dot is a CSS animation; the parent component decides what the label
// should say (workspace.ai.generating vs workspace.ai.updating).
function GeneratingPill({ label }: { label: string }) {
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      padding: "1px 8px", borderRadius: 999,
      background: "var(--ai-bg)", border: "1px solid var(--ai-rule)",
      fontFamily: "var(--font-ui)", fontSize: 10, fontWeight: 500,
      color: "var(--ai-accent)", letterSpacing: "0.04em",
    }}>
      <span style={{
        width: 5, height: 5, borderRadius: "50%",
        background: "var(--ai-accent)",
        animation: "propwise-pulse 1.2s ease-in-out infinite",
      }} />
      {label}
      <style>{`@keyframes propwise-pulse { 0%, 100% { opacity: 0.35 } 50% { opacity: 1 } }`}</style>
    </span>
  );
}

// ─── ProjectNav ───────────────────────────────────────────────────────────────

function ModuleIcon({ id }: { id: string }) {
  switch (id) {
    case "overview": return <svg width="13" height="13" viewBox="0 0 14 14" fill="none"><rect x="1.5" y="1.5" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.3" /><rect x="7.5" y="1.5" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.3" /><rect x="1.5" y="7.5" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.3" /><rect x="7.5" y="7.5" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.3" /></svg>;
    case "shortlist": return <svg width="13" height="13" viewBox="0 0 14 14" fill="none"><path d="M1.5 4h11M1.5 7h11M1.5 10h7" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" /></svg>;
    case "compare": return <svg width="13" height="13" viewBox="0 0 14 14" fill="none"><path d="M4.5 2v10M9.5 2v10M2 5h5M7 9h5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" /></svg>;
    case "viewings": return <svg width="13" height="13" viewBox="0 0 14 14" fill="none"><rect x="1.5" y="2.5" width="11" height="9" rx="1.2" stroke="currentColor" strokeWidth="1.3" /><path d="M5 2.5V1M9 2.5V1M1.5 5.5h11" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" /></svg>;
    case "decision": return <svg width="13" height="13" viewBox="0 0 14 14" fill="none"><circle cx="7" cy="7" r="5" stroke="currentColor" strokeWidth="1.3" /><path d="M4.5 7l2 2 3-3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" /></svg>;
    default: return null;
  }
}

function ProjectNav({ active, onSelect, collapsed, onToggle }: { active: string; onSelect: (id: string) => void; collapsed: boolean; onToggle: () => void }) {
  const t = useWsT();
  const { project, properties, viewings } = useWsData();
  const scheduledCount = viewings.filter(v => v.status === "scheduled").length;
  const activeCount = properties.filter(p => p.status !== "Rejected").length;

  if (collapsed) {
    return (
      <div style={{ width: 44, flexShrink: 0, background: "var(--surface)", borderRight: "1px solid var(--rule)", display: "flex", flexDirection: "column", alignItems: "center", overflow: "hidden", transition: "width 180ms ease" }}>
        <button onClick={onToggle} title="Expand navigation" style={{ width: 44, height: 44, display: "flex", alignItems: "center", justifyContent: "center", background: "transparent", border: "none", borderBottom: "1px solid var(--rule)", cursor: "pointer", color: "var(--ink-3)", flexShrink: 0 }}>
          <svg width="13" height="13" viewBox="0 0 14 14" fill="none"><path d="M5 3l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1, padding: "8px 0", flex: 1 }}>
          {MODULES.map(m => {
            const badge = m.id === "viewings" ? scheduledCount.toString() : m.id === "shortlist" ? activeCount.toString() : "";
            return (
              <button key={m.id} data-tour-id={`workspace.nav.${m.id}`} onClick={() => onSelect(m.id)} title={t(`workspace.modules.${m.id}`)} style={{ width: 36, height: 34, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 6, border: "none", background: active === m.id ? "var(--accent-soft)" : "transparent", color: active === m.id ? "var(--accent)" : "var(--ink-3)", cursor: "pointer", position: "relative" }}>
                <ModuleIcon id={m.id} />
                {badge && parseInt(badge) > 0 && (
                  <span style={{ position: "absolute", top: 4, right: 4, width: 14, height: 14, borderRadius: 7, background: "var(--accent)", color: "white", fontSize: 8, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center" }}>{badge}</span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div style={{ width: 210, flexShrink: 0, background: "var(--surface)", borderRight: "1px solid var(--rule)", display: "flex", flexDirection: "column", overflow: "hidden", transition: "width 180ms ease" }}>
      <div style={{ padding: "0 8px 0 14px", borderBottom: "1px solid var(--rule)", display: "flex", alignItems: "center", gap: 6, minHeight: 44 }}>
        <div style={{ flex: 1, fontFamily: "var(--font-ui)", fontSize: 11, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--ink-4)" }}>{t("workspace.nav.currentProject")}</div>
        <button onClick={onToggle} title="Collapse navigation" style={{ width: 26, height: 26, display: "flex", alignItems: "center", justifyContent: "center", background: "transparent", border: "none", cursor: "pointer", color: "var(--ink-4)", borderRadius: 5, flexShrink: 0 }}
          onMouseEnter={e => (e.currentTarget as HTMLButtonElement).style.background = "var(--surface-2)"}
          onMouseLeave={e => (e.currentTarget as HTMLButtonElement).style.background = "transparent"}>
          <svg width="12" height="12" viewBox="0 0 14 14" fill="none"><path d="M9 3l-4 4 4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
      </div>
      <div style={{ padding: "10px 12px", borderBottom: "1px solid var(--rule)" }}>
        <div style={{ width: "100%", textAlign: "left", background: "var(--accent-soft)", border: "1px solid rgba(29,74,58,0.15)", borderRadius: 8, padding: "8px 10px", display: "flex", alignItems: "flex-start", gap: 8 }}>
          <div style={{ width: 20, height: 20, borderRadius: 5, background: "var(--accent)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1 }}>
            <svg width="10" height="10" viewBox="0 0 14 14" fill="none"><path d="M2 11V5L7 2l5 3v6" stroke="white" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" /><path d="M5 11V8h4v3" stroke="white" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" /></svg>
          </div>
          <div>
            <div style={{ fontFamily: "var(--font-ui)", fontSize: 11.5, fontWeight: 600, color: "var(--accent-ink)", lineHeight: 1.3, letterSpacing: "-0.01em" }}>{project.name || "My Property Search"}</div>
            <div style={{ fontFamily: "var(--font-ui)", fontSize: 12, color: "var(--accent)", marginTop: 1 }}>{project.budget !== "—" ? project.budget : ""}</div>
          </div>
        </div>
      </div>
      <div style={{ padding: "10px 10px", flex: 1, overflow: "auto" }}>
        <div style={{ fontFamily: "var(--font-ui)", fontSize: 11, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--ink-4)", padding: "4px 4px 8px" }}>{t("workspace.nav.modules")}</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {MODULES.map(m => {
            const badge = m.id === "viewings" ? scheduledCount.toString() : m.id === "shortlist" ? activeCount.toString() : "";
            return (
              <button key={m.id} data-tour-id={`workspace.nav.${m.id}`} onClick={() => onSelect(m.id)} style={{
                display: "flex", alignItems: "center", gap: 9,
                padding: "7px 8px", borderRadius: 6, border: "none",
                background: active === m.id ? "var(--accent-soft)" : "transparent",
                color: active === m.id ? "var(--accent-ink)" : "var(--ink-2)",
                fontFamily: "var(--font-ui)", fontSize: 12.5,
                fontWeight: active === m.id ? 500 : 400, cursor: "pointer",
                textAlign: "left", letterSpacing: "-0.005em", position: "relative",
              }}>
                {active === m.id && <div style={{ position: "absolute", left: 0, top: 4, bottom: 4, width: 2, background: "var(--accent)", borderRadius: 2 }} />}
                <span style={{ color: active === m.id ? "var(--accent)" : "var(--ink-4)", display: "flex" }}><ModuleIcon id={m.id} /></span>
                <span style={{ flex: 1 }}>{t(`workspace.modules.${m.id}`)}</span>
                {badge && <span style={{ fontSize: 11, fontWeight: 600, color: "var(--ink-3)", background: "var(--surface-2)", padding: "1px 5px", borderRadius: 3 }}>{badge}</span>}
              </button>
            );
          })}
        </div>
      </div>
      <div style={{ padding: "10px 14px 14px", borderTop: "1px solid var(--rule)", fontFamily: "var(--font-ui)", fontSize: 12, color: "var(--ink-3)", lineHeight: 1.5 }}>
        <div style={{ marginBottom: 2 }}>{properties.length} projects · {properties.reduce((n, p) => n + (p.units?.length ?? 0), 0)} known units</div>
        <div>{t("workspace.nav.updatedAgo", { hours: 2 })}</div>
      </div>
    </div>
  );
}

// ─── OverviewTab ──────────────────────────────────────────────────────────────

function OverviewTab({ onOpenProperty }: { onOpenProperty: (propId: string) => void }) {
  const t = useWsT();
  const isMobile = useIsMobile();
  const { project, properties, viewings, openProperty, openModal, goToModule } = useWsData();
  const topProps = properties.filter(p => ["Worth Checking", "Viewing Scheduled", "Viewed"].includes(p.status)).slice(0, 3);
  const byStatus = (s: string) => properties.filter(p => p.status === s).length;
  const pad = isMobile ? "16px 14px" : "28px 32px";

  type StatPopup = { label: string; props: Property[] } | null;
  const [statsPopup, setStatsPopup] = useState<StatPopup>(null);
  const scheduledViewingProps = viewings.filter(v => v.status === "scheduled").map(v => properties.find(p => p.id === v.propId)).filter(Boolean) as Property[];

  const stats: [string, string, Property[]][] = [
    [properties.length.toString(), t("workspace.overview.projects"), properties],
    [`${byStatus("Worth Checking") + byStatus("Viewing Scheduled")}`, t("workspace.overview.worthChecking"), properties.filter(p => ["Worth Checking", "Viewing Scheduled"].includes(p.status))],
    [`${viewings.filter(v => v.status === "scheduled").length}`, t("workspace.overview.viewingsSched"), scheduledViewingProps],
    [byStatus("Viewed").toString(), t("workspace.overview.viewed"), properties.filter(p => p.status === "Viewed")],
    [byStatus("Rejected").toString(), t("workspace.overview.rejected"), properties.filter(p => p.status === "Rejected")],
  ];

  return (
    <div style={{ padding: pad, overflow: "auto", flex: 1 }} onClick={() => setStatsPopup(null)}>
      {/* Project card */}
      <div style={{ background: "var(--surface)", border: "1px solid var(--rule)", borderRadius: 12, padding: "16px 18px", marginBottom: isMobile ? 16 : 24, display: "flex", flexDirection: isMobile ? "column" : "row", gap: isMobile ? 14 : 32 }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
            <div style={{ fontFamily: "var(--font-ui)", fontSize: 11, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--ink-4)", flex: 1 }}>{t("workspace.overview.projectName")}</div>
            <button onClick={() => openModal({ type: "editWorkspace" })} title="Edit workspace" style={{ background: "transparent", border: "none", cursor: "pointer", color: "var(--ink-4)", padding: 3, borderRadius: 5, display: "flex" }}
              onMouseEnter={e => (e.currentTarget as HTMLButtonElement).style.background = "var(--surface-2)"}
              onMouseLeave={e => (e.currentTarget as HTMLButtonElement).style.background = "transparent"}>
              <svg width="12" height="12" viewBox="0 0 14 14" fill="none"><path d="M10 2l2 2-8 8H2v-2L10 2z" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </button>
          </div>
          <div style={{ fontFamily: "var(--font-serif)", fontSize: isMobile ? 16 : 20, fontWeight: 500, color: "var(--ink)", letterSpacing: "-0.02em", marginBottom: 4 }}>{project.name || "—"}</div>
          {project.intent && <div style={{ fontFamily: "var(--font-ui)", fontSize: 12, color: "var(--ink-3)", marginBottom: 10 }}>{t(`workspace.overview.intentOpts.${project.intent}`, { defaultValue: project.intent })}</div>}
          <div style={{ display: "flex", gap: isMobile ? 16 : 24, flexWrap: "wrap" }}>
            {([
              [t("workspace.overview.budget"), project.budget],
              [t("workspace.overview.areas"), project.areas === "—"
                ? "—"
                : project.areas.split(",").map(a => a.trim()).filter(Boolean)
                    .map(a => areaLabel(a, t)).join("、")],
              [t("workspace.overview.timeline"), project.timeline === "—"
                ? "—"
                : t(`auth.prefs.timelineOpts.${project.timeline}`, { defaultValue: project.timeline })],
            ] as [string, string][]).map(([k, v]) => (
              <div key={k}>
                <div style={{ fontFamily: "var(--font-ui)", fontSize: 12, color: "var(--ink-4)", letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 2 }}>{k}</div>
                <div style={{ fontFamily: "var(--font-ui)", fontSize: 12, color: "var(--ink)", fontWeight: 500 }}>{v}</div>
              </div>
            ))}
          </div>
        </div>
        <div style={{ borderTop: isMobile ? "1px solid var(--rule)" : "none", paddingTop: isMobile ? 12 : 0 }}>
          <div style={{ fontFamily: "var(--font-ui)", fontSize: 12, color: "var(--ink-4)", letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 6 }}>{t("workspace.overview.priorities")}</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
            {project.priorities.map(p => <span key={p} style={{ padding: "3px 9px", borderRadius: 5, background: "var(--surface-2)", fontFamily: "var(--font-ui)", fontSize: 11, color: "var(--ink-2)" }}>{t(`auth.prefs.priorityOpts.${p}`, { defaultValue: p })}</span>)}
          </div>
        </div>
      </div>

      {/* Seeded-candidates CTA — surfaces when the workspace has freshly-seeded
          properties but the user hasn't yet engaged (no scheduled viewings,
          no viewed/rejected). Fades out once stats start carrying signal. */}
      {properties.length > 0
        && byStatus("Viewing Scheduled") + byStatus("Viewed") + byStatus("Rejected") === 0 && (
        <button
          data-tour-id="workspace.overview.candidatesCta"
          onClick={() => goToModule("shortlist")}
          style={{
            display: "flex", alignItems: "center", gap: 14,
            width: "100%", textAlign: "left", cursor: "pointer",
            background: "var(--accent-soft)", border: "1px solid var(--accent)",
            borderRadius: 12, padding: isMobile ? "12px 14px" : "14px 18px",
            marginBottom: isMobile ? 16 : 24, fontFamily: "var(--font-ui)",
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13.5, fontWeight: 600, color: "var(--accent)", marginBottom: 2 }}>
              {t("workspace.overview.candidatesCta.title", { count: properties.length })}
            </div>
            <div style={{ fontSize: 12, color: "var(--ink-2)", lineHeight: 1.45 }}>
              {t("workspace.overview.candidatesCta.body")}
            </div>
          </div>
          <span style={{ fontSize: 12, fontWeight: 600, color: "var(--accent)", whiteSpace: "nowrap" }}>
            {t("workspace.overview.candidatesCta.action")}
          </span>
        </button>
      )}

      {/* Stats row */}
      <div style={{ display: "flex", gap: isMobile ? 8 : 12, marginBottom: isMobile ? 16 : 24, flexWrap: "wrap", position: "relative" }}>
        {stats.map(([n, label, items]) => (
          <button key={label} onClick={e => { e.stopPropagation(); setStatsPopup(prev => prev?.label === label ? null : { label, props: items }); }}
            style={{ background: statsPopup?.label === label ? "var(--accent-soft)" : "var(--surface)", border: `1px solid ${statsPopup?.label === label ? "var(--accent)" : "var(--rule)"}`, borderRadius: 10, padding: isMobile ? "10px 12px" : "12px 18px", minWidth: isMobile ? 0 : 90, flex: 1, textAlign: "left", cursor: "pointer", transition: "border-color 120ms, background 120ms" }}>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: isMobile ? 18 : 22, fontWeight: 500, color: statsPopup?.label === label ? "var(--accent)" : "var(--ink)", letterSpacing: "-0.02em", lineHeight: 1 }}>{n}</div>
            <div style={{ fontFamily: "var(--font-ui)", fontSize: 11, color: "var(--ink-3)", marginTop: 4 }}>{label}</div>
          </button>
        ))}
        {statsPopup && (
          <div onClick={e => e.stopPropagation()} style={{ position: "absolute", top: "calc(100% + 8px)", left: 0, right: 0, zIndex: 40, background: "var(--surface)", border: "1px solid var(--rule)", borderRadius: 12, boxShadow: "0 12px 36px rgba(0,0,0,0.18)", overflow: "hidden" }}>
            <div style={{ padding: "10px 16px 8px", borderBottom: "1px solid var(--rule)", fontFamily: "var(--font-ui)", fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--ink-4)" }}>{statsPopup.label}</div>
            {statsPopup.props.length === 0 ? (
              <div style={{ padding: "14px 16px", fontFamily: "var(--font-serif)", fontSize: 13, fontStyle: "italic", color: "var(--ink-4)" }}>None</div>
            ) : (
              statsPopup.props.map(p => (
                <button key={p.id} onClick={() => { openProperty(p.id); setStatsPopup(null); }}
                  style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, width: "100%", textAlign: "left", background: "transparent", border: "none", borderBottom: "1px solid var(--rule)", padding: "11px 16px", cursor: "pointer" }}
                  onMouseEnter={e => (e.currentTarget as HTMLButtonElement).style.background = "var(--surface-2)"}
                  onMouseLeave={e => (e.currentTarget as HTMLButtonElement).style.background = "transparent"}>
                  <div>
                    <div style={{ fontFamily: "var(--font-ui)", fontSize: 13, fontWeight: 600, color: "var(--ink)" }}>{titleCaseSG(p.name)}</div>
                    <div style={{ fontFamily: "var(--font-ui)", fontSize: 11, color: "var(--ink-3)", marginTop: 2 }}>{p.district} · {p.tenure} · Fit {p.fitScore}</div>
                  </div>
                  <StatusBadge status={p.status} small />
                </button>
              ))
            )}
          </div>
        )}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: isMobile ? 16 : 24, alignItems: "start" }}>
        <div>
          <SectionLabel>{t("workspace.overview.topCandidates")}</SectionLabel>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {topProps.map(prop => (
              <button key={prop.id} onClick={() => onOpenProperty(prop.id)} style={{ background: "var(--surface)", border: "1px solid var(--rule)", borderRadius: 10, padding: "13px 15px", textAlign: "left", cursor: "pointer", width: "100%", transition: "border-color 120ms, box-shadow 120ms" }}
                onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = "var(--accent)"; (e.currentTarget as HTMLButtonElement).style.boxShadow = "0 0 0 3px var(--accent-soft)"; }}
                onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = "var(--rule)"; (e.currentTarget as HTMLButtonElement).style.boxShadow = "none"; }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 8, marginBottom: 4 }}>
                  <div>
                    <div style={{ fontFamily: "var(--font-ui)", fontSize: 13, fontWeight: 600, color: "var(--ink)", letterSpacing: "-0.01em" }}>{titleCaseSG(prop.name)}</div>
                    <div style={{ fontFamily: "var(--font-ui)", fontSize: 11, color: "var(--ink-3)", marginTop: 2 }}>{prop.district} · {prop.tenure} · {prop.beds} · Fit: <FitScore score={prop.fitScore} /></div>
                  </div>
                  <StatusBadge status={prop.status} small />
                </div>
                {prop.aiSummary && <div className="markdown" style={{ fontSize: 12, color: "var(--ink-2)", lineHeight: 1.45 }} dangerouslySetInnerHTML={{ __html: renderMarkdown(prop.aiSummary) }} />}
              </button>
            ))}
          </div>
        </div>
        <div>
          <SectionLabel>{t("workspace.overview.nextActions")}</SectionLabel>
          <NextActions /></div>
      </div>
    </div>
  );
}

// ─── PropertyCard ─────────────────────────────────────────────────────────────
//
// Tabbed unit-detail layout — port of the Claude Design handoff
// (ProProp Consumer Chat / workspace.jsx :: PropertyCard). Each known
// unit becomes a tab; clicking a tab swaps the detail panel below
// without collapsing the card. Status moves from the property header
// (where it used to live as a single rolled-up badge) onto the
// per-unit detail panel — that's the surface where status now wants
// to live, since rolled-up display is the Overview's job.
//
// Properties without any known units render a slim no-units card
// instead of an empty tab strip; an "Add unit" CTA is the primary
// action there.

function PropertyCard({ prop, onOpenProperty, onOpenDetail }: { prop: Property; onOpenProperty: (propId: string) => void; onOpenDetail?: (name: string) => void }) {
  const t = useWsT();
  const { viewings, openModal, deleteUnit } = useWsData();
  const propViewings = viewings.filter(v => v.propId === prop.id);
  const openQCount = prop.questions.filter(q => q.status === "Open").length;
  const units = prop.units ?? [];
  const hasUnits = units.length > 0;
  const [activeUnitId, setActiveUnitId] = useState<string | null>(units[0]?.id ?? null);

  // Re-bind active tab if the unit list changes (add/delete) while the
  // card is mounted — otherwise activeUnitId can dangle.
  const activeUnit = units.find(u => u.id === activeUnitId) ?? units[0] ?? null;
  // The all-rejected check is a leftover signal: a property where every
  // unit is rejected fades, regardless of where the rolled-up status
  // lands. This matches the design and keeps killed projects visually
  // de-emphasised even when the user hasn't refreshed yet.
  const allRejected = hasUnits && units.every(u => u.status === "Rejected");

  return (
    <div style={{ background: "var(--surface)", border: "1px solid var(--rule)", borderRadius: 12, overflow: "hidden", opacity: allRejected || prop.status === "Rejected" ? 0.55 : 1 }}>
      {/* Header — name + meta on the left, unit-count chip on the right.
          Status badge intentionally NOT here (it lives in the active
          unit's detail panel below). */}
      <div style={{ padding: "14px 18px 0", display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
        <div style={{ minWidth: 0 }}>
          {onOpenDetail ? (
            <button
              onClick={() => onOpenDetail(prop.name)}
              title={t("workspace.shortlist.viewDetails")}
              style={{ background: "none", border: "none", padding: 0, fontFamily: "var(--font-ui)", fontSize: 15, fontWeight: 600, color: "var(--ink)", letterSpacing: "-0.01em", cursor: "pointer", textAlign: "left" }}
              onMouseEnter={e => (e.currentTarget as HTMLButtonElement).style.color = "var(--accent)"}
              onMouseLeave={e => (e.currentTarget as HTMLButtonElement).style.color = "var(--ink)"}
            >{titleCaseSG(prop.name)}</button>
          ) : (
            <div style={{ fontFamily: "var(--font-ui)", fontSize: 15, fontWeight: 600, color: "var(--ink)", letterSpacing: "-0.01em" }}>{titleCaseSG(prop.name)}</div>
          )}
          <div style={{ fontFamily: "var(--font-ui)", fontSize: 11.5, color: "var(--ink-3)", marginTop: 2 }}>
            {[prop.district, prop.tenure, prop.top > 0 ? `TOP ${prop.top}` : "", prop.mrt].filter(Boolean).join(" · ")}
          </div>
        </div>
        <span style={{ fontFamily: "var(--font-ui)", fontSize: 10.5, color: "var(--ink-3)", background: "var(--surface-2)", border: "1px solid var(--rule)", borderRadius: 5, padding: "2px 9px", whiteSpace: "nowrap", marginTop: 2, flexShrink: 0 }}>
          {hasUnits ? t("workspace.shortlist.unitCount", { count: units.length }) : t("workspace.shortlist.noUnitsBadge")}
        </span>
      </div>

      {hasUnits ? (
        <>
          {/* Unit tabs + Add-unit pseudo-tab. Each tab's bottom border is
              suppressed when active so the tab visually merges with the
              detail panel below — same trick the design uses. */}
          <div style={{ padding: "10px 18px 0", display: "flex", gap: 4, alignItems: "center", flexWrap: "wrap" }}>
            {units.map(u => {
              const active = activeUnit?.id === u.id;
              return (
                <button
                  key={u.id}
                  onClick={() => setActiveUnitId(u.id)}
                  style={{
                    padding: "5px 11px", borderRadius: "7px 7px 0 0", border: "1px solid var(--rule)",
                    background: active ? "var(--surface)" : "var(--surface-2)",
                    fontFamily: "var(--font-mono)", fontSize: 11.5, fontWeight: active ? 600 : 400,
                    color: active ? "var(--ink)" : "var(--ink-3)",
                    cursor: "pointer", position: "relative", zIndex: active ? 2 : 1,
                    marginBottom: active ? "-1px" : 0,
                    opacity: u.status === "Rejected" ? 0.6 : 1,
                  }}
                >{u.label}</button>
              );
            })}
            <button
              onClick={() => openModal({ type: "addUnit", propId: prop.id })}
              style={{ padding: "4px 8px", borderRadius: 6, border: "1px dashed var(--rule)", background: "transparent", fontFamily: "var(--font-ui)", fontSize: 11, color: "var(--ink-4)", cursor: "pointer", display: "flex", alignItems: "center", gap: 4, marginLeft: 2 }}
            >
              <svg width="8" height="8" viewBox="0 0 10 10" fill="none"><path d="M5 1v8M1 5h8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" /></svg>
              {t("workspace.shortlist.addUnit")}
            </button>
          </div>

          {/* Active unit detail panel — top border merges with the
              active tab above. */}
          {activeUnit && (
            <UnitDetailPanel
              prop={prop}
              unit={activeUnit}
              viewings={propViewings.filter(v => !v.unitLabel || v.unitLabel === activeUnit.label)}
              openQCount={openQCount}
              onOpenProperty={onOpenProperty}
              onAddViewing={() => openModal({ type: "addViewing", propId: prop.id })}
              onEditUnit={() => openModal({ type: "editUnit", propId: prop.id, unitId: activeUnit.id })}
              onDeleteUnit={() => { if (window.confirm(`Delete known unit ${activeUnit.label}?`)) deleteUnit(prop.id, activeUnit.id); }}
            />
          )}
        </>
      ) : (
        // No-units fallback: slim panel that pushes the user to add a
        // unit. Property-level fields (aiSummary / pros / risks /
        // fitScore) still surface here because that's the only data
        // we have until they identify a concrete listing.
        <NoUnitsPanel
          prop={prop}
          openQCount={openQCount}
          propViewings={propViewings}
          onOpenProperty={onOpenProperty}
          onAddUnit={() => openModal({ type: "addUnit", propId: prop.id })}
          onAddViewing={() => openModal({ type: "addViewing", propId: prop.id })}
        />
      )}
    </div>
  );
}

// Detail panel for one active unit tab. Layout mirrors the design's
// PropertyCard active-unit panel: left column has the price/PSF/size
// headline, the (property-level) AI summary, the four-stat tile row,
// and pros/risks; right column carries the actions and a viewings
// list scoped to this unit.
function UnitDetailPanel({ prop, unit, viewings, openQCount, onOpenProperty, onAddViewing, onEditUnit, onDeleteUnit }: {
  prop: Property; unit: Unit; viewings: Viewing[]; openQCount: number;
  onOpenProperty: (propId: string) => void;
  onAddViewing: () => void;
  onEditUnit: () => void;
  onDeleteUnit: () => void;
}) {
  const t = useWsT();
  const tiles: Array<[string, string]> = [
    [t("workspace.facts.floor"), unit.floor ? String(unit.floor) : "—"],
    [t("workspace.facts.facing"), unit.facing && unit.facing !== "—" ? unit.facing : "—"],
    [t("workspace.facts.tenancy"), unit.tenancy || "—"],
    [t("workspace.facts.maintenance"), unit.maintenance && unit.maintenance !== "—" ? unit.maintenance : "—"],
  ];
  return (
    <div style={{ padding: "14px 18px 16px", borderTop: "1px solid var(--rule)", background: "var(--surface)" }}>
      <div style={{ display: "flex", gap: 20, alignItems: "flex-start", flexWrap: "wrap" }}>
        {/* Left: stats / AI summary / tiles / pros & risks */}
        <div style={{ flex: 1, minWidth: 240 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 6, flexWrap: "wrap" }}>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: 18, fontWeight: 600, color: "var(--ink)", letterSpacing: "-0.02em" }}>
              {unit.price > 0 ? `$${(unit.price / 1e6).toFixed(2)}M` : "—"}
            </div>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--ink-3)" }}>
              {unit.psf > 0 ? `$${unit.psf.toLocaleString()} psf` : "— psf"}{unit.size > 0 ? ` · ${unit.size} sqft` : ""}
            </div>
            <StatusBadge status={unit.status} small />
            <span style={{ fontFamily: "var(--font-ui)", fontSize: 11, color: "var(--ink-3)" }}>
              {t("workspace.unit.aiFit")}: <FitScore score={prop.fitScore} />
            </span>
          </div>

          {prop.aiSummary && (
            <div className="markdown" style={{ fontSize: 13, color: "var(--ink-2)", lineHeight: 1.5, marginBottom: 10, maxWidth: 520 }} dangerouslySetInnerHTML={{ __html: renderMarkdown(prop.aiSummary) }} />
          )}

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
            {tiles.map(([label, value]) => (
              <div key={label} style={{ background: "var(--surface-2)", borderRadius: 6, padding: "5px 10px" }}>
                <div style={{ fontFamily: "var(--font-ui)", fontSize: 9.5, color: "var(--ink-4)", textTransform: "uppercase", letterSpacing: "0.06em" }}>{label}</div>
                <div style={{ fontFamily: "var(--font-ui)", fontSize: 12, color: value === "—" ? "var(--ink-4)" : "var(--ink)", fontWeight: 500, marginTop: 1 }}>{value}</div>
              </div>
            ))}
          </div>

          {(prop.pros.length > 0 || prop.risks.length > 0) && (
            <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
              {prop.pros.slice(0, 2).map((p, i) => <Tag key={`p${i}`} tone="pos">{p}</Tag>)}
              {prop.risks.slice(0, 1).map((r, i) => <Tag key={`r${i}`} tone="neg">{r}</Tag>)}
            </div>
          )}
        </div>

        {/* Right: actions + viewings + edit/delete */}
        <div style={{ flexShrink: 0, display: "flex", flexDirection: "column", gap: 6, minWidth: 150 }}>
          <button onClick={() => onOpenProperty(prop.id)} style={{ background: "var(--accent)", border: "none", borderRadius: 7, padding: "7px 14px", fontFamily: "var(--font-ui)", fontSize: 11.5, color: "white", cursor: "pointer", fontWeight: 500 }}>
            {t("workspace.unit.openDossier")}
          </button>
          <button onClick={onAddViewing} style={{ background: "transparent", border: "1px solid var(--rule)", borderRadius: 7, padding: "6px 14px", fontFamily: "var(--font-ui)", fontSize: 11.5, color: "var(--ink-2)", cursor: "pointer" }}>
            {t("workspace.unit.scheduleViewing")}
          </button>

          {viewings.length > 0 && (
            <div style={{ marginTop: 4, padding: "7px 10px", background: "var(--surface-2)", borderRadius: 7 }}>
              <div style={{ fontFamily: "var(--font-ui)", fontSize: 10, color: "var(--ink-4)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>
                {t("workspace.unit.viewings")}
              </div>
              {viewings.slice(0, 3).map(v => (
                <div key={v.id} style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 2 }}>
                  <div style={{ width: 5, height: 5, borderRadius: 5, background: v.status === "scheduled" ? "var(--accent)" : "var(--ink-4)", flexShrink: 0 }} />
                  <div style={{ fontFamily: "var(--font-ui)", fontSize: 10.5, color: "var(--ink-2)" }}>{fmtDate(v.date)} · {v.time}</div>
                </div>
              ))}
            </div>
          )}

          {/* Edit / delete affordances live here so the tab strip stays
              read-only at a glance. Open-Q badge is grouped with them
              because it's a sibling lightweight signal. */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4, fontFamily: "var(--font-ui)", fontSize: 11, color: "var(--ink-4)" }}>
            <button onClick={onEditUnit} title={t("workspace.shortlist.editUnit")}
              style={{ background: "transparent", border: "none", cursor: "pointer", color: "var(--ink-4)", padding: 2, display: "flex", alignItems: "center" }}
              onMouseEnter={e => (e.currentTarget as HTMLButtonElement).style.color = "var(--accent)"}
              onMouseLeave={e => (e.currentTarget as HTMLButtonElement).style.color = "var(--ink-4)"}>
              <svg width="12" height="12" viewBox="0 0 14 14" fill="none"><path d="M10 2l2 2-8 8H2v-2L10 2z" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </button>
            <button onClick={onDeleteUnit} title={t("workspace.shortlist.deleteUnit")}
              style={{ background: "transparent", border: "none", cursor: "pointer", color: "var(--ink-4)", padding: 2, display: "flex", alignItems: "center" }}
              onMouseEnter={e => (e.currentTarget as HTMLButtonElement).style.color = "var(--neg)"}
              onMouseLeave={e => (e.currentTarget as HTMLButtonElement).style.color = "var(--ink-4)"}>
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M1 1l10 10M11 1L1 11" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" /></svg>
            </button>
            {openQCount > 0 && (
              <span style={{ marginLeft: "auto", color: "var(--warn)" }}>
                {t("workspace.shortlist.openQ", { count: openQCount })}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Slim fallback panel for properties with zero known units.
function NoUnitsPanel({ prop, openQCount, propViewings, onOpenProperty, onAddUnit, onAddViewing }: {
  prop: Property;
  openQCount: number;
  propViewings: Viewing[];
  onOpenProperty: (propId: string) => void;
  onAddUnit: () => void;
  onAddViewing: () => void;
}) {
  const t = useWsT();
  return (
    <div style={{ padding: "12px 18px 16px", marginTop: 4 }}>
      {prop.aiSummary && (
        <div className="markdown" style={{ fontSize: 12.5, color: "var(--ink-2)", lineHeight: 1.5, marginBottom: 10 }} dangerouslySetInnerHTML={{ __html: renderMarkdown(prop.aiSummary) }} />
      )}
      {(prop.pros.length > 0 || prop.risks.length > 0) && (
        <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginBottom: 12 }}>
          {prop.pros.slice(0, 2).map((p, i) => <Tag key={`p${i}`} tone="pos">{p}</Tag>)}
          {prop.risks.slice(0, 1).map((r, i) => <Tag key={`r${i}`} tone="neg">{r}</Tag>)}
        </div>
      )}
      <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
        <button onClick={onAddUnit} style={{ background: "var(--accent)", border: "none", borderRadius: 7, padding: "7px 13px", fontFamily: "var(--font-ui)", fontSize: 12, color: "white", cursor: "pointer", fontWeight: 500, display: "flex", alignItems: "center", gap: 5 }}>
          <svg width="9" height="9" viewBox="0 0 10 10" fill="none"><path d="M5 1v8M1 5h8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" /></svg>
          {t("workspace.shortlist.addUnit")}
        </button>
        <button onClick={() => onOpenProperty(prop.id)} style={{ background: "transparent", border: "1px solid var(--rule)", borderRadius: 7, padding: "6px 13px", fontFamily: "var(--font-ui)", fontSize: 12, color: "var(--ink-2)", cursor: "pointer" }}>
          {t("workspace.unit.openDossier")}
        </button>
        <button onClick={onAddViewing} style={{ background: "transparent", border: "1px solid var(--rule)", borderRadius: 7, padding: "6px 13px", fontFamily: "var(--font-ui)", fontSize: 12, color: "var(--ink-2)", cursor: "pointer" }}>
          {t("workspace.unit.scheduleViewing")}
        </button>
        {openQCount > 0 && <span style={{ fontFamily: "var(--font-ui)", fontSize: 11.5, color: "var(--warn)" }}>{t("workspace.shortlist.openQ", { count: openQCount })}</span>}
        {propViewings.length > 0 && (
          <div style={{ display: "flex", gap: 3, alignItems: "center", marginLeft: "auto" }}>
            {propViewings.map(v => <div key={v.id} style={{ width: 6, height: 6, borderRadius: 6, background: v.status === "scheduled" ? "var(--accent)" : "var(--ink-4)" }} />)}
          </div>
        )}
        {/* Property-level status badge surfaces here — it's the only
            usable status for a property without units (the rollup
            doesn't fire when there are none). */}
        <div style={{ marginLeft: "auto" }}>
          <StatusBadge status={prop.status} small />
        </div>
      </div>
    </div>
  );
}

// ─── ShortlistTab ─────────────────────────────────────────────────────────────

function ShortlistTab({ onOpenProperty }: { onOpenProperty: (propId: string) => void }) {
  const t = useWsT();
  const { properties, viewings, openModal } = useWsData();
  const [view, setView] = useState<"card" | "table">("card");
  // ProjectDetailModal — opened when the user clicks the property name in
  // either the card or table view. Holds the raw URA project name (the
  // /dataengine/projects/{name} API expects uppercase). Null = closed.
  const [openDetailName, setOpenDetailName] = useState<string | null>(null);

  const viewBtn = (v: "card" | "table", label: string) => (
    <button key={v} onClick={() => setView(v)} style={{ padding: "4px 10px", borderRadius: 5, border: "1px solid", borderColor: view === v ? "var(--accent)" : "var(--rule)", background: view === v ? "var(--accent-soft)" : "transparent", color: view === v ? "var(--accent)" : "var(--ink-2)", fontFamily: "var(--font-ui)", fontSize: 11, fontWeight: 500, cursor: "pointer" }}>{label}</button>
  );

  const priceRange = (prop: Property) => {
    const units = prop.units ?? [];
    if (units.length === 0) return "—";
    if (units.length === 1) return `$${(units[0].price / 1e6).toFixed(2)}M`;
    const lo = Math.min(...units.map(u => u.price));
    const hi = Math.max(...units.map(u => u.price));
    return `$${(lo / 1e6).toFixed(2)}M–$${(hi / 1e6).toFixed(2)}M`;
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden" }}>
      <div style={{ padding: "13px 28px", borderBottom: "1px solid var(--rule)", display: "flex", alignItems: "center", gap: 10, background: "var(--bg)", flexShrink: 0 }}>
        <div style={{ fontFamily: "var(--font-serif)", fontSize: 16, fontWeight: 500, color: "var(--ink)", letterSpacing: "-0.01em", flex: 1 }}>{t("workspace.shortlist.title")}</div>
        <div style={{ display: "flex", gap: 5 }}>{viewBtn("card", t("workspace.shortlist.cards"))}{viewBtn("table", t("workspace.shortlist.table"))}</div>
        <button onClick={() => openModal({ type: "addProperty" })} style={{ display: "flex", alignItems: "center", gap: 6, background: "var(--accent)", color: "white", border: "none", borderRadius: 6, padding: "6px 12px", fontFamily: "var(--font-ui)", fontSize: 11.5, fontWeight: 500, cursor: "pointer" }}>
          <svg width="10" height="10" viewBox="0 0 12 12" fill="none"><path d="M6 1v10M1 6h10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" /></svg>
          {t("workspace.shortlist.addProperty")}
        </button>
      </div>
      <div style={{ flex: 1, overflow: "auto", padding: "20px 28px" }}>
        {properties.length === 0 ? (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center", padding: "48px 16px", maxWidth: 420, margin: "0 auto", gap: 10 }}>
            <div style={{ fontFamily: "var(--font-serif)", fontSize: 16, fontWeight: 500, color: "var(--ink)", letterSpacing: "-0.01em" }}>{t("workspace.shortlist.emptyTitle")}</div>
            <div style={{ fontFamily: "var(--font-ui)", fontSize: 12.5, color: "var(--ink-3)", lineHeight: 1.5 }}>{t("workspace.shortlist.emptyBody")}</div>
            <button onClick={() => openModal({ type: "addProperty" })} style={{ marginTop: 6, display: "inline-flex", alignItems: "center", gap: 6, background: "var(--accent)", color: "white", border: "none", borderRadius: 6, padding: "8px 14px", fontFamily: "var(--font-ui)", fontSize: 12, fontWeight: 500, cursor: "pointer" }}>
              <svg width="10" height="10" viewBox="0 0 12 12" fill="none"><path d="M6 1v10M1 6h10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" /></svg>
              {t("workspace.shortlist.addProperty")}
            </button>
          </div>
        ) : view === "card" ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 16, maxWidth: 900 }}>
            {properties.map(prop => (
              <PropertyCard key={prop.id} prop={prop} onOpenProperty={onOpenProperty} onOpenDetail={setOpenDetailName} />
            ))}
          </div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: "2px solid var(--rule)" }}>
                {["Property", "Status", "Price", "Fit", "Questions", "Viewings", ""].map(col => (
                  <th key={col} style={{ fontFamily: "var(--font-ui)", fontSize: 12, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--ink-4)", padding: "6px 10px 10px", textAlign: "left", whiteSpace: "nowrap" }}>{col}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {properties.map(prop => {
                const openQ = prop.questions.filter(q => q.status === "Open").length;
                const vCount = viewings.filter(v => v.propId === prop.id).length;
                return (
                  <tr key={prop.id} onClick={() => onOpenProperty(prop.id)}
                    style={{ borderBottom: "1px solid var(--rule)", cursor: "pointer", opacity: prop.status === "Rejected" ? 0.55 : 1 }}
                    onMouseEnter={e => (e.currentTarget as HTMLTableRowElement).style.background = "var(--accent-soft)"}
                    onMouseLeave={e => (e.currentTarget as HTMLTableRowElement).style.background = "transparent"}>
                    <td style={{ padding: "10px 10px" }}>
                      <button
                        onClick={e => { e.stopPropagation(); setOpenDetailName(prop.name); }}
                        title={t("workspace.shortlist.viewDetails")}
                        style={{ background: "none", border: "none", padding: 0, fontFamily: "var(--font-ui)", fontSize: 13, fontWeight: 600, color: "var(--ink)", letterSpacing: "-0.01em", cursor: "pointer", textAlign: "left" }}
                        onMouseEnter={e => (e.currentTarget as HTMLButtonElement).style.color = "var(--accent)"}
                        onMouseLeave={e => (e.currentTarget as HTMLButtonElement).style.color = "var(--ink)"}
                      >{titleCaseSG(prop.name)}</button>
                      <div style={{ fontFamily: "var(--font-ui)", fontSize: 12, color: "var(--ink-3)", marginTop: 1 }}>{[prop.district, prop.tenure, prop.top > 0 ? `TOP ${prop.top}` : "", prop.mrt].filter(Boolean).join(" · ")}</div>
                    </td>
                    <td style={{ padding: "10px 10px" }}><StatusBadge status={prop.status} small /></td>
                    <td style={{ padding: "10px 10px", fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--ink)" }}>{priceRange(prop)}</td>
                    <td style={{ padding: "10px 10px" }}><FitScore score={prop.fitScore} /></td>
                    <td style={{ padding: "10px 10px", fontFamily: "var(--font-ui)", fontSize: 11.5, color: openQ > 0 ? "var(--warn)" : "var(--ink-4)" }}>{openQ > 0 ? `${openQ} open` : "—"}</td>
                    <td style={{ padding: "10px 10px", fontFamily: "var(--font-ui)", fontSize: 11.5, color: "var(--ink-3)" }}>{vCount > 0 ? vCount : "—"}</td>
                    <td style={{ padding: "10px 10px" }}><svg width="12" height="12" viewBox="0 0 14 14" fill="none"><path d="M5 3l4 4-4 4" stroke="var(--ink-4)" strokeWidth="1.5" strokeLinecap="round" /></svg></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      {openDetailName && (
        <ProjectDetailModal name={openDetailName} onClose={() => setOpenDetailName(null)} />
      )}
    </div>
  );
}

// ─── DossierView ──────────────────────────────────────────────────────────────

function DossierView({ propId, onBack }: { propId: string; onBack: () => void }) {
  const t = useWsT();
  const isMobile = useIsMobile();
  const { properties, viewings, openModal, updateQuestion, deleteUnit } = useWsData();
  const prop = properties.find(x => x.id === propId);
  const [selectedUnitId, setSelectedUnitId] = useState<string | null>(null);
  const firstUnitId = (prop?.units ?? [])[0]?.id ?? null;
  useEffect(() => {
    if (selectedUnitId == null && firstUnitId) setSelectedUnitId(firstUnitId);
  }, [firstUnitId, selectedUnitId]);
  if (!prop) return null;
  const u = (prop.units ?? []).find(x => x.id === selectedUnitId) ?? null;
  const propViewings = viewings.filter(v => v.propId === prop.id);

  // projectFacts cells are usually plain strings, but the MRT cell wants
  // a small colored line badge — so it's left at null here and rendered
  // inline below via a dedicated React node lookup (mrtCellNode).
  const projectFacts: [string, string | null | React.ReactNode][] = [
    [t("workspace.facts.project"), titleCaseSG(prop.project)], [t("workspace.facts.district"), prop.district],
    [t("workspace.facts.tenure"), prop.tenure], [t("workspace.facts.top"), prop.top > 0 ? prop.top.toString() : null],
    [t("workspace.facts.beds"), prop.beds],
    [t("workspace.facts.nearestMrt"), <NearestMRTCell key="mrt" stations={prop.nearbyMRT} fallback={prop.mrt} />],
  ];
  const unitFacts: [string, string | null | React.ReactNode][] = u ? [
    [t("workspace.facts.askingPrice"), `$${(u.price / 1e6).toFixed(2)}M`],
    [t("workspace.facts.psf"), `$${u.psf.toLocaleString()}`],
    [t("workspace.facts.size"), `${u.size} sqft`],
    [t("workspace.facts.floor"), u.floor.toString()],
    [t("workspace.facts.stack"), u.stack],
    [t("workspace.facts.facing"), u.facing ? facingLabel(t, u.facing) : u.facing],
    [t("workspace.facts.maintenance"), u.maintenance],
    [t("workspace.facts.tenancy"), u.tenancy ? tenancyLabel(t, u.tenancy) : u.tenancy],
  ] : [];
  const facts = [...projectFacts, ...unitFacts];

  const unitSelector = (prop.units ?? []).length > 0 ? (
    <div style={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
      {(prop.units ?? []).map(pu => (
        <button key={pu.id} onClick={() => setSelectedUnitId(pu.id)} style={{
          padding: "3px 9px", borderRadius: 6, border: "1px solid",
          borderColor: selectedUnitId === pu.id ? "var(--accent)" : "var(--rule)",
          background: selectedUnitId === pu.id ? "var(--accent-soft)" : "transparent",
          fontFamily: "var(--font-mono)", fontSize: 11,
          fontWeight: selectedUnitId === pu.id ? 600 : 400,
          color: selectedUnitId === pu.id ? "var(--accent)" : "var(--ink-3)", cursor: "pointer",
        }}>{pu.label}</button>
      ))}
      <button onClick={() => openModal({ type: "addUnit", propId: prop.id })} style={{ padding: "3px 7px", borderRadius: 6, border: "1px dashed var(--rule)", background: "transparent", fontFamily: "var(--font-ui)", fontSize: 11, color: "var(--ink-4)", cursor: "pointer" }}>+</button>
    </div>
  ) : null;

  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden" }}>
      {isMobile ? (
        /* Mobile header — two compact rows */
        <div style={{ flexShrink: 0, borderBottom: "1px solid var(--rule)", background: "var(--bg)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 14px 6px" }}>
            <button onClick={onBack} style={{ background: "transparent", border: "none", color: "var(--ink-3)", cursor: "pointer", display: "flex", alignItems: "center", gap: 4, fontFamily: "var(--font-ui)", fontSize: 12, flexShrink: 0 }}>
              <svg width="10" height="10" viewBox="0 0 12 12" fill="none"><path d="M8 1L3 6l5 5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" /></svg>
              {t("workspace.dossier.back")}
            </button>
            <span style={{ color: "var(--ink-4)" }}>/</span>
            <div style={{ fontFamily: "var(--font-ui)", fontSize: 13, fontWeight: 600, color: "var(--ink)", letterSpacing: "-0.01em", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{titleCaseSG(prop.name)}</div>
            {/* Per-unit status when a unit is active; the property-level
                rollup is the right read only when the property has no
                units (DossierView's noUnits fallback). Otherwise the
                badge would mislead — e.g. a Negotiating unit would show
                as the rollup's higher-precedence value if anything were
                ranked above it (nothing is, but the principle holds). */}
            <StatusBadge status={u?.status ?? prop.status} small />
          </div>
          <div style={{ padding: "0 14px 10px", display: "flex", alignItems: "center", gap: 8 }}>
            {unitSelector}
          </div>
        </div>
      ) : (
        /* Desktop header */
        <div style={{ padding: "12px 28px", borderBottom: "1px solid var(--rule)", background: "var(--bg)", display: "flex", alignItems: "center", gap: 12, flexShrink: 0 }}>
          <button onClick={onBack} style={{ background: "transparent", border: "none", color: "var(--ink-3)", cursor: "pointer", display: "flex", alignItems: "center", gap: 5, fontFamily: "var(--font-ui)", fontSize: 12 }}>
            <svg width="10" height="10" viewBox="0 0 12 12" fill="none"><path d="M8 1L3 6l5 5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" /></svg>
            {t("workspace.dossier.back")}
          </button>
          <span style={{ color: "var(--ink-4)" }}>/</span>
          <div style={{ fontFamily: "var(--font-ui)", fontSize: 14, fontWeight: 600, color: "var(--ink)", letterSpacing: "-0.01em" }}>{titleCaseSG(prop.name)}</div>
          {unitSelector}
          {/* Per-unit status when a unit is active; falls back to
              property status only for properties with zero units (where
              the rollup doesn't run anyway). */}
          <StatusBadge status={u?.status ?? prop.status} small />
          <div style={{ flex: 1 }} />
          <div style={{ display: "flex", gap: 6 }}>
            <button onClick={() => openModal({ type: "addViewing", propId: prop.id })} style={{ background: "transparent", border: "1px solid var(--rule)", borderRadius: 6, padding: "5px 11px", cursor: "pointer", fontFamily: "var(--font-ui)", fontSize: 11, color: "var(--ink-2)" }}>{t("workspace.dossier.scheduleViewingBtn")}</button>
            <button onClick={() => openModal({ type: "editProperty", propId: prop.id })} style={{ background: "transparent", border: "1px solid var(--rule)", borderRadius: 6, padding: "5px 11px", cursor: "pointer", fontFamily: "var(--font-ui)", fontSize: 11, color: "var(--ink-2)" }}>{t("workspace.dossier.editPropertyBtn")}</button>
            <button onClick={() => openModal({ type: "addViewing", propId: prop.id })} style={{ background: "var(--accent)", border: "none", borderRadius: 6, padding: "5px 13px", cursor: "pointer", fontFamily: "var(--font-ui)", fontSize: 11, color: "white", fontWeight: 500 }}>{t("workspace.dossier.recordViewingBtn")}</button>
          </div>
        </div>
      )}

      <div style={{ flex: 1, overflow: "auto", padding: isMobile ? "16px 14px" : "24px 32px", display: "flex", flexDirection: isMobile ? "column" : "row", gap: isMobile ? 16 : 28 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
            {/* AI snapshot — property level. Body is server-generated by the
                summary worker (doc/WORKSPACE_AI_SUMMARIES.md §6.1) when the
                buyer adds pros/risks/questions/livability or a unit. We
                render the existing body during a regen so the buyer isn't
                left looking at an empty box, with a small "Updating…" pill
                to signal that a fresher version is coming. */}
            <div style={{ background: "var(--ai-bg)", border: "1px solid var(--ai-rule)", borderRadius: 12, padding: "16px 18px", marginBottom: 22 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
                <AIStarIcon />
                <span style={{ fontFamily: "var(--font-ui)", fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--ai-accent)" }}>{t("workspace.dossier.aiSnapshot")}</span>
                {prop.summaryStale && <GeneratingPill label={prop.aiSummary ? t("workspace.ai.updating") : t("workspace.ai.generating")} />}
                <span style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 4, fontFamily: "var(--font-ui)", fontSize: 12, color: "var(--ink-3)" }}>{t("workspace.dossier.aiFit")}: <FitScore score={prop.fitScore} /></span>
              </div>
              {/* AI snapshot body — model emits structured markdown
                  (**Fit:** / **Concern:** / **Before viewing:** lines
                  with **bold** scan-keywords). Render through the same
                  marked+DOMPurify path as the chat surface; .markdown
                  class normalises p/ul/li margins. Existing pre-format
                  summaries still render fine — they're just prose with
                  no bolds, which marked handles transparently. */}
              {prop.aiSummary
                ? <div className="markdown" style={{ fontSize: 14, color: "var(--ink-2)", lineHeight: 1.55, marginBottom: 10 }} dangerouslySetInnerHTML={{ __html: renderMarkdown(prop.aiSummary) }} />
                : prop.summaryStale
                  ? <div style={{ fontFamily: "var(--font-serif)", fontSize: 13, fontStyle: "italic", color: "var(--ink-4)", lineHeight: 1.5, marginBottom: 10 }}>{t("workspace.ai.generatingPropertyHint")}</div>
                  : <div style={{ fontFamily: "var(--font-serif)", fontSize: 13, fontStyle: "italic", color: "var(--ink-4)", lineHeight: 1.5, marginBottom: 10 }}>{t("workspace.ai.emptyPropertyHint")}</div>
              }
              {(prop.pros.length > 0 || prop.risks.length > 0) && (
                <div style={{ display: "flex", gap: 16 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontFamily: "var(--font-ui)", fontSize: 12, fontWeight: 600, color: "var(--pos)", letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 5 }}>{t("workspace.dossier.pros")}</div>
                    {prop.pros.map((p, i) => <div key={i} style={{ display: "flex", gap: 6, marginBottom: 3 }}><span style={{ color: "var(--pos)", fontSize: 12, lineHeight: 1.6 }}>+</span><span style={{ fontFamily: "var(--font-ui)", fontSize: 12, color: "var(--ink-2)", lineHeight: 1.5 }}>{p}</span></div>)}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontFamily: "var(--font-ui)", fontSize: 12, fontWeight: 600, color: "var(--neg)", letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 5 }}>{t("workspace.dossier.risks")}</div>
                    {prop.risks.map((r, i) => <div key={i} style={{ display: "flex", gap: 6, marginBottom: 3 }}><span style={{ color: "var(--neg)", fontSize: 12, lineHeight: 1.6 }}>–</span><span style={{ fontFamily: "var(--font-ui)", fontSize: 12, color: "var(--ink-2)", lineHeight: 1.5 }}>{r}</span></div>)}
                  </div>
                </div>
              )}
            </div>

            {/* Negotiation panel — renders for any unit at Negotiating.
                When negotiation JSON is still null (e.g. just-flipped
                to Negotiating, no moves logged yet) we synthesise a
                stub `{ asking: unit.price }` client-side so the panel
                renders the Listed Asking row and a "Log move" CTA;
                first Log-move click writes the real blob server-side
                and the timeline grows from there. Without this fallback
                the panel was invisible until a blob existed, but the
                only way to create the blob was via the panel itself. */}
            {u?.status === "Negotiating" && u.price > 0 && (
              <NegotiationPanel
                offer={u.negotiation ?? { asking: u.price }}
                onLogMove={() => openModal({ type: "logMove", propId: prop.id, unitId: u.id })}
              />
            )}

            {/* Decision Status — per-unit picker + bulk-edit + the
                rolled-up explainer. The only place users learn the
                rollup precedence rule. */}
            <DecisionStatusSection prop={prop} />

            {/* Key facts */}
            <div style={{ marginBottom: 22 }}>
              <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8, marginBottom: 6 }}>
                <SectionLabel style={{ marginBottom: 0 }}>{t("workspace.dossier.keyFacts")}{u ? ` — ${u.label}` : ""}</SectionLabel>
                {/* Inline edit shortcut. Opens the property editor (district /
                    tenure / TOP / beds / nearest MRT). For unit-specific fields
                    use the unit selector's "edit unit" affordance. */}
                <button
                  onClick={() => openModal(u ? { type: "editUnit", propId: prop.id, unitId: u.id } : { type: "editProperty", propId: prop.id })}
                  style={{ background: "transparent", border: "1px solid var(--rule)", borderRadius: 6, padding: "3px 9px", cursor: "pointer", fontFamily: "var(--font-ui)", fontSize: 11, color: "var(--ink-3)" }}
                  title={t("workspace.dossier.editPropertyBtn")}
                >
                  {t("workspace.dossier.editPropertyBtn")}
                </button>
              </div>
              <div style={{ background: "var(--surface)", border: "1px solid var(--rule)", borderRadius: 10, overflow: "hidden" }}>
                {facts.map(([k, v], i) => (
                  <div key={k} style={{ display: "flex", padding: "9px 16px", borderBottom: i < facts.length - 1 ? "1px solid var(--rule)" : "none", background: i % 2 === 0 ? "transparent" : "rgba(0,0,0,0.012)" }}>
                    <div style={{ width: 160, fontFamily: "var(--font-ui)", fontSize: 11.5, color: "var(--ink-3)", flexShrink: 0 }}>{k}</div>
                    <div style={{ fontFamily: "var(--font-ui)", fontSize: 12, color: "var(--ink)", fontWeight: 500 }}>{v === null || v === "—" ? <UnknownPill /> : v}</div>
                  </div>
                ))}
              </div>
              {!u && (prop.units ?? []).length > 0 && (
                <div style={{ marginTop: 8, fontFamily: "var(--font-serif)", fontStyle: "italic", fontSize: 12, color: "var(--ink-4)" }}>Select a known unit above to see unit-specific price and details.</div>
              )}
            </div>

            {/* Questions — property level */}
            <div style={{ marginBottom: 22 }}>
              <SectionLabel>{t("workspace.dossier.openQuestions")}</SectionLabel>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {prop.questions.map((q, i) => {
                  const CYCLE: Record<string, string> = { Open: "Asked", Asked: "Answered", Answered: "Open" };
                  const qColor = q.status === "Answered" ? "var(--pos)" : q.status === "Asked" ? "var(--warn)" : "var(--ink-3)";
                  return (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, background: "var(--surface)", border: "1px solid var(--rule)", borderRadius: 8, padding: "9px 13px" }}>
                      <div style={{ width: 6, height: 6, borderRadius: 6, background: qColor, flexShrink: 0 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontFamily: "var(--font-ui)", fontSize: 12.5, color: "var(--ink)", lineHeight: 1.4 }}>{q.q}</div>
                        {q.units && q.units.length > 0 && <div style={{ fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--ink-4)", marginTop: 1 }}>re: {q.units.join(", ")}</div>}
                      </div>
                      <button onClick={() => {
                          const next = CYCLE[q.status] ?? "Open";
                          if (next === "Answered") openModal({ type: "editQuestion", propId: prop.id, qIdx: i, prefillStatus: "Answered" });
                          else updateQuestion(prop.id, i, { status: next });
                        }}
                        title="Cycle status"
                        style={{ background: "transparent", border: "none", cursor: "pointer", fontFamily: "var(--font-ui)", fontSize: 12, fontWeight: 500, color: qColor, letterSpacing: "0.05em", flexShrink: 0, padding: "2px 6px", borderRadius: 4 }}
                        onMouseEnter={e => (e.currentTarget as HTMLButtonElement).style.background = "var(--surface-2)"}
                        onMouseLeave={e => (e.currentTarget as HTMLButtonElement).style.background = "transparent"}>
                        {qStatusLabel(t, q.status)}
                      </button>
                      <button onClick={() => openModal({ type: "editQuestion", propId: prop.id, qIdx: i })}
                        style={{ background: "transparent", border: "none", cursor: "pointer", color: "var(--ink-4)", padding: 2 }}>
                        <svg width="11" height="11" viewBox="0 0 14 14" fill="none"><path d="M10 2l2 2-8 8H2v-2L10 2z" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" /></svg>
                      </button>
                    </div>
                  );
                })}
                {prop.questions.length === 0 && <div style={{ fontFamily: "var(--font-serif)", fontStyle: "italic", fontSize: 13, color: "var(--ink-3)", padding: 4 }}>{t("workspace.dossier.noOpenQuestions")}</div>}
                <button onClick={() => openModal({ type: "addQuestion", propId: prop.id })} style={{ background: "transparent", border: "1px dashed var(--rule)", borderRadius: 8, padding: "8px 13px", cursor: "pointer", fontFamily: "var(--font-ui)", fontSize: 12, color: "var(--ink-3)", display: "flex", alignItems: "center", gap: 6 }}>
                  <svg width="10" height="10" viewBox="0 0 12 12" fill="none"><path d="M6 1v10M1 6h10" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" /></svg>
                  {t("workspace.dossier.addQuestion")}
                </button>
              </div>
            </div>

            {/* Viewings for this property */}
            <div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                <SectionLabel>{t("workspace.dossier.viewings", { count: propViewings.length })}</SectionLabel>
                <button onClick={() => openModal({ type: "addViewing", propId: prop.id })} style={{ background: "transparent", border: "1px solid var(--rule)", borderRadius: 6, padding: "4px 10px", cursor: "pointer", fontFamily: "var(--font-ui)", fontSize: 11, color: "var(--ink-2)" }}>{t("workspace.dossier.schedule")}</button>
              </div>
              {propViewings.length === 0 ? (
                <div style={{ border: "1px dashed var(--rule)", borderRadius: 10, padding: 16, fontFamily: "var(--font-serif)", fontStyle: "italic", fontSize: 13, color: "var(--ink-4)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <span>{t("workspace.viewings.noneForProp", { name: titleCaseSG(prop.name) })}</span>
                  <button onClick={() => openModal({ type: "addViewing", propId: prop.id })} style={{ background: "var(--accent)", border: "none", borderRadius: 6, padding: "5px 12px", cursor: "pointer", fontFamily: "var(--font-ui)", fontSize: 11, color: "white", fontWeight: 500, fontStyle: "normal" }}>{t("workspace.dossier.scheduleViewing")}</button>
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {propViewings.map(v => (
                    <div key={v.id} style={{ background: "var(--surface)", border: "1px solid var(--rule)", borderRadius: 10, padding: "12px 14px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontFamily: "var(--font-ui)", fontSize: 12.5, fontWeight: 500, color: "var(--ink)" }}>{fmtDate(v.date)} · {v.time}{v.unitLabel ? ` · ${v.unitLabel}` : ""}</div>
                          <div style={{ fontFamily: "var(--font-ui)", fontSize: 11, color: "var(--ink-3)", marginTop: 1 }}>{t("workspace.viewings.agent")}: {v.agent}</div>
                        </div>
                        {v.status === "scheduled" ? <StatusBadge status="Viewing Scheduled" small /> : <StatusBadge status="Viewed" small />}
                        <button onClick={() => openModal({ type: "recordNotes", viewingId: v.id })}
                          style={{ background: v.status === "scheduled" ? "var(--accent)" : "transparent", border: v.status === "scheduled" ? "none" : "1px solid var(--rule)", borderRadius: 6, padding: "4px 10px", cursor: "pointer", fontFamily: "var(--font-ui)", fontSize: 11, color: v.status === "scheduled" ? "white" : "var(--ink-2)", fontWeight: v.status === "scheduled" ? 500 : 400, whiteSpace: "nowrap" }}>
                          {v.status === "scheduled" ? t("workspace.viewings.recordNotesBtn") : t("workspace.viewings.editNotes")}
                        </button>
                      </div>
                      {v.status === "completed" && v.ratings && (
                        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: v.aiSummary ? 8 : 0 }}>
                          {([[t("workspace.dossier.layout"), v.ratings.layout], [t("workspace.dossier.light"), v.ratings.light], [t("workspace.dossier.noise"), v.ratings.noise], [t("workspace.dossier.condition"), v.ratings.condition]] as [string, number | null][]).map(([k, val]) => (
                            <div key={k} style={{ display: "flex", alignItems: "center", gap: 5 }}><span style={{ fontFamily: "var(--font-ui)", fontSize: 12, color: "var(--ink-4)" }}>{k}</span><RatingDots value={val} /></div>
                          ))}
                        </div>
                      )}
                      {v.status === "completed" && v.media && v.media.length > 0 && (
                        <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginBottom: 8 }}>
                          {v.media.slice(0, 4).map((m, mi) => (
                            <div key={mi} style={{ width: 48, height: 36, borderRadius: 5, background: m.thumb, border: "1px solid var(--rule)", position: "relative", overflow: "hidden", cursor: "pointer", flexShrink: 0 }}>
                              {m.type === "video" && <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,0.25)" }}><svg width="8" height="9" viewBox="0 0 10 12" fill="none"><path d="M1 1l8 5-8 5V1z" fill="white" /></svg></div>}
                              {mi === 3 && v.media && v.media.length > 4 && <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center" }}><span style={{ fontFamily: "var(--font-ui)", fontSize: 12, fontWeight: 600, color: "white" }}>+{v.media.length - 4}</span></div>}
                            </div>
                          ))}
                        </div>
                      )}
                      {v.status === "completed" && v.aiSummary && (
                        <div style={{ background: "var(--ai-bg)", border: "1px solid var(--ai-rule)", borderRadius: 7, padding: "8px 11px" }}>
                          {/* Viewing summary is structured markdown (bold section
                              labels + bullets), same as the property snapshot and
                              the standalone viewing card — route it through
                              renderMarkdown so the bold/bullet markers don't leak
                              literally (PROP-143). */}
                          <div className="markdown" style={{ fontFamily: "var(--font-serif)", fontSize: 12, fontStyle: "italic", color: "var(--ink-2)", lineHeight: 1.5 }} dangerouslySetInnerHTML={{ __html: renderMarkdown(v.aiSummary) }} />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right sidebar — full-width on mobile, fixed 240px on desktop */}
          <div style={{ width: isMobile ? "100%" : 240, flexShrink: 0 }}>
            <SectionLabel>{t("workspace.dossier.knownUnitsLabel")}</SectionLabel>
            <div style={{ background: "var(--surface)", border: "1px solid var(--rule)", borderRadius: 10, overflow: "hidden", marginBottom: 14 }}>
              {(prop.units ?? []).length === 0 ? (
                <div style={{ padding: "12px 14px", fontFamily: "var(--font-serif)", fontSize: 12, fontStyle: "italic", color: "var(--ink-4)", lineHeight: 1.5 }}>{t("workspace.dossier.noUnitsKnown")}</div>
              ) : (
                (prop.units ?? []).map((pu, i) => (
                  <div key={pu.id} onClick={() => setSelectedUnitId(pu.id)}
                    style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%", background: selectedUnitId === pu.id ? "var(--accent-soft)" : "transparent", borderBottom: i < (prop.units ?? []).length - 1 ? "1px solid var(--rule)" : "none", padding: "9px 13px", cursor: "pointer", textAlign: "left", gap: 8 }}>
                    <span style={{ fontFamily: "var(--font-mono)", fontSize: 11.5, color: selectedUnitId === pu.id ? "var(--accent)" : "var(--ink)", fontWeight: 500 }}>{pu.label}</span>
                    <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--ink-3)", marginLeft: "auto" }}>${(pu.price / 1e6).toFixed(2)}M</span>
                    <button
                      onClick={e => { e.stopPropagation(); openModal({ type: "editUnit", propId: prop.id, unitId: pu.id }); }}
                      title="Edit unit"
                      style={{ background: "transparent", border: "none", cursor: "pointer", color: "var(--ink-4)", padding: 2, display: "flex", alignItems: "center" }}
                      onMouseEnter={e => (e.currentTarget as HTMLButtonElement).style.color = "var(--accent)"}
                      onMouseLeave={e => (e.currentTarget as HTMLButtonElement).style.color = "var(--ink-4)"}>
                      <svg width="11" height="11" viewBox="0 0 14 14" fill="none"><path d="M10 2l2 2-8 8H2v-2L10 2z" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" /></svg>
                    </button>
                    <button
                      onClick={e => { e.stopPropagation(); if (window.confirm(`Delete known unit ${pu.label}?`)) { deleteUnit(prop.id, pu.id); if (selectedUnitId === pu.id) setSelectedUnitId(null); } }}
                      title="Delete unit"
                      style={{ background: "transparent", border: "none", cursor: "pointer", color: "var(--ink-4)", padding: 2, display: "flex", alignItems: "center" }}
                      onMouseEnter={e => (e.currentTarget as HTMLButtonElement).style.color = "var(--neg)"}
                      onMouseLeave={e => (e.currentTarget as HTMLButtonElement).style.color = "var(--ink-4)"}>
                      <svg width="11" height="11" viewBox="0 0 12 12" fill="none"><path d="M1 1l10 10M11 1L1 11" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" /></svg>
                    </button>
                  </div>
                ))
              )}
              <button onClick={() => openModal({ type: "addUnit", propId: prop.id })} style={{ display: "flex", alignItems: "center", gap: 6, width: "100%", background: "transparent", border: "none", borderTop: (prop.units ?? []).length > 0 ? "1px solid var(--rule)" : "none", padding: "9px 13px", cursor: "pointer", fontFamily: "var(--font-ui)", fontSize: 11, color: "var(--ink-4)" }}>
                <svg width="10" height="10" viewBox="0 0 12 12" fill="none"><path d="M6 1v10M1 6h10" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" /></svg>
                {t("workspace.dossier.addUnit")}
              </button>
            </div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
              <SectionLabel style={{ marginBottom: 0 }}>{t("workspace.dossier.livabilitySignals")}</SectionLabel>
              <button onClick={() => openModal({ type: "editLivability", propId: prop.id })} style={{ background: "transparent", border: "none", cursor: "pointer", color: "var(--ink-4)", padding: 2, display: "flex" }}>
                <svg width="11" height="11" viewBox="0 0 14 14" fill="none"><path d="M10 2l2 2-8 8H2v-2L10 2z" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" /></svg>
              </button>
            </div>
            <div style={{ background: "var(--surface)", border: "1px solid var(--rule)", borderRadius: 10, overflow: "hidden" }}>
              {([
                [t("workspace.dossier.naturalLight"), prop.livability?.naturalLight],
                [t("workspace.dossier.noise"), prop.livability?.noise],
                [t("workspace.dossier.layout"), prop.livability?.layout],
                [t("workspace.dossier.wfh"), prop.livability?.wfh],
              ] as [string, string | undefined][]).map(([k, v], i) => (
                <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "8px 13px", borderBottom: i < 3 ? "1px solid var(--rule)" : "none" }}>
                  <div style={{ fontFamily: "var(--font-ui)", fontSize: 11.5, color: "var(--ink-3)" }}>{k}</div>
                  <div style={{ fontFamily: "var(--font-ui)", fontSize: 11.5, fontWeight: 500, color: v ? "var(--pos)" : "var(--ink-4)" }}>{v ?? <UnknownPill />}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
    </div>
  );
}

// ─── CompareTab ───────────────────────────────────────────────────────────────
// Compare is unit-level: each slot picks a unit (from any property). The table
// rows pull real values out of the chosen (property, unit) pair — no mocked
// scoring — and a property's units appear grouped together in the picker so
// users can compare units within the same project as well as across projects.

type CompareDim = {
  id: string;
  labelKey: string; // i18n key under workspace.facts.*
  // Renderer returns either plain text or a ReactNode (for badges).
  // `t` is threaded in so enum dims (facing / tenancy) can localise their
  // value via the shared label helpers (PROP-216); dims that don't need it
  // simply omit the parameter.
  render: (prop: Property, u: Unit, t: TFn) => React.ReactNode;
};

type SlotRef = { propId: string; unitId: string };

const EMPTY_SLOT: SlotRef = { propId: "", unitId: "" };

const fmtMoneyM = (n: number) => n > 0 ? `$${(n / 1e6).toFixed(2)}M` : "—";
const fmtPsf    = (n: number) => n > 0 ? `$${n.toLocaleString()}` : "—";
const fmtSize   = (n: number) => n > 0 ? `${n} sqft` : "—";
const fmtNum    = (n: number) => n > 0 ? String(n) : "—";
const fmtText   = (s?: string | null) => (s && s !== "—" ? s : "—");
const dimEmpty  = <span style={{ fontFamily: "var(--font-ui)", fontSize: 11, color: "var(--ink-4)", fontStyle: "italic" }}>—</span>;

const COMPARE_DIMS: CompareDim[] = [
  { id: "project",       labelKey: "project",       render: (p) => fmtText(p.project) },
  { id: "district",      labelKey: "district",      render: (p) => fmtText(p.district) },
  { id: "tenure",        labelKey: "tenure",        render: (p) => fmtText(p.tenure) },
  { id: "top",           labelKey: "top",           render: (p) => fmtNum(p.top) },
  { id: "beds",          labelKey: "beds",          render: (p) => fmtText(p.beds) },
  { id: "nearestMrt",    labelKey: "nearestMrt",    render: (p) => fmtText(p.mrt) },
  { id: "floor",         labelKey: "floor",         render: (_p, u) => fmtNum(u.floor) },
  { id: "stack",         labelKey: "stack",         render: (_p, u) => fmtText(u.stack) },
  { id: "facing",        labelKey: "facing",        render: (_p, u, t) => fmtText(u.facing ? facingLabel(t, u.facing) : u.facing) },
  { id: "size",          labelKey: "size",          render: (_p, u) => fmtSize(u.size) },
  { id: "askingPrice",   labelKey: "askingPrice",   render: (_p, u) => fmtMoneyM(u.price) },
  { id: "psf",           labelKey: "psf",           render: (_p, u) => fmtPsf(u.psf) },
  { id: "maintenance",   labelKey: "maintenance",   render: (_p, u) => fmtText(u.maintenance) },
  { id: "tenancy",       labelKey: "tenancy",       render: (_p, u, t) => fmtText(u.tenancy ? tenancyLabel(t, u.tenancy) : u.tenancy) },
  { id: "status",        labelKey: "status",        render: (_p, u) => <StatusBadge status={u.status || "New"} small /> },
  { id: "fitScore",      labelKey: "fitScore",      render: (p) => <FitScore score={p.fitScore} /> },
  { id: "openQuestions", labelKey: "openQuestions", render: (p) => {
    const n = p.questions.filter(q => q.status === "Open").length;
    return n > 0 ? <span style={{ fontFamily: "var(--font-ui)", fontSize: 11.5, color: "var(--warn)", fontWeight: 500 }}>{n}</span> : <span style={{ fontFamily: "var(--font-ui)", fontSize: 11.5, color: "var(--ink-4)" }}>0</span>;
  } },
];

function CompareTab() {
  const t = useWsT();
  const isMobile = useIsMobile();
  const { properties, compareSummary, compareUnitIds, setCompareUnitIds } = useWsData();
  const [slots, setSlots] = useState<SlotRef[]>([EMPTY_SLOT, EMPTY_SLOT, EMPTY_SLOT]);
  const [openSelector, setOpenSelector] = useState<number | null>(null);

  // Seed slots from the workspace's persisted compare_unit_ids on mount
  // (and whenever properties load — we may not have them at first render
  // because bootstrap is async). Resolves each unit_id to its parent
  // property so the existing slots[].propId/unitId shape stays intact.
  // Effect intentionally one-way: server → slots only when the user
  // hasn't already touched them this session, otherwise typing into the
  // picker would fight with the server state.
  const slotsTouchedRef = useRef(false);
  useEffect(() => {
    if (slotsTouchedRef.current) return;
    if (compareUnitIds.length === 0 || properties.length === 0) return;
    const next: SlotRef[] = [EMPTY_SLOT, EMPTY_SLOT, EMPTY_SLOT];
    for (let i = 0; i < compareUnitIds.length && i < 3; i++) {
      const uid = compareUnitIds[i];
      const prop = properties.find(p => (p.units ?? []).some(u => u.id === uid));
      if (prop) next[i] = { propId: prop.id, unitId: uid };
    }
    setSlots(next);
  }, [compareUnitIds, properties]);

  // Close the picker when the user clicks anywhere outside the slot wrapper
  // that owns the open dropdown. The slot wrappers are tagged with
  // data-compare-picker-root so we can identify "inside" via closest().
  useEffect(() => {
    if (openSelector === null) return;
    const onDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && target.closest("[data-compare-picker-root]")) return;
      setOpenSelector(null);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [openSelector]);

  const setSlot = (i: number, ref: SlotRef) => {
    const n = [...slots]; n[i] = ref;
    setSlots(n); setOpenSelector(null);
    slotsTouchedRef.current = true;
    // Push the filled-in unit IDs to the workspace so the summary
    // worker has the same selection to feed the LLM. setCompareUnitIds
    // is a no-op if the set hasn't actually changed.
    const ids = n.map(s => s.unitId).filter(Boolean);
    setCompareUnitIds(ids);
  };

  const resolved: ({ slot: SlotRef; prop: Property; unit: Unit } | null)[] = slots.map(s => {
    if (!s.propId || !s.unitId) return null;
    const prop = properties.find(p => p.id === s.propId);
    const unit = prop?.units?.find(u => u.id === s.unitId);
    return prop && unit ? { slot: s, prop, unit } : null;
  });
  const filledCount = resolved.filter(Boolean).length;

  const propsWithUnits = properties.filter(p => (p.units ?? []).length > 0);
  const noUnitsAtAll = propsWithUnits.length === 0;

  // Unit picker — units grouped by their property. A unit already chosen in
  // another slot is rendered disabled so the same unit can't be compared
  // against itself across columns.
  const renderPicker = (slotIdx: number, current: SlotRef) => {
    const takenElsewhere = new Set<string>();
    slots.forEach((s, i) => {
      if (i !== slotIdx && s.propId && s.unitId) takenElsewhere.add(`${s.propId}::${s.unitId}`);
    });
    return (
      <div style={{ position: "absolute", top: "100%", left: 0, zIndex: 30, background: "var(--surface)", border: "1px solid var(--rule)", borderRadius: 8, boxShadow: "0 8px 24px rgba(0,0,0,0.15)", padding: 6, minWidth: 240, maxHeight: 320, overflow: "auto", marginTop: 4 }}>
        {noUnitsAtAll ? (
          <div style={{ padding: "10px 8px", fontFamily: "var(--font-serif)", fontSize: 12, fontStyle: "italic", color: "var(--ink-4)", lineHeight: 1.5 }}>
            {t("workspace.compare.noUnitsToCompare")}
          </div>
        ) : propsWithUnits.map(prop => (
          <div key={prop.id} style={{ marginBottom: 4 }}>
            <div style={{ padding: "5px 8px 3px", fontFamily: "var(--font-ui)", fontSize: 10.5, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--ink-4)" }}>{prop.name}</div>
            {(prop.units ?? []).map(u => {
              const isCurrent = current.propId === prop.id && current.unitId === u.id;
              const isTaken = takenElsewhere.has(`${prop.id}::${u.id}`);
              return (
                <button key={u.id}
                  disabled={isTaken}
                  onClick={() => { if (!isTaken) setSlot(slotIdx, { propId: prop.id, unitId: u.id }); }}
                  title={isTaken ? "Already in another column" : undefined}
                  style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", background: isCurrent ? "var(--accent-soft)" : "transparent", border: "none", borderRadius: 5, padding: "6px 8px", cursor: isTaken ? "not-allowed" : "pointer", textAlign: "left", opacity: isTaken ? 0.45 : 1 }}>
                  <span style={{ fontFamily: "var(--font-mono)", fontSize: 11.5, fontWeight: 600, color: isCurrent ? "var(--accent)" : "var(--ink)" }}>{u.label}</span>
                  <span style={{ fontFamily: "var(--font-mono)", fontSize: 10.5, color: "var(--ink-3)", marginLeft: "auto" }}>{u.price > 0 ? `$${(u.price / 1e6).toFixed(2)}M` : ""}</span>
                  {isTaken
                    ? <span style={{ fontFamily: "var(--font-ui)", fontSize: 10, color: "var(--ink-4)", fontStyle: "italic" }}>in use</span>
                    : <StatusBadge status={u.status || "New"} small />}
                </button>
              );
            })}
          </div>
        ))}
      </div>
    );
  };

  // Compare AI summary — server-generated by the summary worker
  // (doc/WORKSPACE_AI_SUMMARIES.md §6.3) keyed off the workspace's
  // persisted compare_unit_ids selection. setCompareUnitIds (in
  // useWsDataState) marks the local compareSummary stale immediately so
  // the pill renders and the stale-poll fetches the regenerated body.
  //
  // Two real states for the hint when no body exists yet:
  //   - filledCount < 2: tell the user to pick units.
  //   - filledCount >= 2 (always implies stale post-fix): generating.
  // There's no "had 2 units, not stale, no body" steady state to handle.
  const cmp = compareSummary;
  const cmpHasBody = !!cmp?.body;
  const cmpStale = !!cmp?.stale;
  const cmpHint = filledCount < 2
    ? t("workspace.compare.selectUnits")
    : t("workspace.ai.generatingCompareHint");
  const aiSummary = (
    <div style={{ background: "var(--ai-bg)", border: "1px solid var(--ai-rule)", borderRadius: 12, padding: isMobile ? "12px 14px" : "16px 20px", marginBottom: isMobile ? 16 : 24 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
        <AIStarIcon />
        <span style={{ fontFamily: "var(--font-ui)", fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--ai-accent)" }}>{t("workspace.compare.aiSummary")}</span>
        {cmpStale && filledCount >= 2 && <GeneratingPill label={cmpHasBody ? t("workspace.ai.updating") : t("workspace.ai.generating")} />}
      </div>
      {/* Compare body — model emits one bolded "**Project Unit** — …"
          line per candidate plus a "**Next:** …" closer. Marked turns
          the line-break-only structure into <br> via the global
          breaks:true setting; the .markdown class handles p/li resets. */}
      {cmpHasBody
        ? <div className="markdown" style={{ fontSize: isMobile ? 13 : 14, color: "var(--ink-2)", lineHeight: 1.6 }} dangerouslySetInnerHTML={{ __html: renderMarkdown(cmp!.body) }} />
        : <div style={{ fontFamily: "var(--font-serif)", fontSize: isMobile ? 12.5 : 13, fontStyle: "italic", color: "var(--ink-4)", lineHeight: 1.55 }}>{cmpHint}</div>}
    </div>
  );

  // Picker / change-unit button shared between layouts
  const slotPickerButton = (i: number, has: boolean, compact: boolean) => (
    <button onClick={() => setOpenSelector(openSelector === i ? null : i)}
      style={{ marginTop: compact ? 4 : 5, background: "transparent", border: "1px solid var(--rule)", borderRadius: compact ? 4 : 5, padding: compact ? "2px 6px" : "3px 8px", cursor: "pointer", fontFamily: "var(--font-ui)", fontSize: compact ? 11 : 12, color: "var(--ink-3)", display: "flex", alignItems: "center", gap: 4 }}>
      {has ? t("workspace.compare.changeUnit") : t("workspace.compare.pickUnit")}
      <svg width={compact ? "7" : "8"} height={compact ? "7" : "8"} viewBox="0 0 10 10" fill="none"><path d="M2 3l3 4 3-4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" /></svg>
    </button>
  );

  // ── Mobile layout ────────────────────────────────────────────────────────────
  if (isMobile) {
    const LABEL_W = 110;
    const COL_W = 140;
    return (
      <div style={{ flex: 1, overflow: "auto", padding: "16px 0" }}>
        <div style={{ padding: "0 14px" }}>{aiSummary}</div>
        <div style={{ overflowX: "auto", overflowY: "visible" }}>
          <div style={{ minWidth: LABEL_W + COL_W * slots.length, display: "flex", flexDirection: "column" }}>

            <div style={{ display: "flex", borderBottom: "2px solid var(--rule)", background: "var(--surface)", position: "sticky", top: 0, zIndex: 10 }}>
              <div style={{ width: LABEL_W, flexShrink: 0, padding: "10px 10px", background: "var(--surface)", borderRight: "1px solid var(--rule)" }} />
              {slots.map((slot, i) => {
                const r = resolved[i];
                return (
                  <div key={i} data-compare-picker-root style={{ width: COL_W, flexShrink: 0, padding: "10px 10px", borderRight: i < slots.length - 1 ? "1px solid var(--rule)" : "none", position: "relative" }}>
                    <div style={{ fontFamily: "var(--font-ui)", fontSize: 11, fontWeight: 600, color: "var(--ink)", lineHeight: 1.3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {r ? titleCaseSG(r.prop.name) : "—"}
                    </div>
                    {r && (
                      <div style={{ fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--ink-3)", marginTop: 1 }}>
                        {r.unit.label} · {r.prop.district}
                      </div>
                    )}
                    {slotPickerButton(i, !!r, true)}
                    {openSelector === i && renderPicker(i, slot)}
                  </div>
                );
              })}
            </div>

            {COMPARE_DIMS.map((dim, i) => (
              <div key={dim.id} style={{ display: "flex", borderBottom: "1px solid var(--rule)", background: i % 2 === 0 ? "transparent" : "rgba(0,0,0,0.012)" }}>
                <div style={{ width: LABEL_W, flexShrink: 0, padding: "10px 10px", fontFamily: "var(--font-ui)", fontSize: 11, color: "var(--ink-2)", lineHeight: 1.35, background: i % 2 === 0 ? "var(--surface)" : "var(--surface-2)", borderRight: "1px solid var(--rule)", position: "sticky", left: 0, zIndex: 5 }}>
                  {t(`workspace.facts.${dim.labelKey}`)}
                </div>
                {resolved.map((r, j) => (
                  <div key={j} style={{ width: COL_W, flexShrink: 0, padding: "10px 10px", borderRight: j < slots.length - 1 ? "1px solid var(--rule)" : "none", display: "flex", alignItems: "center", fontFamily: "var(--font-ui)", fontSize: 11.5, color: "var(--ink)" }}>
                    {r ? dim.render(r.prop, r.unit, t) : dimEmpty}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ── Desktop layout ───────────────────────────────────────────────────────────
  return (
    <div style={{ flex: 1, overflow: "auto", padding: "24px 32px" }}>
      {aiSummary}
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr style={{ borderBottom: "2px solid var(--rule)" }}>
            <th style={{ fontFamily: "var(--font-ui)", fontSize: 12, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--ink-4)", padding: "8px 12px", textAlign: "left", width: 200 }}>{t("workspace.compare.dimension")}</th>
            {slots.map((slot, i) => {
              const r = resolved[i];
              return (
                <th key={i} style={{ padding: "8px 16px", textAlign: "left", position: "relative", verticalAlign: "top" }}>
                  <div style={{ fontFamily: "var(--font-ui)", fontSize: 12, fontWeight: 600, color: "var(--ink)" }}>{r ? titleCaseSG(r.prop.name) : "—"}</div>
                  {r && <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--ink-3)", marginTop: 1 }}>{r.unit.label} · {r.prop.district}</div>}
                  <div data-compare-picker-root style={{ position: "relative", marginTop: 5 }}>
                    {slotPickerButton(i, !!r, false)}
                    {openSelector === i && renderPicker(i, slot)}
                  </div>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {COMPARE_DIMS.map((dim, i) => (
            <tr key={dim.id} style={{ borderBottom: "1px solid var(--rule)", background: i % 2 === 0 ? "transparent" : "rgba(0,0,0,0.012)" }}>
              <td style={{ padding: "10px 12px", fontFamily: "var(--font-ui)", fontSize: 12, color: "var(--ink-2)" }}>{t(`workspace.facts.${dim.labelKey}`)}</td>
              {resolved.map((r, j) => (
                <td key={j} style={{ padding: "10px 16px", fontFamily: "var(--font-ui)", fontSize: 12.5, color: "var(--ink)" }}>
                  {r ? dim.render(r.prop, r.unit, t) : dimEmpty}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── MediaGrid ────────────────────────────────────────────────────────────────

function MediaGrid({ media }: { media: MediaItem[] }) {
  const t = useWsT();
  const [lightbox, setLightbox] = useState<MediaItem | null>(null);
  const preview = media.slice(0, 5);
  const overflow = media.length - 5;
  return (
    <>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 6, marginBottom: 12 }}>
        {preview.map((m, i) => (
          <div key={i} onClick={() => setLightbox(m)} style={{ position: "relative", borderRadius: 8, overflow: "hidden", aspectRatio: "4/3", background: m.thumb, cursor: "pointer", border: "1px solid var(--rule)" }}>
            <div style={{ position: "absolute", inset: 0, background: `linear-gradient(135deg, ${m.thumb}cc, ${m.thumb}88)` }} />
            {m.type === "video" && (
              <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 4 }}>
                <div style={{ width: 28, height: 28, borderRadius: 28, background: "rgba(0,0,0,0.45)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <svg width="10" height="12" viewBox="0 0 10 12" fill="none"><path d="M1 1l8 5-8 5V1z" fill="white" /></svg>
                </div>
                {m.duration && <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "white", background: "rgba(0,0,0,0.4)", padding: "1px 5px", borderRadius: 3 }}>{m.duration}</span>}
              </div>
            )}
            {m.type === "photo" && (
              <div style={{ position: "absolute", top: 5, right: 5 }}>
                <svg width="12" height="12" viewBox="0 0 14 14" fill="none"><rect x="1" y="3" width="12" height="9" rx="1.5" stroke="rgba(0,0,0,0.4)" strokeWidth="1.2" fill="rgba(255,255,255,0.5)" /><circle cx="7" cy="7.5" r="2" stroke="rgba(0,0,0,0.35)" strokeWidth="1.2" /></svg>
              </div>
            )}
            <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, padding: "14px 6px 5px", background: "linear-gradient(to top, rgba(0,0,0,0.45), transparent)" }}>
              <div style={{ fontFamily: "var(--font-ui)", fontSize: 11, color: "white", fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.label}</div>
            </div>
            {i === 4 && overflow > 0 && (
              <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <span style={{ fontFamily: "var(--font-ui)", fontSize: 14, fontWeight: 600, color: "white" }}>+{overflow}</span>
              </div>
            )}
          </div>
        ))}
      </div>
      <button style={{ display: "flex", alignItems: "center", gap: 6, background: "transparent", border: "1px dashed var(--rule)", borderRadius: 7, padding: "7px 12px", cursor: "pointer", fontFamily: "var(--font-ui)", fontSize: 11.5, color: "var(--ink-3)", marginBottom: 12, width: "100%", justifyContent: "center" }}>
        <svg width="10" height="10" viewBox="0 0 12 12" fill="none"><path d="M6 1v10M1 6h10" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" /></svg>
        {t("workspace.media.addMedia")}
      </button>
      {lightbox && (
        <div onClick={() => setLightbox(null)} style={{ position: "fixed", inset: 0, zIndex: 200, background: "rgba(0,0,0,0.75)", backdropFilter: "blur(6px)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 12 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: lightbox.thumb, borderRadius: 14, overflow: "hidden", width: 520, aspectRatio: "4/3", position: "relative", boxShadow: "0 24px 80px rgba(0,0,0,0.5)" }}>
            <div style={{ position: "absolute", inset: 0, background: `linear-gradient(135deg, ${lightbox.thumb}cc, ${lightbox.thumb}88)`, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 10 }}>
              {lightbox.type === "video"
                ? <><div style={{ width: 56, height: 56, borderRadius: 56, background: "rgba(0,0,0,0.4)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center" }}><svg width="20" height="24" viewBox="0 0 10 12" fill="none"><path d="M1 1l8 5-8 5V1z" fill="white" /></svg></div><span style={{ fontFamily: "var(--font-mono)", fontSize: 12, color: "white", background: "rgba(0,0,0,0.35)", padding: "2px 8px", borderRadius: 4 }}>{lightbox.duration}</span></>
                : <svg width="40" height="40" viewBox="0 0 40 40" fill="none" style={{ opacity: 0.3 }}><rect x="2" y="8" width="36" height="26" rx="4" stroke="white" strokeWidth="2.5" /><circle cx="20" cy="21" r="7" stroke="white" strokeWidth="2.5" /></svg>
              }
            </div>
            <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, padding: "32px 16px 14px", background: "linear-gradient(to top, rgba(0,0,0,0.5), transparent)" }}>
              <div style={{ fontFamily: "var(--font-ui)", fontSize: 13, color: "white", fontWeight: 500 }}>{lightbox.label}</div>
            </div>
          </div>
          <div style={{ fontFamily: "var(--font-ui)", fontSize: 11.5, color: "rgba(255,255,255,0.5)" }}>{t("workspace.media.clickToClose")}</div>
        </div>
      )}
    </>
  );
}

// ─── ViewingCard ──────────────────────────────────────────────────────────────

function ViewingCard({ viewing, prop, onOpenProperty, expanded, onToggleExpand }: {
  viewing: Viewing; prop: Property;
  onOpenProperty: (propId: string) => void;
  expanded: boolean; onToggleExpand: () => void;
}) {
  const t = useWsT();
  const { openModal } = useWsData();
  const isScheduled = viewing.status === "scheduled";
  const completedCount = isScheduled && viewing.checklist ? viewing.checklist.filter(c => c.done).length : 0;
  const checklistLen = viewing.checklist?.length ?? 0;

  return (
    <div style={{ background: "var(--surface)", border: "1px solid var(--rule)", borderRadius: 12, overflow: "hidden" }}>
      <div style={{ padding: "13px 16px", display: "flex", alignItems: "flex-start", gap: 12, borderBottom: expanded ? "1px solid var(--rule)" : "none", cursor: "pointer" }} onClick={onToggleExpand}>
        <div style={{ width: 44, flexShrink: 0, textAlign: "center", background: isScheduled ? "var(--accent-soft)" : "var(--surface-2)", borderRadius: 8, padding: "6px 4px" }}>
          <div style={{ fontFamily: "var(--font-ui)", fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: isScheduled ? "var(--accent)" : "var(--ink-3)" }}>{viewingDateParts(viewing.date).month}</div>
          <div style={{ fontFamily: "var(--font-mono)", fontSize: 16, fontWeight: 600, color: isScheduled ? "var(--accent-ink)" : "var(--ink)", lineHeight: 1 }}>{viewingDateParts(viewing.date).day}</div>
          <div style={{ fontFamily: "var(--font-ui)", fontSize: 11, color: isScheduled ? "var(--accent)" : "var(--ink-3)", marginTop: 1 }}>{viewing.time.replace(":00", "").toLowerCase()}</div>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 3, flexWrap: "wrap" }}>
            <button onClick={e => { e.stopPropagation(); onOpenProperty(prop.id); }} style={{ fontFamily: "var(--font-ui)", fontSize: 13.5, fontWeight: 600, color: "var(--ink)", background: "none", border: "none", padding: 0, cursor: "pointer", letterSpacing: "-0.01em" }}
              onMouseEnter={e => (e.currentTarget as HTMLButtonElement).style.color = "var(--accent)"}
              onMouseLeave={e => (e.currentTarget as HTMLButtonElement).style.color = "var(--ink)"}
            >{titleCaseSG(prop.name)}</button>
            {viewing.unitLabel && <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--ink-4)", background: "var(--surface-2)", padding: "1px 6px", borderRadius: 4 }}>{viewing.unitLabel}</span>}
            {isScheduled ? <StatusBadge status="Viewing Scheduled" small /> : <StatusBadge status="Viewed" small />}
          </div>
          <div style={{ fontFamily: "var(--font-ui)", fontSize: 11.5, color: "var(--ink-3)", marginBottom: isScheduled ? 6 : 0 }}>{fmtDate(viewing.date)} · {viewing.time} · Agent: {viewing.agent}</div>
          {isScheduled && checklistLen > 0 && (
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <div style={{ height: 4, flex: 1, maxWidth: 100, background: "var(--rule)", borderRadius: 4, overflow: "hidden" }}>
                <div style={{ height: "100%", borderRadius: 4, background: "var(--accent)", width: `${(completedCount / checklistLen) * 100}%` }} />
              </div>
              <span style={{ fontFamily: "var(--font-ui)", fontSize: 12, color: "var(--ink-3)" }}>{t("workspace.viewings.checklistProgress", { done: completedCount, total: checklistLen })}</span>
            </div>
          )}
          {!isScheduled && viewing.ratings && (
            <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginTop: 4 }}>
              {([[t("workspace.dossier.layout"), viewing.ratings.layout], [t("workspace.dossier.light"), viewing.ratings.light], [t("workspace.dossier.noise"), viewing.ratings.noise], [t("workspace.dossier.condition"), viewing.ratings.condition]] as [string, number | null][]).map(([k, v]) => (
                <div key={k} style={{ display: "flex", alignItems: "center", gap: 5 }}><span style={{ fontFamily: "var(--font-ui)", fontSize: 12, color: "var(--ink-4)" }}>{k}</span><RatingDots value={v} /></div>
              ))}
            </div>
          )}
        </div>
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ transform: expanded ? "rotate(0deg)" : "rotate(-90deg)", transition: "transform 150ms", color: "var(--ink-4)", flexShrink: 0, marginTop: 4 }}>
          <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
      {expanded && (
        <div style={{ padding: "14px 16px" }}>
          {isScheduled ? (
            <>
              <div style={{ fontFamily: "var(--font-ui)", fontSize: 12, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--ink-4)", marginBottom: 10 }}>{t("workspace.viewings.checklist")}</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 14 }}>
                {(viewing.checklist ?? []).map((c, i) => (
                  <div key={i} style={{ display: "flex", gap: 9, alignItems: "flex-start" }}>
                    <div style={{ width: 15, height: 15, borderRadius: 3, flexShrink: 0, marginTop: 1, border: c.done ? "none" : "1.5px solid var(--rule-strong)", background: c.done ? "var(--accent)" : "transparent", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      {c.done && <svg width="8" height="8" viewBox="0 0 10 10" fill="none"><path d="M2 5l2.5 2.5L8 3" stroke="white" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" /></svg>}
                    </div>
                    <div style={{ fontFamily: "var(--font-ui)", fontSize: 12.5, color: c.done ? "var(--ink-3)" : "var(--ink-2)", lineHeight: 1.4, textDecoration: c.done ? "line-through" : "none" }}>{c.item}</div>
                  </div>
                ))}
              </div>
              <button onClick={() => openModal({ type: "recordNotes", viewingId: viewing.id })} style={{ width: "100%", background: "var(--accent)", border: "none", borderRadius: 7, padding: 9, fontFamily: "var(--font-ui)", fontSize: 12.5, color: "white", cursor: "pointer", fontWeight: 500 }}>{t("workspace.viewings.recordNotes")}</button>
            </>
          ) : (
            <>
              {viewing.tags && viewing.tags.length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginBottom: 12 }}>
                  {viewing.tags.map((t, i) => <span key={i} style={{ background: "var(--surface-2)", borderRadius: 5, padding: "3px 9px", fontFamily: "var(--font-ui)", fontSize: 11, color: "var(--ink-2)" }}>{t}</span>)}
                </div>
              )}
              {viewing.media && viewing.media.length > 0 && <MediaGrid media={viewing.media} />}
              {viewing.notes && <div style={{ fontFamily: "var(--font-ui)", fontSize: 12.5, color: "var(--ink-2)", lineHeight: 1.55, marginBottom: 12 }}>{viewing.notes}</div>}
              {/* AI viewing summary — server-generated by the summary
                  worker (doc/WORKSPACE_AI_SUMMARIES.md §6.2) once the
                  viewing is Completed and notes exist. We render the
                  block whenever there's content OR a regen in flight,
                  so the buyer sees something is happening. */}
              {(viewing.aiSummary || viewing.summaryStale) && (
                <div style={{ background: "var(--ai-bg)", border: "1px solid var(--ai-rule)", borderRadius: 8, padding: "10px 13px", marginBottom: 10 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 5 }}>
                    <span style={{ fontFamily: "var(--font-ui)", fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--ai-accent)" }}>{t("workspace.viewings.aiSummary")}</span>
                    {viewing.summaryStale && <GeneratingPill label={viewing.aiSummary ? t("workspace.ai.updating") : t("workspace.ai.generating")} />}
                  </div>
                  {/* Viewing summary — model emits bolded section
                      labels (**Strengths:** / **Concerns:** /
                      **Suggested follow-up:**) with bulleted lists.
                      marked renders the <ul>/<li> and .markdown
                      handles spacing. */}
                  {viewing.aiSummary
                    ? <div className="markdown" style={{ fontSize: 12.5, color: "var(--ink-2)", lineHeight: 1.5 }} dangerouslySetInnerHTML={{ __html: renderMarkdown(viewing.aiSummary) }} />
                    : <div style={{ fontFamily: "var(--font-serif)", fontSize: 12, fontStyle: "italic", color: "var(--ink-4)", lineHeight: 1.5 }}>{t("workspace.ai.generatingViewingHint")}</div>}
                </div>
              )}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                {viewing.decision ? <Tag tone="warn">{t("workspace.viewings.decision")}: {decisionLabel(t, viewing.decision)}</Tag> : <Tag tone="neutral">{t("workspace.viewings.noDecision")}</Tag>}
                <button onClick={() => openModal({ type: "recordNotes", viewingId: viewing.id })} style={{ background: "transparent", border: "1px solid var(--rule)", borderRadius: 6, padding: "5px 11px", cursor: "pointer", fontFamily: "var(--font-ui)", fontSize: 11, color: "var(--ink-2)" }}>{t("workspace.viewings.editNotes")}</button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ─── ViewingsTab ──────────────────────────────────────────────────────────────

function ViewingsTab({ onOpenProperty }: { onOpenProperty: (propId: string) => void }) {
  const t = useWsT();
  const { properties, viewings, openModal } = useWsData();
  const [filter, setFilter] = useState<"all" | "upcoming" | "past">("all");
  const [expanded, setExpanded] = useState<string | null>(null);

  const enriched = viewings.map(v => {
    const prop = properties.find(p => p.id === v.propId);
    return { ...v, prop };
  }).filter(v => v.prop) as (Viewing & { prop: Property })[];

  const filtered = filter === "upcoming" ? enriched.filter(v => v.status === "scheduled")
    : filter === "past" ? enriched.filter(v => v.status === "completed")
    : enriched;

  const sorted = [...filtered].sort((a, b) => a.status !== b.status ? (a.status === "scheduled" ? -1 : 1) : a.id.localeCompare(b.id));
  const counts = {
    all: enriched.length,
    upcoming: enriched.filter(v => v.status === "scheduled").length,
    past: enriched.filter(v => v.status === "completed").length,
  };

  const filterBtn = (key: "all" | "upcoming" | "past", label: string) => (
    <button key={key} onClick={() => setFilter(key)} style={{ padding: "5px 12px", borderRadius: 6, border: "1px solid", borderColor: filter === key ? "var(--accent)" : "var(--rule)", background: filter === key ? "var(--accent-soft)" : "transparent", color: filter === key ? "var(--accent)" : "var(--ink-2)", fontFamily: "var(--font-ui)", fontSize: 11.5, fontWeight: 500, cursor: "pointer" }}>
      {label} <span style={{ color: filter === key ? "var(--accent)" : "var(--ink-4)", fontSize: 10.5 }}>({counts[key]})</span>
    </button>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden" }}>
      <div style={{ padding: "13px 28px", borderBottom: "1px solid var(--rule)", display: "flex", alignItems: "center", gap: 10, flexShrink: 0, background: "var(--bg)" }}>
        <div style={{ fontFamily: "var(--font-serif)", fontSize: 16, fontWeight: 500, color: "var(--ink)", letterSpacing: "-0.01em", flex: 1 }}>{t("workspace.viewings.title")}</div>
        <div style={{ display: "flex", gap: 5 }}>{filterBtn("all", t("workspace.viewings.filterAll"))}{filterBtn("upcoming", t("workspace.viewings.filterUpcoming"))}{filterBtn("past", t("workspace.viewings.filterPast"))}</div>
        <button onClick={() => openModal({ type: "addViewing" })} style={{ display: "flex", alignItems: "center", gap: 6, background: "var(--accent)", color: "white", border: "none", borderRadius: 6, padding: "6px 12px", fontFamily: "var(--font-ui)", fontSize: 11.5, fontWeight: 500, cursor: "pointer" }}>
          <svg width="10" height="10" viewBox="0 0 12 12" fill="none"><path d="M6 1v10M1 6h10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" /></svg>
          {t("workspace.viewings.scheduleViewing")}
        </button>
      </div>
      <div style={{ flex: 1, overflow: "auto", padding: "20px 28px" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 10, maxWidth: 760 }}>
          {filter === "all" ? (
            <>
              {counts.upcoming > 0 && (
                <div style={{ paddingBottom: 8 }}>
                  <SectionLabel>{t("workspace.viewings.upcoming")} · {counts.upcoming}</SectionLabel>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {sorted.filter(v => v.status === "scheduled").map(v => <ViewingCard key={v.id} viewing={v} prop={v.prop} onOpenProperty={onOpenProperty} expanded={expanded === v.id} onToggleExpand={() => setExpanded(e => e === v.id ? null : v.id)} />)}
                  </div>
                </div>
              )}
              {counts.past > 0 && (
                <div style={{ paddingTop: counts.upcoming > 0 ? 12 : 0 }}>
                  <SectionLabel>{t("workspace.viewings.past")} · {counts.past}</SectionLabel>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {sorted.filter(v => v.status === "completed").map(v => <ViewingCard key={v.id} viewing={v} prop={v.prop} onOpenProperty={onOpenProperty} expanded={expanded === v.id} onToggleExpand={() => setExpanded(e => e === v.id ? null : v.id)} />)}
                  </div>
                </div>
              )}
            </>
          ) : (
            sorted.map(v => <ViewingCard key={v.id} viewing={v} prop={v.prop} onOpenProperty={onOpenProperty} expanded={expanded === v.id} onToggleExpand={() => setExpanded(e => e === v.id ? null : v.id)} />)
          )}
        </div>
      </div>
    </div>
  );
}

// ─── DecisionBoard ────────────────────────────────────────────────────────────
// The board operates at the unit level: each unit owns its own decision status
// ("New", "Worth Checking", … "Rejected"). For display we still group by
// property — a property card appears in every column where it has at least
// one matching unit, and shows only those units. A property with zero known
// units falls back to its property-level status (option B), so a freshly
// added shortlist row stays visible until units get added.

type DragPayload =
  | { kind: "unit"; propId: string; unitId: string }
  | { kind: "property"; propId: string };                          // no-units fallback — drags the property-level status

// Each entry is one card: a single unit (most common) OR a
// property-level row when the property has no units. The design uses
// per-unit cards on the Decision Board — one card per unit makes the
// column-density legible and removes the ambiguous "drag the parent
// card" interaction.
type ColumnEntry =
  | { kind: "unit"; prop: Property; unit: Unit }
  | { kind: "property"; prop: Property };

function DecisionBoard({ onOpenProperty }: { onOpenProperty: (propId: string) => void }) {
  const t = useWsT();
  const { i18n } = useTranslation();
  const isMobile = useIsMobile();
  const { properties, updateUnit, updateProperty, goToModule } = useWsData();
  // Same date formatter as NegotiationPanel — keeps the on-card mini
  // table dates consistent with the dossier timeline.
  const fdate = (s: string | undefined) => formatMoveDate(s, i18n.language);
  const [dragging, setDragging] = useState<DragPayload | null>(null);
  const [dragOver, setDragOver] = useState<string | null>(null);

  // Column order matches the Claude Design canvas (workspace.jsx
  // DecisionBoard): Negotiating sits between Viewed and Watchlist —
  // both Negotiating & Watchlist are "actively-tracked-but-not-yet-
  // decided", but Negotiating is the warmer one (offer in flight).
  // `tone` is the STATUS_COLORS entry for the column's drop status —
  // drives header color and the optional "warm" lane treatment for
  // Negotiating.
  const groups = [
    { id: "topCandidates", statuses: ["New", "Worth Checking", "Viewing Scheduled"], dropStatus: "Worth Checking", tone: STATUS_COLORS["Worth Checking"] },
    { id: "viewed",        statuses: ["Viewed"],                                      dropStatus: "Viewed",         tone: STATUS_COLORS["Viewed"] },
    { id: "negotiating",   statuses: ["Negotiating"],                                 dropStatus: "Negotiating",    tone: STATUS_COLORS["Negotiating"], warm: true },
    { id: "watchlist",     statuses: ["Watchlist"],                                   dropStatus: "Watchlist",      tone: STATUS_COLORS["Watchlist"] },
    { id: "rejected",      statuses: ["Rejected"],                                    dropStatus: "Rejected",       tone: STATUS_COLORS["Rejected"] },
  ];

  const entriesForColumn = (statuses: string[]): ColumnEntry[] => {
    const out: ColumnEntry[] = [];
    for (const prop of properties) {
      const units = prop.units ?? [];
      if (units.length === 0) {
        // Properties with no known units fall back to the property-
        // level status — one card represents the project itself and
        // drag-drop targets workspace_properties.status directly.
        if (statuses.includes(prop.status)) out.push({ kind: "property", prop });
      } else {
        for (const u of units) {
          if (statuses.includes(u.status)) out.push({ kind: "unit", prop, unit: u });
        }
      }
    }
    return out;
  };

  const handleDrop = (dropStatus: string) => {
    if (!dragging) return;
    if (dragging.kind === "unit") {
      updateUnit(dragging.propId, dragging.unitId, { status: dropStatus });
    } else {
      updateProperty(dragging.propId, { status: dropStatus });
    }
    setDragging(null);
    setDragOver(null);
  };

  // One card per ColumnEntry. Lead with property name (size 12 600);
  // mono unit/price line below; AI summary in serif italic; footer
  // status chip (inline StatusPicker) + open-Q badge. Negotiating
  // cards get a 3px left-edge accent and an embedded offer mini-table
  // between the meta line and the AI summary.
  const renderCard = (entry: ColumnEntry, mobile: boolean) => {
    const isUnit = entry.kind === "unit";
    const prop = entry.prop;
    const u = isUnit ? entry.unit : null;
    const cur = u?.status ?? prop.status;
    const isNegotiating = cur === "Negotiating";
    const offer = u?.negotiation;
    const cardKey = isUnit ? `u-${u!.id}` : `p-${prop.id}`;

    const isCardDragging =
      (isUnit && dragging?.kind === "unit" && dragging.unitId === u!.id) ||
      (!isUnit && dragging?.kind === "property" && dragging.propId === prop.id);

    const dragHandlers = mobile ? {} : {
      draggable: true,
      onDragStart: (e: React.DragEvent) => {
        e.dataTransfer.effectAllowed = "move";
        if (isUnit) setDragging({ kind: "unit", propId: prop.id, unitId: u!.id });
        else setDragging({ kind: "property", propId: prop.id });
      },
      onDragEnd: () => { setDragging(null); setDragOver(null); },
    };

    const onChangeStatus = (next: string) => {
      if (isUnit) updateUnit(prop.id, u!.id, { status: next });
      else updateProperty(prop.id, { status: next });
    };

    const openQs = prop.questions.filter(q => q.status === "Open").length;

    return (
      <div key={cardKey}
        {...dragHandlers}
        onClick={() => onOpenProperty(prop.id)}
        style={{
          background: "var(--surface)",
          border: "1px solid var(--rule)",
          borderLeft: isNegotiating
            ? `3px solid ${STATUS_COLORS["Negotiating"].dot}`
            : "1px solid var(--rule)",
          borderRadius: 9,
          padding: "10px 11px",
          opacity: isCardDragging ? 0.4 : cur === "Rejected" ? 0.6 : 1,
          cursor: !mobile ? "grab" : "pointer",
          transition: "border-color 120ms, box-shadow 120ms, opacity 120ms",
        }}
        onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.boxShadow = "0 1px 3px rgba(23,32,29,0.06)"; }}
        onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.boxShadow = "none"; }}>
        <div style={{ fontFamily: "var(--font-ui)", fontSize: 12, fontWeight: 600, color: "var(--ink)", letterSpacing: "-0.005em", lineHeight: 1.25, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{titleCaseSG(prop.name)}</div>
        {u
          ? <div style={{ fontFamily: "var(--font-mono)", fontSize: 10.5, color: "var(--ink-3)", marginTop: 1, marginBottom: 6 }}>{u.label}{u.price > 0 ? ` · $${(u.price / 1e6).toFixed(2)}M` : ""}</div>
          : <div style={{ fontFamily: "var(--font-mono)", fontSize: 10.5, color: "var(--ink-3)", marginTop: 1, marginBottom: 6 }}>{prop.district} · {prop.tenure}</div>}

        {isNegotiating && offer && (
          <div style={{
            background: STATUS_COLORS["Negotiating"].bg,
            color: STATUS_COLORS["Negotiating"].ink,
            borderRadius: 6, padding: "6px 8px", marginBottom: 6,
            fontFamily: "var(--font-ui)", fontSize: 10, lineHeight: 1.45,
            display: "flex", flexDirection: "column", gap: 2,
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 6 }}>
              <span style={{ opacity: 0.75 }}>{t("workspace.negotiation.event.asking")}</span>
              <span style={{ fontFamily: "var(--font-mono)" }}>${(offer.asking / 1e6).toFixed(3)}M</span>
            </div>
            {typeof offer.buyerOffer === "number" && (
              <div style={{ display: "flex", justifyContent: "space-between", gap: 6 }}>
                <span style={{ opacity: 0.75 }}>{t("workspace.negotiation.event.buyerOffer")}{offer.buyerOfferOn ? ` · ${fdate(offer.buyerOfferOn)}` : ""}</span>
                <span style={{ fontFamily: "var(--font-mono)" }}>${(offer.buyerOffer / 1e6).toFixed(3)}M</span>
              </div>
            )}
            {typeof offer.sellerCounter === "number" && (
              <div style={{ display: "flex", justifyContent: "space-between", gap: 6 }}>
                <span style={{ opacity: 0.75 }}>{t("workspace.negotiation.event.sellerCounter")}{offer.sellerCounterOn ? ` · ${fdate(offer.sellerCounterOn)}` : ""}</span>
                <span style={{ fontFamily: "var(--font-mono)" }}>${(offer.sellerCounter / 1e6).toFixed(3)}M</span>
              </div>
            )}
            {typeof offer.buyerCall === "number" && (
              <div style={{ display: "flex", justifyContent: "space-between", gap: 6, fontWeight: 600 }}>
                <span>{t("workspace.negotiation.event.yourCall")}{offer.buyerCallOn ? ` · ${fdate(offer.buyerCallOn)}` : ""}</span>
                <span style={{ fontFamily: "var(--font-mono)" }}>${(offer.buyerCall / 1e6).toFixed(3)}M</span>
              </div>
            )}
          </div>
        )}

        {prop.aiSummary && (
          // Compact mobile card preview — flatten the structured
          // markdown into single-line plain text so the 3-line clamp
          // still works (line-clamp doesn't compose with the <p>/<br>
          // wrappers marked emits). Bigger surfaces above render the
          // full structured markdown.
          <div style={{
            fontFamily: "var(--font-serif)", fontSize: 11, fontStyle: "italic",
            color: "var(--ink-2)", lineHeight: 1.4, marginBottom: 8,
            display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}>{stripMarkdown(prop.aiSummary)}</div>
        )}

        <div style={{ display: "flex", alignItems: "center", gap: 6, justifyContent: "space-between" }}>
          <div onClick={(e) => e.stopPropagation()}>
            {/* On mobile we keep the chevron always visible — there's
                no hover state to reveal it, and without it the chip
                reads as decorative and users assume the card is
                supposed to be drag-only (which doesn't work on
                touch). Desktop keeps the affordance hidden until
                hover. */}
            <StatusPicker value={cur} onChange={onChangeStatus} size="xs" hideAffordanceWhenIdle={!mobile} />
          </div>
          {openQs > 0 && (
            <span style={{ fontFamily: "var(--font-ui)", fontSize: 10, color: "var(--warn)" }}>
              {t("workspace.shortlist.openQ", { count: openQs })}
            </span>
          )}
        </div>
      </div>
    );
  };

  if (isMobile) {
    if (properties.length === 0) {
      return (
        <div style={{ flex: 1, overflow: "auto", padding: "32px 16px", display: "flex", alignItems: "flex-start", justifyContent: "center" }}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", maxWidth: 360, gap: 10 }}>
            <div style={{ fontFamily: "var(--font-serif)", fontSize: 16, fontWeight: 500, color: "var(--ink)", letterSpacing: "-0.01em" }}>{t("workspace.decision.emptyTitle")}</div>
            <div style={{ fontFamily: "var(--font-ui)", fontSize: 12.5, color: "var(--ink-3)", lineHeight: 1.5 }}>{t("workspace.decision.emptyBody")}</div>
            <button onClick={() => goToModule("shortlist")} style={{ marginTop: 6, background: "var(--accent)", color: "white", border: "none", borderRadius: 6, padding: "8px 14px", fontFamily: "var(--font-ui)", fontSize: 12, fontWeight: 500, cursor: "pointer" }}>
              {t("workspace.decision.emptyCta")}
            </button>
          </div>
        </div>
      );
    }
    return (
      <div style={{ flex: 1, overflow: "auto", padding: "14px 14px 24px" }}>
        {/* All five lanes always render on mobile so the user keeps the
            full bucket structure in view; each lane gets a slim
            single-line header (no toned tile, no tagline) and a tiny
            empty-state when 0 entries — drag-drop isn't a thing here,
            so the column doesn't need to advertise its drop target. */}
        <div style={{
          display: "flex", alignItems: "center", gap: 6,
          padding: "0 4px 12px",
          fontFamily: "var(--font-serif)", fontSize: 12.5,
          fontStyle: "italic", color: "var(--ink-3)", lineHeight: 1.45,
        }}>
          <svg width="11" height="11" viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0, opacity: 0.7 }}>
            <path d="M3 7l3 3 5-6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <span>{t("workspace.decision.mobileHint")}</span>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {groups.map(g => {
            const entries = entriesForColumn(g.statuses);
            return (
              <div key={g.id}>
                <div style={{
                  display: "flex", alignItems: "center", gap: 7,
                  padding: "0 4px", marginBottom: 6,
                }}>
                  <span style={{
                    width: 7, height: 7, borderRadius: 7, background: g.tone.dot, flexShrink: 0,
                  }} />
                  <span style={{
                    fontFamily: "var(--font-ui)", fontSize: 11, fontWeight: 600,
                    letterSpacing: "0.06em", textTransform: "uppercase",
                    color: "var(--ink-3)", flex: 1,
                  }}>{t(`workspace.decision.${g.id}`)}</span>
                  <span style={{
                    fontFamily: "var(--font-mono)", fontSize: 10.5, fontWeight: 500,
                    color: "var(--ink-4)", background: "var(--surface-2)",
                    padding: "0 6px", borderRadius: 4, minWidth: 18, textAlign: "center",
                  }}>{entries.length}</span>
                </div>
                {entries.length === 0 ? (
                  <div style={{
                    border: "1px dashed var(--rule)", borderRadius: 8,
                    padding: "9px 12px", fontFamily: "var(--font-serif)",
                    fontStyle: "italic", fontSize: 12, color: "var(--ink-4)",
                  }}>{t(`workspace.decision.empty.${g.id}`)}</div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
                    {entries.map(e => renderCard(e, true))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div style={{ flex: 1, overflow: "auto", padding: "20px 24px 32px" }}>
      {/* Board legend / explainer — copy comes from the design canvas. */}
      <div style={{
        display: "flex", alignItems: "center", gap: 14,
        padding: "0 4px 14px", flexWrap: "wrap",
      }}>
        <div style={{ fontFamily: "var(--font-serif)", fontSize: 14, fontWeight: 500, color: "var(--ink)", letterSpacing: "-0.01em" }}>
          {t("workspace.decision.helperLine")}
        </div>
        <div style={{ flex: 1 }} />
        <div style={{ display: "flex", alignItems: "center", gap: 6, fontFamily: "var(--font-ui)", fontSize: 11, color: "var(--ink-3)" }}>
          <span style={{ width: 6, height: 6, borderRadius: 6, background: STATUS_COLORS["Negotiating"].dot }} />
          {t("workspace.decision.negotiatingHint")}
        </div>
      </div>

      <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
        {groups.map(g => {
          const entries = entriesForColumn(g.statuses);
          const isOver = dragOver === g.id;
          const warm = g.warm;
          return (
            <div key={g.id} style={{ flex: 1, minWidth: 0 }}
              onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; setDragOver(g.id); }}
              onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setDragOver(null); }}
              onDrop={e => { e.preventDefault(); handleDrop(g.dropStatus); }}>
              {/* Column header — toned tile with dot + label + count + tagline. */}
              <div style={{
                background: warm ? g.tone.bg : "transparent",
                border: warm ? `1px solid ${g.tone.dot}33` : "1px solid var(--rule)",
                borderRadius: 9, padding: "8px 10px 9px", marginBottom: 8,
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{
                    width: 8, height: 8, borderRadius: 8, background: g.tone.dot, flexShrink: 0,
                    boxShadow: warm ? `0 0 0 3px ${g.tone.bg}` : "none",
                  }} />
                  <span style={{
                    fontFamily: "var(--font-ui)", fontSize: 11.5, fontWeight: 600,
                    color: warm ? g.tone.ink : "var(--ink)",
                    letterSpacing: "-0.005em", flex: 1,
                  }}>{t(`workspace.decision.${g.id}`)}</span>
                  <span style={{
                    fontFamily: "var(--font-mono)", fontSize: 11, fontWeight: 500,
                    color: warm ? g.tone.ink : "var(--ink-3)",
                    background: warm ? "rgba(255,255,255,0.5)" : "var(--surface-2)",
                    padding: "0 6px", borderRadius: 4,
                  }}>{entries.length}</span>
                </div>
                <div style={{
                  fontFamily: "var(--font-ui)", fontSize: 10, color: warm ? g.tone.ink : "var(--ink-4)",
                  letterSpacing: "0.04em", marginTop: 3, opacity: 0.85,
                }}>{t(`workspace.decision.tagline.${g.id}`)}</div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 7, minHeight: 60, borderRadius: 10, transition: "background 100ms", background: isOver ? "var(--accent-soft)" : "transparent", padding: isOver ? "7px" : "0", border: isOver ? "2px dashed var(--accent)" : "2px dashed transparent" }}>
                {entries.map(e => renderCard(e, false))}
                {entries.length === 0 && !isOver && (
                  <div style={{
                    border: "1px dashed var(--rule)", borderRadius: 9,
                    padding: "14px 10px", textAlign: "center",
                    fontFamily: "var(--font-serif)", fontStyle: "italic",
                    fontSize: 11.5, color: "var(--ink-4)", lineHeight: 1.45,
                  }}>{t(`workspace.decision.empty.${g.id}`)}</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── DraggableFAB ─────────────────────────────────────────────────────────────

function DraggableFAB({ onOpen }: { onOpen: () => void }) {
  const SIZE = 48;
  const MARGIN = 16;
  const BOTTOM_CLEAR = 90;

  // null = use CSS bottom/right (safe default before any drag)
  // non-null = use left/top after first drag
  const [dragged, setDragged] = useState<{ x: number; y: number } | null>(null);
  const [dragging, setDragging] = useState(false);
  const startRef = useRef<{ px: number; py: number; ox: number; oy: number } | null>(null);
  const movedRef = useRef(false);

  function onPointerDown(e: React.PointerEvent<HTMLButtonElement>) {
    e.currentTarget.setPointerCapture(e.pointerId);
    const ox = dragged?.x ?? window.innerWidth - SIZE - MARGIN;
    const oy = dragged?.y ?? window.innerHeight - SIZE - BOTTOM_CLEAR;
    startRef.current = { px: e.clientX, py: e.clientY, ox, oy };
    movedRef.current = false;
    setDragging(true);
  }

  function onPointerMove(e: React.PointerEvent<HTMLButtonElement>) {
    if (!startRef.current) return;
    const dx = e.clientX - startRef.current.px;
    const dy = e.clientY - startRef.current.py;
    if (Math.abs(dx) > 4 || Math.abs(dy) > 4) movedRef.current = true;
    setDragged({
      x: Math.max(0, Math.min(window.innerWidth - SIZE, startRef.current.ox + dx)),
      y: Math.max(MARGIN, Math.min(window.innerHeight - SIZE - MARGIN, startRef.current.oy + dy)),
    });
  }

  function onPointerUp() {
    setDragging(false);
    startRef.current = null;
    if (!movedRef.current) { onOpen(); return; }
    setDragged(p => {
      if (!p) return p;
      return {
        x: p.x + SIZE / 2 < window.innerWidth / 2 ? MARGIN : window.innerWidth - SIZE - MARGIN,
        y: Math.max(MARGIN, Math.min(window.innerHeight - SIZE - MARGIN, p.y)),
      };
    });
  }

  const posStyle: React.CSSProperties = dragged
    ? { left: dragged.x, top: dragged.y }
    : { right: MARGIN, bottom: BOTTOM_CLEAR };

  return (
    <button
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      title="Open AI Assistant"
      style={{
        position: "fixed", zIndex: 50, ...posStyle,
        width: SIZE, height: SIZE, borderRadius: "50%", border: "none",
        background: "var(--accent)", color: "white",
        display: "flex", alignItems: "center", justifyContent: "center",
        cursor: dragging ? "grabbing" : "grab",
        touchAction: "none", userSelect: "none",
        boxShadow: dragging
          ? "0 10px 28px rgba(0,0,0,0.28), 0 2px 8px rgba(0,0,0,0.16)"
          : "0 4px 16px rgba(0,0,0,0.18), 0 1px 4px rgba(0,0,0,0.12), inset 0 1px 0 rgba(255,255,255,0.15)",
        transform: dragging ? "scale(1.1)" : "scale(1)",
        transition: dragging
          ? "transform 100ms ease, box-shadow 100ms ease"
          : "left 280ms cubic-bezier(0.34,1.56,0.64,1), top 280ms cubic-bezier(0.34,1.56,0.64,1), transform 200ms ease, box-shadow 200ms ease",
      }}
    >
      <AIStarIcon />
    </button>
  );
}

// ─── AIWorkspacePanel ─────────────────────────────────────────────────────────

function AIWorkspacePanel({ module, collapsed, onToggle, mobile }: { module: string; collapsed: boolean; onToggle: () => void; mobile?: boolean }) {
  const t = useWsT();
  const [input, setInput] = useState("");
  const [msgs, setMsgs] = useState<{ role: string; text: string }[]>([]);
  const endRef = useRef<HTMLDivElement>(null);

  const ctxSuggestions: Record<string, string[]> = {
    overview:  ["What needs my attention most?", "Which unit should I view first?", "Am I on track for my timeline?"],
    shortlist: ["Compare Marina One units", "Which unit is overpriced?", "Find missing information"],
    compare:   ["Which has better resale liquidity?", "Summarize the tradeoffs", "What would you offer for #24-07?"],
    viewings:  ["Generate checklist for V on Shenton #31-02", "Summarize my viewing notes", "What should I ask the agent?"],
    decision:  ["Should I make an offer for #24-07?", "What info am I still missing?", "Help me decide between top 2"],
  };
  const suggestions = ctxSuggestions[module] ?? ctxSuggestions.overview;

  const send = (text: string) => {
    if (!text.trim()) return;
    setMsgs(m => [...m, { role: "user", text }, { role: "ai", text: `Analyzing "${text.slice(0, 40)}…" — response would appear here with workspace context.` }]);
    setInput("");
    setTimeout(() => endRef.current?.scrollIntoView?.({ block: "end" }), 50);
  };

  if (collapsed) {
    return <DraggableFAB onOpen={onToggle} />;
  }

  // Mobile: render as a bottom-sheet overlay
  if (mobile) {
    return (
      <>
        <div onClick={onToggle} style={{ position: "fixed", inset: 0, zIndex: 40, background: "rgba(0,0,0,0.45)", backdropFilter: "blur(2px)" }} />
        <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 50, height: "72vh", background: "var(--surface)", borderRadius: "16px 16px 0 0", borderTop: "1px solid var(--rule)", display: "flex", flexDirection: "column", overflow: "hidden" }}>
          {/* drag handle */}
          <div style={{ padding: "10px 0 6px", display: "flex", justifyContent: "center", flexShrink: 0 }}>
            <div style={{ width: 36, height: 4, borderRadius: 2, background: "var(--rule-strong)" }} />
          </div>
          <div style={{ padding: "0 16px 10px", borderBottom: "1px solid var(--rule)", display: "flex", alignItems: "center", gap: 7, flexShrink: 0 }}>
            <AIStarIcon />
            <div style={{ fontFamily: "var(--font-ui)", fontSize: 12, fontWeight: 600, color: "var(--ink)", letterSpacing: "-0.005em", flex: 1 }}>{t("workspace.ai.title")}</div>
            <span style={{ fontFamily: "var(--font-ui)", fontSize: 11, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--ai-accent)", background: "var(--ai-bg)", border: "1px solid var(--ai-rule)", padding: "1px 5px", borderRadius: 3 }}>{t("workspace.ai.contextAware")}</span>
            <button onClick={onToggle} style={{ width: 26, height: 26, display: "flex", alignItems: "center", justifyContent: "center", background: "var(--surface-2)", border: "none", cursor: "pointer", color: "var(--ink-3)", borderRadius: "50%" }}>
              <svg width="11" height="11" viewBox="0 0 14 14" fill="none"><path d="M11 3L3 11M3 3l8 8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" /></svg>
            </button>
          </div>
          <div style={{ flex: 1, overflow: "auto", padding: "12px 16px", display: "flex", flexDirection: "column", gap: 8 }}>
            {msgs.length === 0 ? (
              <>
                <div style={{ fontFamily: "var(--font-serif)", fontSize: 13, fontStyle: "italic", color: "var(--ink-3)", lineHeight: 1.5, padding: "4px 0 8px" }}>{t("workspace.ai.emptyHint")}</div>
                {suggestions.map((s, i) => (
                  <button key={i} onClick={() => send(s)} style={{ background: "var(--ai-bg)", border: "1px solid var(--ai-rule)", borderRadius: 8, padding: "10px 12px", fontFamily: "var(--font-serif)", fontSize: 12.5, fontStyle: "italic", color: "var(--ink-2)", textAlign: "left", cursor: "pointer", lineHeight: 1.45 }}>{s}</button>
                ))}
              </>
            ) : msgs.map((m, i) => (
              <div key={i} style={{ display: "flex", flexDirection: m.role === "user" ? "row-reverse" : "row", gap: 7 }}>
                {m.role === "ai" && <div style={{ width: 22, height: 22, borderRadius: 22, background: "var(--ai-bg)", border: "1px solid var(--ai-rule)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1 }}><AIStarIcon /></div>}
                <div style={{ maxWidth: "85%", padding: "8px 12px", borderRadius: 10, background: m.role === "user" ? "var(--accent)" : "var(--ai-bg)", border: m.role === "ai" ? "1px solid var(--ai-rule)" : "none", fontFamily: m.role === "ai" ? "var(--font-serif)" : "var(--font-ui)", fontSize: 13, fontStyle: m.role === "ai" ? "italic" : "normal", color: m.role === "user" ? "white" : "var(--ink-2)", lineHeight: 1.5 }}>{m.text}</div>
              </div>
            ))}
            <div ref={endRef} />
          </div>
          <div style={{ padding: "10px 14px", borderTop: "1px solid var(--rule)", paddingBottom: "calc(10px + var(--sab, env(safe-area-inset-bottom, 0px)))" }}>
            <div style={{ display: "flex", gap: 8, background: "var(--bg)", border: "1px solid var(--rule-strong)", borderRadius: 10, padding: "8px 10px" }}>
              <input value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); send(input); } }} placeholder={t("workspace.ai.placeholder")} style={{ flex: 1, background: "transparent", border: "none", outline: "none", fontFamily: "var(--font-serif)", fontSize: 14, fontStyle: "italic", color: "var(--ink)" }} />
              <button onClick={() => send(input)} style={{ width: 30, height: 30, borderRadius: 8, border: "none", background: input.trim() ? "var(--accent)" : "var(--surface-2)", color: input.trim() ? "white" : "var(--ink-4)", display: "flex", alignItems: "center", justifyContent: "center", cursor: input.trim() ? "pointer" : "not-allowed", flexShrink: 0 }}>
                <svg width="12" height="12" viewBox="0 0 14 14" fill="none"><path d="M7 11V3M3 7l4-4 4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <div style={{ width: 280, flexShrink: 0, borderLeft: "1px solid var(--rule)", background: "var(--surface)", display: "flex", flexDirection: "column", overflow: "hidden", transition: "width 180ms ease" }}>
      <div style={{ padding: "0 8px 0 14px", borderBottom: "1px solid var(--rule)", display: "flex", alignItems: "center", gap: 7, minHeight: 44 }}>
        <AIStarIcon />
        <div style={{ fontFamily: "var(--font-ui)", fontSize: 12, fontWeight: 600, color: "var(--ink)", letterSpacing: "-0.005em", flex: 1 }}>{t("workspace.ai.title")}</div>
        <span style={{ fontFamily: "var(--font-ui)", fontSize: 11, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--ai-accent)", background: "var(--ai-bg)", border: "1px solid var(--ai-rule)", padding: "1px 5px", borderRadius: 3 }}>{t("workspace.ai.contextAware")}</span>
        <button onClick={onToggle} title="Collapse AI Assistant" style={{ width: 26, height: 26, display: "flex", alignItems: "center", justifyContent: "center", background: "transparent", border: "none", cursor: "pointer", color: "var(--ink-4)", borderRadius: 5, flexShrink: 0, marginLeft: 2 }}
          onMouseEnter={e => (e.currentTarget as HTMLButtonElement).style.background = "var(--surface-2)"}
          onMouseLeave={e => (e.currentTarget as HTMLButtonElement).style.background = "transparent"}>
          <svg width="12" height="12" viewBox="0 0 14 14" fill="none"><path d="M5 3l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
      </div>
      <div style={{ flex: 1, overflow: "auto", padding: "12px 14px", display: "flex", flexDirection: "column", gap: 8 }}>
        {msgs.length === 0 ? (
          <>
            <div style={{ fontFamily: "var(--font-serif)", fontSize: 12.5, fontStyle: "italic", color: "var(--ink-3)", lineHeight: 1.5, padding: "4px 0 8px" }}>{t("workspace.ai.emptyHint")}</div>
            {suggestions.map((s, i) => (
              <button key={i} onClick={() => send(s)} style={{ background: "var(--ai-bg)", border: "1px solid var(--ai-rule)", borderRadius: 7, padding: "8px 10px", fontFamily: "var(--font-serif)", fontSize: 12, fontStyle: "italic", color: "var(--ink-2)", textAlign: "left", cursor: "pointer", lineHeight: 1.45 }}
                onMouseEnter={e => (e.currentTarget as HTMLButtonElement).style.borderColor = "var(--ai-accent)"}
                onMouseLeave={e => (e.currentTarget as HTMLButtonElement).style.borderColor = "var(--ai-rule)"}
              >{s}</button>
            ))}
          </>
        ) : msgs.map((m, i) => (
          <div key={i} style={{ display: "flex", flexDirection: m.role === "user" ? "row-reverse" : "row", gap: 7 }}>
            {m.role === "ai" && <div style={{ width: 20, height: 20, borderRadius: 20, background: "var(--ai-bg)", border: "1px solid var(--ai-rule)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1 }}><AIStarIcon /></div>}
            <div style={{ maxWidth: "85%", padding: "7px 10px", borderRadius: 9, background: m.role === "user" ? "var(--accent)" : "var(--ai-bg)", border: m.role === "ai" ? "1px solid var(--ai-rule)" : "none", fontFamily: m.role === "ai" ? "var(--font-serif)" : "var(--font-ui)", fontSize: 12, fontStyle: m.role === "ai" ? "italic" : "normal", color: m.role === "user" ? "white" : "var(--ink-2)", lineHeight: 1.5 }}>{m.text}</div>
          </div>
        ))}
        <div ref={endRef} />
      </div>
      <div style={{ padding: "10px 12px 14px", borderTop: "1px solid var(--rule)" }}>
        <div style={{ display: "flex", gap: 6, background: "var(--bg)", border: "1px solid var(--rule-strong)", borderRadius: 8, padding: "7px 8px" }}>
          <input value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); send(input); } }} placeholder={t("workspace.ai.placeholder")} style={{ flex: 1, background: "transparent", border: "none", outline: "none", fontFamily: "var(--font-serif)", fontSize: 12.5, fontStyle: "italic", color: "var(--ink)" }} />
          <button onClick={() => send(input)} style={{ width: 26, height: 26, borderRadius: 6, border: "none", background: input.trim() ? "var(--accent)" : "var(--surface-2)", color: input.trim() ? "white" : "var(--ink-4)", display: "flex", alignItems: "center", justifyContent: "center", cursor: input.trim() ? "pointer" : "not-allowed", flexShrink: 0 }}>
            <svg width="11" height="11" viewBox="0 0 14 14" fill="none"><path d="M7 11V3M3 7l4-4 4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
          </button>
        </div>
      </div>
    </div>
  );
}


// ─── Modal helpers ────────────────────────────────────────────────────────────

const inputCss: React.CSSProperties = {
  width: "100%", boxSizing: "border-box", background: "var(--bg)",
  border: "1px solid var(--rule-strong)", borderRadius: 7, padding: "8px 10px",
  fontFamily: "var(--font-ui)", fontSize: 16, color: "var(--ink)", outline: "none",
};
const selectCss: React.CSSProperties = { ...inputCss, cursor: "pointer" };
const errInputCss: React.CSSProperties = { ...inputCss, borderColor: "var(--neg)", boxShadow: "0 0 0 3px rgba(180, 60, 50, 0.12)" };

function Field({ label, children, error }: { label: string; children: React.ReactNode; error?: string }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontFamily: "var(--font-ui)", fontSize: 12, fontWeight: 600, color: error ? "var(--neg)" : "var(--ink-3)", letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 5 }}>{label}{error ? " *" : ""}</div>
      {children}
      {error && <div style={{ fontFamily: "var(--font-ui)", fontSize: 11.5, color: "var(--neg)", marginTop: 4 }}>{error}</div>}
    </div>
  );
}

function WsModal({ title, onClose, children, onSubmit, submitLabel }: {
  title: string; onClose: () => void; children: React.ReactNode;
  onSubmit: () => void; submitLabel: string;
}) {
  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 100, background: "rgba(0,0,0,0.5)", backdropFilter: "blur(3px)" }} />
      <div style={{ position: "fixed", top: "50%", left: "50%", transform: "translate(-50%,-50%)", zIndex: 101, background: "var(--surface)", borderRadius: 14, boxShadow: "0 24px 64px rgba(0,0,0,0.2)", width: "min(500px, calc(100vw - 32px))", maxHeight: "88vh", overflow: "auto", display: "flex", flexDirection: "column" }}>
        <div style={{ padding: "20px 22px 16px", borderBottom: "1px solid var(--rule)", display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
          <div style={{ flex: 1, fontFamily: "var(--font-serif)", fontSize: 18, fontWeight: 500, color: "var(--ink)", letterSpacing: "-0.015em" }}>{title}</div>
          <button onClick={onClose} style={{ width: 28, height: 28, borderRadius: 7, background: "var(--surface-2)", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--ink-3)" }}>
            <svg width="11" height="11" viewBox="0 0 12 12" fill="none"><path d="M1 1l10 10M11 1L1 11" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" /></svg>
          </button>
        </div>
        <div style={{ flex: 1, overflow: "auto", padding: "20px 22px 0" }}>{children}</div>
        <div style={{ padding: "16px 22px 20px", flexShrink: 0 }}>
          <button onClick={onSubmit} style={{ width: "100%", background: "var(--accent)", border: "none", borderRadius: 8, padding: "12px 0", fontFamily: "var(--font-ui)", fontSize: 16, fontWeight: 500, color: "white", cursor: "pointer" }}>{submitLabel}</button>
        </div>
      </div>
    </>
  );
}

// ─── AddPropertyModal ─────────────────────────────────────────────────────────

const BED_OPTIONS = ["Studio", "1-BR", "2-BR", "3-BR", "4-BR", "5-BR+"];

type PropertyHitKind = "project" | "hdb";
interface PropertyHit {
  kind: PropertyHitKind;
  name: string;
  subtitle?: string;
  // project-only
  slug?: string | null;
  district?: string | null;
  tenure?: string | null;
  property_types?: string[] | null;
  market_segment?: string | null;
  // hdb-only
  block?: string | null;
  street_name?: string | null;
  estate_name?: string | null;
  year_completed?: number | null;
  // shared
  street?: string | null;
  postal?: string | null;
  lat?: number | null;
  lng?: number | null;
  planning_area?: string | null;
}

function AddPropertyModal({ onClose }: { onClose: () => void }) {
  const { addProperty } = useWsData();

  const [query, setQuery] = useState("");
  const [hits, setHits] = useState<PropertyHit[]>([]);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const reqIdRef = useRef(0);

  const [selected, setSelected] = useState<PropertyHit | null>(null);

  // bedroom multi-select
  const [beds, setBeds] = useState<string[]>([]);
  const toggleBed = (b: string) => setBeds(prev => prev.includes(b) ? prev.filter(x => x !== b) : [...prev, b]);

  // Debounced unified search across projects + HDB blocks.
  useEffect(() => {
    const q = query.trim();
    if (selected || q.length < 2) {
      setHits([]);
      setLoading(false);
      return;
    }
    const myId = ++reqIdRef.current;
    setLoading(true);
    const t = window.setTimeout(async () => {
      try {
        const r = await apiJson<{ rows: PropertyHit[] }>(
          `/dataengine/search/properties?q=${encodeURIComponent(q)}&limit=10`,
        );
        if (myId !== reqIdRef.current) return;
        setHits(r.rows ?? []);
        setActiveIdx(0);
      } catch {
        if (myId === reqIdRef.current) setHits([]);
      } finally {
        if (myId === reqIdRef.current) setLoading(false);
      }
    }, 180);
    return () => window.clearTimeout(t);
  }, [query, selected]);

  const selectHit = (hit: PropertyHit) => {
    setSelected(hit);
    setQuery(hit.name);
    setSuggestOpen(false);
  };

  const clearSelection = () => {
    setSelected(null);
    setQuery("");
  };

  const derived = selected ? deriveProjectFields(selected) : null;

  const submit = () => {
    if (!selected || !query.trim()) return;
    const bedsStr = beds.length > 0 ? beds.join(", ") : "—";
    addProperty({
      name: query.trim(), project: selected.name,
      district: derived?.district ?? "", tenure: derived?.tenure ?? "99-yr",
      top: derived?.top ?? 0, beds: bedsStr, mrt: derived?.mrt ?? "",
      status: "New", fitScore: 5.0, aiSummary: "", pros: [], risks: [], questions: [], units: [],
    });
    onClose();
  };

  const lockedFieldCss: React.CSSProperties = {
    ...inputCss, background: "var(--surface-2)", color: derived ? "var(--ink)" : "var(--ink-4)",
    cursor: "default", userSelect: "none",
  };

  const showDropdown = !selected && suggestOpen && query.trim().length >= 2;

  return (
    <WsModal title="Add property" onClose={onClose} onSubmit={submit} submitLabel="Add to shortlist">
      {/* Autocomplete: projects + HDB blocks, with kind labels */}
      <Field label="Project name">
        <div style={{ position: "relative" }}>
          <input
            style={inputCss} value={query} autoFocus
            onChange={e => {
              setQuery(e.target.value);
              setSuggestOpen(true);
              if (selected) setSelected(null);
            }}
            onFocus={() => setSuggestOpen(true)}
            onBlur={() => setTimeout(() => setSuggestOpen(false), 150)}
            onKeyDown={e => {
              if (!showDropdown || hits.length === 0) return;
              if (e.key === "ArrowDown") {
                setActiveIdx(i => Math.min(i + 1, hits.length - 1));
                e.preventDefault();
              } else if (e.key === "ArrowUp") {
                setActiveIdx(i => Math.max(i - 1, 0));
                e.preventDefault();
              } else if (e.key === "Enter") {
                const hit = hits[activeIdx];
                if (hit) {
                  selectHit(hit);
                  e.preventDefault();
                }
              } else if (e.key === "Escape") {
                setSuggestOpen(false);
              }
            }}
            placeholder="Search project or HDB block… e.g. Marina, Tampines"
            role="combobox"
            aria-expanded={showDropdown}
          />
          {showDropdown && (
            <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, zIndex: 50, background: "var(--surface)", border: "1px solid var(--rule)", borderRadius: 8, boxShadow: "0 8px 24px rgba(0,0,0,0.12)", maxHeight: 320, overflow: "auto", padding: 4 }}>
              {loading && hits.length === 0 && (
                <div style={{ padding: "10px 12px", fontFamily: "var(--font-ui)", fontSize: 13, color: "var(--ink-4)" }}>Searching…</div>
              )}
              {!loading && hits.length === 0 && (
                <div style={{ padding: "10px 12px", fontFamily: "var(--font-ui)", fontSize: 13, color: "var(--ink-4)" }}>No matches.</div>
              )}
              {hits.map((h, i) => (
                <PropertyHitRow key={`${h.kind}:${h.name}:${h.block ?? ""}:${h.street_name ?? ""}`}
                  hit={h} active={i === activeIdx}
                  onMouseEnter={() => setActiveIdx(i)}
                  onMouseDown={(e) => { e.preventDefault(); selectHit(h); }} />
              ))}
            </div>
          )}
        </div>
        {selected && (
          <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 8, fontFamily: "var(--font-ui)", fontSize: 12 }}>
            <KindBadge hit={selected} />
            <span style={{ color: "var(--ink-3)", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{selected.subtitle || ""}</span>
            <button type="button" onClick={clearSelection} style={{ background: "transparent", border: "1px solid var(--rule)", borderRadius: 6, padding: "3px 9px", fontSize: 11, color: "var(--ink-2)", cursor: "pointer" }}>Change</button>
          </div>
        )}
      </Field>

      {/* Fetched fields — locked, populated when project selected */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <Field label="District">
          <div style={lockedFieldCss}>{derived?.district || <span style={{ color: "var(--ink-4)" }}>—</span>}</div>
        </Field>
        <Field label="Tenure">
          <div style={lockedFieldCss}>{derived?.tenure || <span style={{ color: "var(--ink-4)" }}>—</span>}</div>
        </Field>
        <Field label="TOP year">
          <div style={lockedFieldCss}>{derived?.top || <span style={{ color: "var(--ink-4)" }}>—</span>}</div>
        </Field>
        <Field label="Nearest MRT">
          <div style={lockedFieldCss}>{derived?.mrt || <span style={{ color: "var(--ink-4)" }}>—</span>}</div>
        </Field>
      </div>
      {!selected && query.length > 0 && (
        <div style={{ fontFamily: "var(--font-ui)", fontSize: 12, color: "var(--ink-4)", fontStyle: "italic", marginTop: -6, marginBottom: 12 }}>
          Select a project from the list to auto-fill project details.
        </div>
      )}

      {/* Bedroom multi-select chips */}
      <Field label="Bedrooms">
        <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
          {BED_OPTIONS.map(b => {
            const active = beds.includes(b);
            return (
              <button key={b} type="button" onClick={() => toggleBed(b)}
                style={{ padding: "7px 14px", borderRadius: 7, border: `1.5px solid ${active ? "var(--accent)" : "var(--rule)"}`, background: active ? "var(--accent)" : "transparent", color: active ? "white" : "var(--ink-2)", fontFamily: "var(--font-ui)", fontSize: 14, fontWeight: active ? 600 : 400, cursor: "pointer", transition: "all 100ms" }}>
                {b}
              </button>
            );
          })}
        </div>
      </Field>
    </WsModal>
  );
}

// ─── Property autocomplete helpers ────────────────────────────────────────────

function PropertyHitRow({ hit, active, onMouseEnter, onMouseDown }: {
  hit: PropertyHit;
  active: boolean;
  onMouseEnter: () => void;
  onMouseDown: (e: React.MouseEvent) => void;
}) {
  return (
    <button type="button"
      onMouseEnter={onMouseEnter}
      onMouseDown={onMouseDown}
      role="option"
      aria-selected={active}
      style={{
        width: "100%", textAlign: "left", padding: "8px 10px",
        borderRadius: 6, border: "none",
        background: active ? "var(--surface-2)" : "transparent",
        cursor: "pointer", display: "flex", alignItems: "center", gap: 10,
        fontFamily: "var(--font-ui)",
      }}>
      <KindBadge hit={hit} />
      <span style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13.5, fontWeight: 600, color: "var(--ink)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {hit.name}
        </div>
        {hit.subtitle && (
          <div style={{ fontSize: 11.5, color: "var(--ink-3)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {hit.subtitle}
          </div>
        )}
      </span>
    </button>
  );
}

function KindBadge({ hit }: { hit: PropertyHit }) {
  const isHdb = hit.kind === "hdb";
  const t = (hit.property_types?.[0] ?? "").toLowerCase();
  let label = "Condo";
  if (isHdb) label = "HDB";
  else if (t.includes("apartment")) label = "Apt";
  else if (t.includes("executive condominium")) label = "EC";
  else if (t.includes("terrace") || t.includes("semi-detached") || t.includes("detached")) label = "Landed";
  const palette = isHdb
    ? { bg: "rgba(214,138,60,0.12)", fg: "#a55a14", border: "rgba(214,138,60,0.35)" }
    : { bg: "rgba(56,122,90,0.12)", fg: "#2f6c4f", border: "rgba(56,122,90,0.35)" };
  return (
    <span style={{
      flexShrink: 0,
      fontSize: 10, fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase",
      padding: "3px 7px", borderRadius: 999,
      background: palette.bg, color: palette.fg, border: `1px solid ${palette.border}`,
      fontFamily: "var(--font-ui)",
    }}>{label}</span>
  );
}

// Derives the locked fields shown below the autocomplete from a hit.
// HDB blocks: tenure defaults to 99-yr, TOP comes from year_completed
// (HDB Property Information dataset). Private projects: TOP/MRT still
// need separate enrichment (project_details.top_year / nearest-POI
// lookup) and stay blank for now.
function deriveProjectFields(hit: PropertyHit): { district: string; tenure: string; top: number; mrt: string } {
  if (hit.kind === "hdb") {
    return {
      district: hit.district ?? "",
      tenure: "99-yr",
      top: hit.year_completed ?? 0,
      mrt: "",
    };
  }
  return {
    district: hit.district ?? "",
    tenure: hit.tenure ?? "",
    top: 0,
    mrt: "",
  };
}

// ─── ScheduleViewingModal ─────────────────────────────────────────────────────

function fmtDate(iso: string) {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("en-SG", { weekday: "short", day: "numeric", month: "short", year: "numeric" });
}
function fmtTime(t: string) {
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  return `${h % 12 || 12}:${m.toString().padStart(2, "0")} ${ampm}`;
}
// viewingDateParts splits a canonical ISO date (yyyy-mm-dd) into the
// day-number + short uppercase month shown on the viewing-card date chip.
// viewing.date is stored as ISO (PROP-167) so the chip parses it here.
function viewingDateParts(iso: string) {
  if (!iso) return { day: "", month: "" };
  const d = new Date(iso + "T00:00:00");
  return {
    day: String(d.getDate()),
    month: d.toLocaleDateString("en-SG", { month: "short" }).toUpperCase(),
  };
}

function ScheduleViewingModal({ prefillPropId, onClose }: { prefillPropId?: string; onClose: () => void }) {
  const t = useWsT();
  const { properties, addViewing, addUnit } = useWsData();
  const active = properties.filter(p => p.status !== "Rejected");
  const tomorrow = new Date(); tomorrow.setDate(tomorrow.getDate() + 1);
  const defaultDate = tomorrow.toISOString().split("T")[0];
  const [f, setF] = useState({ propId: prefillPropId ?? active[0]?.id ?? "", unitLabel: "", date: defaultDate, time: "10:00", agent: "" });
  const [pendingCreate, setPendingCreate] = useState<string | null>(null);
  const [tried, setTried] = useState(false);
  const set = (k: string, v: string) => setF(p => ({ ...p, [k]: v }));

  const selectedProp = active.find(p => p.id === f.propId);
  const knownUnits = selectedProp?.units ?? [];
  const matchesKnown = (label: string) => knownUnits.some(u => u.label.toLowerCase() === label.toLowerCase());

  const dateError = tried && !f.date ? t("workspace.modal.err.pickDate") : "";
  const agentError = tried && !f.agent.trim() ? t("workspace.modal.err.agentName") : "";

  const finalize = (label: string) => {
    // Store the canonical ISO date (f.date is the type="date" yyyy-mm-dd) so
    // the server's ISO validation (PROP-123) accepts it; the UI formats it
    // with fmtDate at render time. time stays human (server doesn't validate
    // viewing_time format). PROP-167.
    addViewing({ propId: f.propId, unitLabel: label || undefined, status: "scheduled", date: f.date, time: fmtTime(f.time), agent: f.agent, checklist: [] });
    onClose();
  };

  const submit = () => {
    if (!f.date || !f.agent.trim()) { setTried(true); return; }
    const label = f.unitLabel.trim();
    if (!label || matchesKnown(label)) { finalize(label); return; }
    setPendingCreate(label);
  };

  const confirmCreate = () => {
    if (!pendingCreate) return;
    const label = pendingCreate;
    const m = label.match(/#?(\d+)\s*[-–]\s*(\w+)/);
    const floor = m ? parseInt(m[1]) || 0 : 0;
    const stack = m ? m[2] : "";
    addUnit(f.propId, {
      id: `u_${Date.now()}`, label, floor, stack, facing: "—",
      size: 0, price: 0, psf: 0, maintenance: "—", tenancy: "Vacant",
      status: "Viewing Scheduled",
    });
    setPendingCreate(null);
    finalize(label);
  };

  if (pendingCreate) {
    return (
      <WsModal title={t("workspace.modal.title.newUnit")} onClose={() => setPendingCreate(null)} onSubmit={confirmCreate} submitLabel={t("workspace.modal.submit.newUnitSchedule", { label: pendingCreate })}>
        <div style={{ fontFamily: "var(--font-serif)", fontSize: 14, color: "var(--ink-2)", lineHeight: 1.5, marginBottom: 12 }}>
          <Trans
            i18nKey="workspace.modal.newUnitSeen"
            values={{ label: pendingCreate, property: selectedProp?.name ?? t("workspace.modal.thisProperty") }}
            components={{ unit: <span style={{ fontFamily: "var(--font-mono)", fontWeight: 600, color: "var(--ink)" }} /> }}
          />
        </div>
        <div style={{ fontFamily: "var(--font-ui)", fontSize: 13, color: "var(--ink-3)", lineHeight: 1.5, marginBottom: 16 }}>
          {t("workspace.modal.newUnitBody")}
        </div>
        <button type="button" onClick={() => setPendingCreate(null)} style={{ width: "100%", background: "transparent", border: "1px solid var(--rule)", borderRadius: 8, padding: "10px 0", fontFamily: "var(--font-ui)", fontSize: 14, color: "var(--ink-2)", cursor: "pointer" }}>
          {t("workspace.modal.btn.goBack")}
        </button>
      </WsModal>
    );
  }

  return (
    <WsModal title={t("workspace.modal.title.schedule")} onClose={onClose} onSubmit={submit} submitLabel={t("workspace.modal.submit.schedule")}>
      <Field label={t("workspace.modal.field.property")}>
        <select style={selectCss} value={f.propId} onChange={e => { set("propId", e.target.value); set("unitLabel", ""); }}>
          {active.map(p => <option key={p.id} value={p.id}>{titleCaseSG(p.name)}</option>)}
        </select>
      </Field>
      <Field label={t("workspace.modal.field.unitStack")}>
        {knownUnits.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
            {knownUnits.map(ku => {
              const active = f.unitLabel === ku.label;
              return (
                <button key={ku.id} type="button" onClick={() => set("unitLabel", active ? "" : ku.label)}
                  style={{ padding: "5px 11px", borderRadius: 7, border: `1.5px solid ${active ? "var(--accent)" : "var(--rule)"}`, background: active ? "var(--accent)" : "transparent", color: active ? "white" : "var(--ink-2)", fontFamily: "var(--font-mono)", fontSize: 12, fontWeight: active ? 600 : 400, cursor: "pointer", transition: "all 100ms" }}>
                  {ku.label}
                </button>
              );
            })}
          </div>
        )}
        <input style={inputCss} value={f.unitLabel} onChange={e => set("unitLabel", e.target.value)} placeholder={knownUnits.length > 0 ? t("workspace.modal.ph.unitPick") : t("workspace.modal.ph.unit")} />
        {f.unitLabel.trim() && !matchesKnown(f.unitLabel.trim()) && (
          <div style={{ fontFamily: "var(--font-ui)", fontSize: 11.5, color: "var(--ink-3)", marginTop: 4 }}>{t("workspace.modal.hint.newUnit")}</div>
        )}
      </Field>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <Field label={t("workspace.modal.field.date")} error={dateError}><input style={dateError ? errInputCss : inputCss} type="date" value={f.date} onChange={e => set("date", e.target.value)} /></Field>
        <Field label={t("workspace.modal.field.time")}><input style={inputCss} type="time" value={f.time} onChange={e => set("time", e.target.value)} /></Field>
      </div>
      <Field label={t("workspace.modal.field.agent")} error={agentError}><input style={agentError ? errInputCss : inputCss} value={f.agent} onChange={e => set("agent", e.target.value)} placeholder={t("workspace.modal.ph.agent")} /></Field>
    </WsModal>
  );
}

// ─── RecordNotesModal ─────────────────────────────────────────────────────────

function RecordNotesModal({ viewingId, onClose }: { viewingId: string; onClose: () => void }) {
  const t = useWsT();
  const { viewings, updateViewing } = useWsData();
  const v = viewings.find(x => x.id === viewingId);
  const isEdit = v?.status === "completed";
  const [ratings, setRatings] = useState({
    layout: v?.ratings?.layout ?? 0,
    light: v?.ratings?.light ?? 0,
    noise: v?.ratings?.noise ?? 0,
    condition: v?.ratings?.condition ?? 0,
  });
  const [notes, setNotes] = useState(v?.notes ?? "");
  const [tagInput, setTagInput] = useState("");
  const [tags, setTags] = useState<string[]>(v?.tags ?? []);
  const [decision, setDecision] = useState(v?.decision ?? "");
  if (!v) return null;
  const addTag = () => { if (tagInput.trim()) { setTags(t => [...t, tagInput.trim()]); setTagInput(""); } };
  const ratingPicker = (key: keyof typeof ratings, label: string) => (
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
      <div style={{ width: 100, fontFamily: "var(--font-ui)", fontSize: 12, color: "var(--ink-3)", flexShrink: 0 }}>{label}</div>
      <div style={{ display: "flex", gap: 4 }}>
        {[1, 2, 3, 4, 5].map(n => (
          <button key={n} onClick={() => setRatings(r => ({ ...r, [key]: r[key] === n ? 0 : n }))}
            style={{ width: 28, height: 28, borderRadius: 6, border: "1px solid", cursor: "pointer", fontFamily: "var(--font-mono)", fontSize: 11, transition: "background 100ms", borderColor: ratings[key] >= n ? "var(--accent)" : "var(--rule)", background: ratings[key] >= n ? "var(--accent)" : "transparent", color: ratings[key] >= n ? "white" : "var(--ink-3)" }}>{n}</button>
        ))}
      </div>
    </div>
  );
  const submit = () => {
    updateViewing(viewingId, {
      status: "completed",
      ratings: { layout: ratings.layout || null, light: ratings.light || null, noise: ratings.noise || null, condition: ratings.condition || null },
      notes: notes || undefined,
      tags: tags.length ? tags : undefined,
      decision: decision || null,
    });
    onClose();
  };
  return (
    <WsModal title={isEdit ? t("workspace.modal.title.editNotes") : t("workspace.modal.title.recordNotes")} onClose={onClose} onSubmit={submit} submitLabel={isEdit ? t("workspace.modal.submit.saveChanges") : t("workspace.modal.submit.markCompleted")}>
      <div style={{ background: "var(--surface-2)", borderRadius: 8, padding: "9px 12px", marginBottom: 18, fontFamily: "var(--font-ui)", fontSize: 12, color: "var(--ink-2)" }}>{fmtDate(v.date)} · {v.time} · {v.agent}</div>
      <div style={{ fontFamily: "var(--font-ui)", fontSize: 12, fontWeight: 600, color: "var(--ink-3)", letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 10 }}>{t("workspace.modal.ratings.heading")}</div>
      {ratingPicker("layout", t("workspace.modal.ratings.layout"))}
      {ratingPicker("light", t("workspace.modal.ratings.light"))}
      {ratingPicker("noise", t("workspace.modal.ratings.noise"))}
      {ratingPicker("condition", t("workspace.modal.ratings.condition"))}
      <Field label={t("workspace.modal.field.tags")}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginBottom: 7 }}>
          {tags.map((tag, i) => (
            <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "var(--surface-2)", borderRadius: 5, padding: "3px 8px", fontFamily: "var(--font-ui)", fontSize: 11 }}>
              {tag}
              <button onClick={() => setTags(ts => ts.filter((_, j) => j !== i))} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--ink-4)", padding: 0, fontSize: 13, lineHeight: 1, display: "flex" }}>×</button>
            </span>
          ))}
        </div>
        <div style={{ display: "flex", gap: 7 }}>
          <input style={{ ...inputCss, flex: 1 }} value={tagInput} onChange={e => setTagInput(e.target.value)} onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }} placeholder={t("workspace.modal.ph.addTag")} />
          <button onClick={addTag} style={{ padding: "8px 14px", background: "var(--surface-2)", border: "1px solid var(--rule)", borderRadius: 7, cursor: "pointer", fontFamily: "var(--font-ui)", fontSize: 12, color: "var(--ink-2)", whiteSpace: "nowrap" }}>{t("workspace.modal.btn.add")}</button>
        </div>
      </Field>
      <Field label={t("workspace.modal.field.notes")}><textarea style={{ ...inputCss, minHeight: 80, resize: "vertical" } as React.CSSProperties} value={notes} onChange={e => setNotes(e.target.value)} placeholder={t("workspace.modal.ph.notes")} /></Field>
      <Field label={t("workspace.modal.field.decision")}>
        <select style={selectCss} value={decision} onChange={e => setDecision(e.target.value)}>
          <option value="">{t("workspace.modal.decision.none")}</option>
          <option value="Proceed">{decisionLabel(t, "Proceed")}</option><option value="Deprioritise">{decisionLabel(t, "Deprioritise")}</option><option value="Rejected">{decisionLabel(t, "Rejected")}</option>
        </select>
      </Field>
    </WsModal>
  );
}

// ─── AddUnitModal ─────────────────────────────────────────────────────────────

function AddUnitModal({ propId, unitId, onClose }: { propId: string; unitId?: string; onClose: () => void }) {
  const t = useWsT();
  const { properties, addUnit, updateUnit } = useWsData();
  const existing = unitId ? properties.find(p => p.id === propId)?.units?.find(u => u.id === unitId) : undefined;
  const isEdit = !!existing;
  const [f, setF] = useState(() => existing ? {
    label: existing.label,
    floor: existing.floor ? String(existing.floor) : "",
    stack: existing.stack ?? "",
    facing: existing.facing || "North",
    size: existing.size ? String(existing.size) : "",
    price: existing.price ? String(existing.price) : "",
    maintenance: existing.maintenance && existing.maintenance !== "—" ? existing.maintenance : "",
    tenancy: existing.tenancy || "Vacant",
    tenancyEnd: existing.tenancyEnd ?? "",
    status: existing.status || "New",
  } : { label: "", floor: "", stack: "", facing: "North", size: "", price: "", maintenance: "", tenancy: "Vacant", tenancyEnd: "", status: "New" });
  const [labelManual, setLabelManual] = useState(isEdit);
  const [tried, setTried] = useState(false);

  const set = (k: string, v: string) => setF(prev => {
    const next = { ...prev, [k]: v };
    if ((k === "floor" || k === "stack") && !labelManual) {
      const fl = k === "floor" ? v : prev.floor;
      const st = k === "stack" ? v : prev.stack;
      next.label = fl || st ? `#${fl}-${st}` : "";
    }
    return next;
  });

  const labelError = tried && !f.label.trim() ? t("workspace.modal.err.unitLabel") : "";

  const submit = () => {
    if (!f.label.trim()) { setTried(true); return; }
    const price = parseFloat(f.price) || 0;
    const size = parseFloat(f.size) || 0;
    const psf = size > 0 && price > 0 ? Math.round(price / size) : 0;
    const tenancyEnd = f.tenancy !== "Vacant" && f.tenancyEnd ? f.tenancyEnd : undefined;
    if (isEdit && existing) {
      updateUnit(propId, existing.id, {
        label: f.label.trim(),
        floor: parseInt(f.floor) || 0,
        stack: f.stack,
        facing: f.facing,
        size, price, psf,
        maintenance: f.maintenance || "—",
        tenancy: f.tenancy,
        tenancyEnd,
        status: f.status,
      });
    } else {
      addUnit(propId, {
        id: `u_${Date.now()}`, label: f.label.trim(), floor: parseInt(f.floor) || 0,
        stack: f.stack, facing: f.facing, size, price, psf,
        maintenance: f.maintenance || "—", tenancy: f.tenancy,
        tenancyEnd,
        status: f.status,
      });
    }
    onClose();
  };
  return (
    <WsModal title={isEdit ? t("workspace.modal.title.editUnit") : t("workspace.modal.title.addUnit")} onClose={onClose} onSubmit={submit} submitLabel={isEdit ? t("workspace.modal.submit.saveChanges") : t("workspace.modal.submit.addUnit")}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <Field label={t("workspace.modal.field.floor")}><input style={inputCss} type="number" value={f.floor} onChange={e => set("floor", e.target.value)} placeholder={t("workspace.modal.ph.floor")} autoFocus={!isEdit} /></Field>
        <Field label={t("workspace.modal.field.stack")}><input style={inputCss} value={f.stack} onChange={e => set("stack", e.target.value)} placeholder={t("workspace.modal.ph.stack")} /></Field>
      </div>
      <Field label={t("workspace.modal.field.unitLabel")} error={labelError}>
        <input style={labelError ? errInputCss : inputCss} value={f.label} onChange={e => { setLabelManual(true); setF(p => ({ ...p, label: e.target.value })); }} placeholder="#24-07" />
      </Field>
      <Field label={t("workspace.modal.field.status")}>
        <select style={selectCss} value={f.status} onChange={e => set("status", e.target.value)}>
          {Object.keys(STATUS_COLORS).map(s => <option key={s} value={s}>{STATUS_KEYS[s] ? t(`workspace.status.${STATUS_KEYS[s]}`) : s}</option>)}
        </select>
      </Field>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <Field label={t("workspace.modal.field.facing")}>
          <select style={selectCss} value={f.facing} onChange={e => set("facing", e.target.value)}>
            {["North", "South", "East", "West", "NE", "NW", "SE", "SW", "—"].map(x => <option key={x} value={x}>{facingLabel(t, x)}</option>)}
          </select>
        </Field>
        <Field label={t("workspace.modal.field.size")}><input style={inputCss} type="number" value={f.size} onChange={e => set("size", e.target.value)} placeholder={t("workspace.modal.ph.size")} /></Field>
        <Field label={t("workspace.modal.field.askingPrice")}><input style={inputCss} type="number" value={f.price} onChange={e => set("price", e.target.value)} placeholder={t("workspace.modal.ph.askingPrice")} /></Field>
        <Field label={t("workspace.modal.field.maintenance")}><input style={inputCss} value={f.maintenance} onChange={e => set("maintenance", e.target.value)} placeholder={t("workspace.modal.ph.maintenance")} /></Field>
      </div>
      <Field label={t("workspace.modal.field.tenancy")}>
        <select style={selectCss} value={f.tenancy} onChange={e => set("tenancy", e.target.value)}>
          {["Vacant", "Tenanted", "Tenanted (ending soon)", "Owner-occupied"].map(x => <option key={x} value={x}>{tenancyLabel(t, x)}</option>)}
        </select>
      </Field>
      {f.tenancy !== "Vacant" && (
        <Field label={t("workspace.modal.field.tenancyEnds")}>
          <input style={inputCss} type="month" value={f.tenancyEnd} onChange={e => set("tenancyEnd", e.target.value)} />
        </Field>
      )}
    </WsModal>
  );
}

// ─── QuestionModal ────────────────────────────────────────────────────────────

function QuestionModal({ propId, qIdx, onClose, prefillStatus }: { propId: string; qIdx?: number; onClose: () => void; prefillStatus?: string }) {
  const t = useWsT();
  const { properties, openModal, addQuestion, updateQuestion } = useWsData();
  const prop = properties.find(p => p.id === propId);
  const existing = qIdx !== undefined ? prop?.questions[qIdx] : undefined;
  const knownUnits = prop?.units ?? [];
  const [qText, setQText] = useState(existing?.q ?? "");
  const [units, setUnits] = useState<string[]>(existing?.units ?? []);
  const [status, setStatus] = useState(prefillStatus ?? existing?.status ?? "Open");
  const [answer, setAnswer] = useState(existing?.answer ?? "");
  const [tried, setTried] = useState(false);
  const toggleUnit = (label: string) => setUnits(prev => prev.includes(label) ? prev.filter(x => x !== label) : [...prev, label]);
  const qError = tried && !qText.trim() ? t("workspace.modal.err.question") : "";
  const answerError = tried && status === "Answered" && !answer.trim() ? t("workspace.modal.err.answer") : "";
  const submit = () => {
    if (!qText.trim() || (status === "Answered" && !answer.trim())) { setTried(true); return; }
    const cleanedUnits = units.filter(l => knownUnits.some(ku => ku.label === l));
    const patch = { q: qText.trim(), units: cleanedUnits.length ? cleanedUnits : undefined, status, answer: status === "Answered" && answer.trim() ? answer.trim() : undefined };
    if (qIdx !== undefined) updateQuestion(propId, qIdx, patch);
    else addQuestion(propId, { q: qText.trim(), units: cleanedUnits.length ? cleanedUnits : undefined, status: "Open" });
    onClose();
  };
  return (
    <WsModal title={qIdx !== undefined ? t("workspace.modal.title.editQuestion") : t("workspace.modal.title.addQuestion")} onClose={onClose} onSubmit={submit} submitLabel={qIdx !== undefined ? t("workspace.modal.submit.saveChanges") : t("workspace.modal.submit.addQuestion")}>
      <Field label={t("workspace.modal.field.question")} error={qError}><textarea style={{ ...(qError ? errInputCss : inputCss), minHeight: 80, resize: "vertical" } as React.CSSProperties} value={qText} onChange={e => setQText(e.target.value)} placeholder={t("workspace.modal.ph.question")} autoFocus /></Field>
      <Field label={t("workspace.modal.field.reUnits")}>
        {knownUnits.length === 0 ? (
          <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", border: "1px dashed var(--rule)", borderRadius: 7, fontFamily: "var(--font-ui)", fontSize: 12, color: "var(--ink-4)" }}>
            <span style={{ fontStyle: "italic" }}>{t("workspace.modal.noUnits")}</span>
            <button type="button" onClick={() => { onClose(); openModal({ type: "addUnit", propId }); }} style={{ background: "transparent", border: "none", cursor: "pointer", color: "var(--accent)", fontFamily: "var(--font-ui)", fontSize: 12, padding: 0 }}>{t("workspace.modal.btn.addUnit")}</button>
          </div>
        ) : (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {knownUnits.map(ku => {
              const active = units.includes(ku.label);
              return (
                <button key={ku.id} type="button" onClick={() => toggleUnit(ku.label)}
                  style={{ padding: "5px 11px", borderRadius: 7, border: `1.5px solid ${active ? "var(--accent)" : "var(--rule)"}`, background: active ? "var(--accent)" : "transparent", color: active ? "white" : "var(--ink-2)", fontFamily: "var(--font-mono)", fontSize: 12, fontWeight: active ? 600 : 400, cursor: "pointer", transition: "all 100ms" }}>
                  {ku.label}
                </button>
              );
            })}
          </div>
        )}
      </Field>
      {qIdx !== undefined && (
        <Field label={t("workspace.modal.field.status")}>
          <select style={selectCss} value={status} onChange={e => setStatus(e.target.value)}>
            <option value="Open">{qStatusLabel(t, "Open")}</option><option value="Asked">{qStatusLabel(t, "Asked")}</option><option value="Answered">{qStatusLabel(t, "Answered")}</option>
          </select>
        </Field>
      )}
      {status === "Answered" && (
        <Field label={t("workspace.modal.field.answer")} error={answerError}>
          <textarea style={{ ...(answerError ? errInputCss : inputCss), minHeight: 80, resize: "vertical" } as React.CSSProperties} value={answer} onChange={e => setAnswer(e.target.value)} placeholder={t("workspace.modal.ph.answer")} />
        </Field>
      )}
    </WsModal>
  );
}

// ─── EditPropertyModal ────────────────────────────────────────────────────────

function EditPropertyModal({ propId, onClose }: { propId: string; onClose: () => void }) {
  const { properties, updateProperty } = useWsData();
  const prop = properties.find(p => p.id === propId);
  if (!prop) return null;
  // District / Tenure / TOP year / nearest MRT are NOT editable — they're
  // canonical project facts read from the data engine at API time, not
  // workspace state. The user owns only project_name (their label),
  // status, beds (their bedroom interest), AI summary, fit score.
  const [f, setF] = useState({ name: prop.name, status: prop.status, beds: prop.beds, aiSummary: prop.aiSummary, fitScore: prop.fitScore.toString() });
  const set = (k: string, v: string) => setF(p => ({ ...p, [k]: v }));
  const submit = () => {
    updateProperty(propId, { name: f.name || prop.name, project: f.name || prop.project, status: f.status, beds: f.beds, aiSummary: f.aiSummary, fitScore: parseFloat(f.fitScore) || prop.fitScore });
    onClose();
  };
  return (
    <WsModal title="Edit property" onClose={onClose} onSubmit={submit} submitLabel="Save changes">
      <Field label="Project name"><input style={inputCss} value={f.name} onChange={e => set("name", e.target.value)} autoFocus /></Field>
      <Field label="Status">
        <select style={selectCss} value={f.status} onChange={e => set("status", e.target.value)}>
          {Object.keys(STATUS_COLORS).map(s => <option key={s}>{s}</option>)}
        </select>
      </Field>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <Field label="Beds"><input style={inputCss} value={f.beds} onChange={e => set("beds", e.target.value)} /></Field>
        <Field label="Fit score (0–10)"><input style={inputCss} type="number" step="0.1" min="0" max="10" value={f.fitScore} onChange={e => set("fitScore", e.target.value)} /></Field>
      </div>
      <Field label="AI summary"><textarea style={{ ...inputCss, minHeight: 70, resize: "vertical" } as React.CSSProperties} value={f.aiSummary} onChange={e => set("aiSummary", e.target.value)} /></Field>
    </WsModal>
  );
}

// ─── EditLivabilityModal ──────────────────────────────────────────────────────

const LIVABILITY_OPTIONS: Record<string, string[]> = {
  naturalLight: ["", "Excellent", "Good", "Fair", "Poor"],
  noise: ["", "Very Quiet", "Quiet", "Moderate", "Noisy"],
  layout: ["", "Efficient", "Average", "Cramped"],
  wfh: ["", "Suitable", "Okay", "Not Suitable"],
};

function EditLivabilityModal({ propId, onClose }: { propId: string; onClose: () => void }) {
  const t = useWsT();
  const { properties, updateProperty } = useWsData();
  const prop = properties.find(p => p.id === propId);
  if (!prop) return null;
  const [f, setF] = useState({
    naturalLight: prop.livability?.naturalLight ?? "",
    noise: prop.livability?.noise ?? "",
    layout: prop.livability?.layout ?? "",
    wfh: prop.livability?.wfh ?? "",
  });
  const set = (k: string, v: string) => setF(p => ({ ...p, [k]: v }));
  const submit = () => {
    updateProperty(propId, { livability: {
      naturalLight: f.naturalLight || undefined, noise: f.noise || undefined,
      layout: f.layout || undefined, wfh: f.wfh || undefined,
    }});
    onClose();
  };
  const labels: [string, keyof typeof f][] = [
    [t("workspace.dossier.naturalLight"), "naturalLight"],
    [t("workspace.dossier.noise"), "noise"],
    [t("workspace.dossier.layout"), "layout"],
    [t("workspace.dossier.wfh"), "wfh"],
  ];
  return (
    <WsModal title={t("workspace.dossier.livabilitySignals")} onClose={onClose} onSubmit={submit} submitLabel="Save">
      {labels.map(([label, key]) => (
        <Field key={key} label={label}>
          <select style={selectCss} value={f[key]} onChange={e => set(key, e.target.value)}>
            {LIVABILITY_OPTIONS[key].map(o => <option key={o} value={o}>{o || "Unknown"}</option>)}
          </select>
        </Field>
      ))}
    </WsModal>
  );
}

// ─── LogMoveModal ─────────────────────────────────────────────────────────────
// Records one event in a unit's negotiation timeline. Bootstraps the
// blob from the unit's asking price on first move; subsequent moves
// patch one of buyerOffer / sellerCounter / buyerCall (with the
// matching `*On` date). Frontend stores prices in dollars, matching
// the design's mock data shape.

function LogMoveModal({ propId, unitId, onClose }: { propId: string; unitId: string; onClose: () => void }) {
  const t = useWsT();
  const { properties, updateUnit } = useWsData();
  const prop = properties.find(p => p.id === propId);
  const unit = prop?.units?.find(u => u.id === unitId);
  // Default the new move to whichever slot is most likely empty next.
  const offer = unit?.negotiation;
  const defaultKind: "buyerOffer" | "sellerCounter" | "buyerCall" =
    !offer || offer.buyerOffer == null ? "buyerOffer"
    : offer.sellerCounter == null ? "sellerCounter"
    : "buyerCall";
  const [kind, setKind] = useState<"buyerOffer" | "sellerCounter" | "buyerCall">(defaultKind);
  const [priceText, setPriceText] = useState("");
  // Default the date to today in ISO yyyy-mm-dd so the native date
  // picker opens with a sensible value instead of empty.
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));

  if (!unit) return null;

  const submit = () => {
    const priceNum = Number(priceText.replace(/[, ]/g, ""));
    if (!Number.isFinite(priceNum) || priceNum <= 0) return;
    const base: NegotiationOffer = offer ?? { asking: unit.price > 0 ? unit.price : priceNum };
    const next: NegotiationOffer = { ...base };
    if (kind === "buyerOffer") {
      next.buyerOffer = priceNum;
      next.buyerOfferOn = date || next.buyerOfferOn;
    } else if (kind === "sellerCounter") {
      next.sellerCounter = priceNum;
      next.sellerCounterOn = date || next.sellerCounterOn;
    } else {
      next.buyerCall = priceNum;
      next.buyerCallOn = date || next.buyerCallOn;
    }
    // Next-step is purely derived in NegotiationPanel from which slot
    // was last filled — we intentionally don't surface it as a user
    // field on this modal. Drop any stale value carried in the blob.
    delete next.nextStep;
    updateUnit(propId, unitId, { negotiation: next, status: "Negotiating" });
    onClose();
  };

  const kinds: Array<{ id: "buyerOffer" | "sellerCounter" | "buyerCall"; labelKey: string }> = [
    { id: "buyerOffer",    labelKey: "buyerOffer" },
    { id: "sellerCounter", labelKey: "sellerCounter" },
    { id: "buyerCall",     labelKey: "yourCall" },
  ];

  return (
    <WsModal title={t("workspace.logMove.title")} onClose={onClose} onSubmit={submit} submitLabel={t("workspace.logMove.submit")}>
      <Field label={t("workspace.logMove.moveType")}>
        <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
          {kinds.map(k => (
            <button key={k.id} type="button" onClick={() => setKind(k.id)}
              style={{
                padding: "5px 11px", borderRadius: 6,
                border: "1px solid " + (kind === k.id ? "var(--accent)" : "var(--rule)"),
                background: kind === k.id ? "var(--accent-soft)" : "transparent",
                color: kind === k.id ? "var(--accent)" : "var(--ink-2)",
                fontFamily: "var(--font-ui)", fontSize: 11.5, fontWeight: kind === k.id ? 600 : 400,
                cursor: "pointer",
              }}>
              {t(`workspace.negotiation.event.${k.labelKey}`)}
            </button>
          ))}
        </div>
      </Field>
      <Field label={t("workspace.logMove.price")}>
        <input style={selectCss} value={priceText} onChange={e => setPriceText(e.target.value)}
          inputMode="numeric" placeholder="1455000" />
      </Field>
      <Field label={t("workspace.logMove.date")}>
        <input type="date" style={selectCss} value={date} onChange={e => setDate(e.target.value)} />
      </Field>
    </WsModal>
  );
}

// ─── NextActions (derived from live workspace state) ──────────────────────────

function NextActions() {
  const t = useWsT();
  const { properties, viewings, openProperty, goToModule } = useWsData();

  const items: { type: "warn" | "info" | "ok"; text: string; action: string; onAction: () => void }[] = [];

  // Open questions
  properties.forEach(p => {
    const openQ = p.questions.filter(q => q.status === "Open").length;
    if (openQ > 0) items.push({ type: "warn", text: `${openQ} open question${openQ > 1 ? "s" : ""} for ${titleCaseSG(p.name)}`, action: "Open dossier", onAction: () => openProperty(p.id) });
  });

  // Upcoming viewings
  viewings.filter(v => v.status === "scheduled").forEach(v => {
    const prop = properties.find(p => p.id === v.propId);
    const label = prop ? titleCaseSG(prop.name) : "property";
    items.push({ type: "info", text: `Viewing for ${label}${v.unitLabel ? ` ${v.unitLabel}` : ""} on ${fmtDate(v.date)} at ${v.time}`, action: "View checklist", onAction: () => goToModule("viewings") });
  });

  // Viewed but no decision
  properties.filter(p => p.status === "Viewed").forEach(p => {
    const hasDecision = viewings.some(v => v.propId === p.id && v.decision);
    if (!hasDecision) items.push({ type: "ok", text: `${titleCaseSG(p.name)} viewed — record a decision`, action: "Open dossier", onAction: () => openProperty(p.id) });
  });

  if (items.length === 0) {
    return <div style={{ fontFamily: "var(--font-serif)", fontStyle: "italic", fontSize: 13, color: "var(--ink-4)", padding: "12px 0" }}>{t("workspace.overview.noActions")}</div>;
  }

  const styleFor = { warn: { bg: "var(--warn-bg)", c: "var(--warn)", dot: "#c87c00" }, info: { bg: "#ddeaf5", c: "#1a3e6a", dot: "#1a3e6a" }, ok: { bg: "var(--pos-bg)", c: "var(--pos)", dot: "var(--pos)" } };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
      {items.map((item, i) => {
        const s = styleFor[item.type];
        return (
          <div key={i} style={{ background: s.bg, borderRadius: 9, padding: "11px 13px", display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 6, height: 6, borderRadius: 6, background: s.dot, flexShrink: 0 }} />
            <div style={{ flex: 1, fontFamily: "var(--font-ui)", fontSize: 12.5, color: s.c, lineHeight: 1.4 }}>{item.text}</div>
            <button onClick={item.onAction} style={{ background: "rgba(255,255,255,0.7)", border: "1px solid rgba(0,0,0,0.06)", borderRadius: 6, padding: "4px 9px", cursor: "pointer", fontFamily: "var(--font-ui)", fontSize: 11, color: s.c, whiteSpace: "nowrap", fontWeight: 500 }}>{item.action}</button>
          </div>
        );
      })}
    </div>
  );
}

// ─── EditWorkspaceModal ───────────────────────────────────────────────────────

// Intent values match those produced by the signup wizard
// (Auth.tsx PreferencesScreen) — keep in sync with the four-value enum
// "buy" | "sell" | "rent" | "explore".
const INTENT_OPTS = ["buy", "sell", "rent", "explore"] as const;

function EditWorkspaceModal({ onClose }: { onClose: () => void }) {
  const t = useWsT();
  const { project, updateProject } = useWsData();
  const [name, setName] = useState(project.name);
  const [intent, setIntent] = useState<string>(project.intent || "");
  const [budget, setBudget] = useState<string>(() => bucketFromRange(project.budget_min, project.budget_max));
  const [areas, setAreas] = useState<string[]>(
    () => (project.areas === "—" ? "" : project.areas || "").split(",").map(a => a.trim()).filter(Boolean)
  );
  const [timeline, setTimeline] = useState<string>(project.timeline === "—" ? "" : project.timeline);
  const [priorities, setPriorities] = useState<Set<string>>(() => new Set(project.priorities));

  const toggle = (s: Set<string>, v: string) => {
    const next = new Set(s);
    if (next.has(v)) next.delete(v); else next.add(v);
    return next;
  };

  const submit = () => {
    if (!name.trim()) return;
    const bucket = budget ? BUDGET_BUCKETS[budget] : null;
    updateProject({
      name: name.trim(),
      intent: intent,
      areas: areas.length > 0 ? areas.join(",") : "—",
      timeline: timeline || "—",
      budget_min: bucket?.min ?? null,
      budget_max: bucket?.max ?? null,
      priorities: Array.from(priorities),
    });
    onClose();
  };

  const Chip = ({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) => (
    <button
      type="button"
      onClick={onClick}
      style={{
        borderRadius: 999,
        border: `1px solid ${active ? "var(--accent)" : "var(--rule)"}`,
        background: active ? "var(--accent-soft)" : "var(--surface)",
        color: active ? "var(--accent)" : "var(--ink-2)",
        fontWeight: active ? 500 : 400,
        padding: "5px 11px",
        fontFamily: "var(--font-ui)",
        fontSize: 12,
        cursor: "pointer",
      }}
    >{children}</button>
  );
  const ChipRow = ({ children }: { children: React.ReactNode }) => (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>{children}</div>
  );

  return (
    <WsModal title={t("workspace.editWorkspace.title")} onClose={onClose} onSubmit={submit} submitLabel={t("workspace.editWorkspace.save")}>
      <Field label={t("workspace.editWorkspace.name")}>
        <input style={inputCss} value={name} onChange={e => setName(e.target.value)} autoFocus placeholder="e.g. Buy CBD Condo" />
      </Field>
      <Field label={t("workspace.editWorkspace.intent")}>
        <ChipRow>
          {INTENT_OPTS.map(k => (
            <Chip key={k} active={intent === k} onClick={() => setIntent(intent === k ? "" : k)}>
              {t(`workspace.overview.intentOpts.${k}`)}
            </Chip>
          ))}
        </ChipRow>
      </Field>
      <Field label={t("auth.prefs.budget")}>
        <ChipRow>
          {BUDGET_KEYS.map(k => (
            <Chip key={k} active={budget === k} onClick={() => setBudget(budget === k ? "" : k)}>
              {t(`auth.prefs.budgets.${k}`)}
            </Chip>
          ))}
        </ChipRow>
      </Field>
      <Field label={t("workspace.editWorkspace.areas")}>
        <AreaMultiSelect value={areas} onChange={setAreas} />
      </Field>
      <Field label={t("workspace.editWorkspace.timeline")}>
        <ChipRow>
          {TIMELINE_OPTS.map(k => (
            <Chip key={k} active={timeline === k} onClick={() => setTimeline(timeline === k ? "" : k)}>
              {t(`auth.prefs.timelineOpts.${k}`)}
            </Chip>
          ))}
        </ChipRow>
      </Field>
      <Field label={t("workspace.editWorkspace.priorities")}>
        <ChipRow>
          {PRIORITY_OPTS.map(k => (
            <Chip key={k} active={priorities.has(k)} onClick={() => setPriorities(s => toggle(s, k))}>
              {t(`auth.prefs.priorityOpts.${k}`)}
            </Chip>
          ))}
        </ChipRow>
      </Field>
    </WsModal>
  );
}

// ─── WorkspaceModals ──────────────────────────────────────────────────────────

function WorkspaceModals({ modal, onClose }: { modal: ModalKind | null; onClose: () => void }) {
  if (!modal) return null;
  switch (modal.type) {
    case "addProperty":    return <AddPropertyModal onClose={onClose} />;
    case "editWorkspace":  return <EditWorkspaceModal onClose={onClose} />;
    case "addViewing":     return <ScheduleViewingModal prefillPropId={modal.propId} onClose={onClose} />;
    case "recordNotes":    return <RecordNotesModal viewingId={modal.viewingId} onClose={onClose} />;
    case "addUnit":        return <AddUnitModal propId={modal.propId} onClose={onClose} />;
    case "editUnit":       return <AddUnitModal propId={modal.propId} unitId={modal.unitId} onClose={onClose} />;
    case "addQuestion":    return <QuestionModal propId={modal.propId} onClose={onClose} />;
    case "editQuestion":   return <QuestionModal propId={modal.propId} qIdx={modal.qIdx} prefillStatus={modal.prefillStatus} onClose={onClose} />;
    case "editProperty":   return <EditPropertyModal propId={modal.propId} onClose={onClose} />;
    case "editLivability": return <EditLivabilityModal propId={modal.propId} onClose={onClose} />;
    case "logMove":        return <LogMoveModal propId={modal.propId} unitId={modal.unitId} onClose={onClose} />;
  }
}

// ─── EmptyWorkspaceCard ──────────────────────────────────────────────────────
// Shown when bootstrap completes successfully but no workspace exists
// for the signed-in user. Creation is intent-driven — the user opts in
// via the CTA, never as a side effect of navigating to /workspace.
function EmptyWorkspaceCard({ onCreate }: { onCreate: () => Promise<void> }) {
  const t = useWsT();
  const [busy, setBusy] = useState(false);
  const click = async () => {
    if (busy) return;
    setBusy(true);
    try { await onCreate(); } finally { setBusy(false); }
  };
  return (
    <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: 24, background: "var(--bg)" }}>
      <div style={{ maxWidth: 420, width: "100%", padding: 28, borderRadius: 12, background: "var(--surface)", border: "1px solid var(--rule)", textAlign: "center" }}>
        <div style={{ fontFamily: "var(--font-serif)", fontSize: 22, fontWeight: 500, color: "var(--ink)", letterSpacing: "-0.01em", marginBottom: 8 }}>
          {t("workspace.empty.title", { defaultValue: "No workspace yet" })}
        </div>
        <div style={{ fontFamily: "var(--font-ui)", fontSize: 13, color: "var(--ink-3)", lineHeight: 1.5, marginBottom: 20 }}>
          {t("workspace.empty.body", { defaultValue: "Workspaces collect the properties you're tracking, your viewings, and the AI summaries Propwise generates for you. Create one to get started." })}
        </div>
        <button
          onClick={click}
          disabled={busy}
          style={{
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            padding: "10px 18px", borderRadius: 8,
            background: "var(--accent)", color: "white", border: "none",
            fontFamily: "var(--font-ui)", fontSize: 13, fontWeight: 600, letterSpacing: "-0.005em",
            cursor: busy ? "wait" : "pointer", opacity: busy ? 0.6 : 1,
          }}
        >
          {busy
            ? t("workspace.empty.creating", { defaultValue: "Creating…" })
            : t("workspace.empty.cta", { defaultValue: "Create your workspace" })}
        </button>
      </div>
    </div>
  );
}

// ─── WorkspaceMobile ──────────────────────────────────────────────────────────

function WorkspaceMobile({ wsId }: { wsId?: string }) {
  const t = useWsT();
  const ds = useWsDataState(wsId);
  const navigate = useNavigate();
  const params = useParams<{ module: string; propId: string }>();
  const [modal, setModal] = useState<ModalKind | null>(null);
  const [aiOpen, setAiOpen] = useState(false);

  const dossierPropId = params.propId ?? null;
  const module = dossierPropId ? "dossier" : (params.module && VALID_MODULES.includes(params.module) ? params.module : "overview");

  const openProperty = (propId: string) => {
    if (wsId) navigate(`/workspace/${wsId}/shortlist/${propId}`);
  };

  const handleModuleSelect = (m: string) => {
    if (wsId) navigate(`/workspace/${wsId}/${m}`);
  };

  const ctxValue: WsData = {
    project: ds.project,
    properties: ds.properties, viewings: ds.viewings,
    openProperty, goToModule: handleModuleSelect, openModal: setModal,
    updateProject: ds.updateProject,
    addProperty: ds.addProperty, updateProperty: ds.updateProperty,
    addViewing: ds.addViewing, updateViewing: ds.updateViewing,
    addUnit: ds.addUnit, updateUnit: ds.updateUnit, deleteUnit: ds.deleteUnit, addQuestion: ds.addQuestion, updateQuestion: ds.updateQuestion,
    compareSummary: ds.compareSummary,
    compareUnitIds: ds.compareUnitIds,
    setCompareUnitIds: ds.setCompareUnitIds,
    refreshWorkspace: ds.refreshWorkspace,
  };

  const renderContent = () => {
    if (ds.wsMissing) return <EmptyWorkspaceCard onCreate={ds.createDefaultWorkspace} />;
    if (module === "dossier" && dossierPropId) {
      return <DossierView propId={dossierPropId} onBack={() => handleModuleSelect("shortlist")} />;
    }
    switch (module) {
      case "overview":  return <OverviewTab onOpenProperty={openProperty} />;
      case "shortlist": return <ShortlistTab onOpenProperty={openProperty} />;
      case "compare":   return <CompareTab />;
      case "viewings":  return <ViewingsTab onOpenProperty={openProperty} />;
      case "decision":  return <DecisionBoard onOpenProperty={openProperty} />;
      default:          return null;
    }
  };

  const scheduledCount = ds.viewings.filter(v => v.status === "scheduled").length;
  const aiModule = module === "dossier" ? "shortlist" : module;
  const isDossier = module === "dossier";

  return (
    <WsDataCtx.Provider value={ctxValue}>
    <WorkspaceModals modal={modal} onClose={() => setModal(null)} />
    {/* flex: 1 + minWidth: 0 so the workspace fills the row-direction
        flex parent (Workspace.tsx:4055). Without flex: 1 the column
        sizes to its widest intrinsic child, which on the Decision
        Board's column-stack content collapses to ~50% of the screen. */}
    <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", height: "100%", background: "var(--bg)", overflow: "hidden" }}>
      {/* Mobile header */}
      <div style={{ flexShrink: 0, height: 48, borderBottom: "1px solid var(--rule)", background: "var(--surface)", display: "flex", alignItems: "center", padding: "0 14px", gap: 10 }}>
        <div style={{ width: 26, height: 26, borderRadius: 6, background: "var(--accent)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <svg width="12" height="12" viewBox="0 0 14 14" fill="none"><path d="M2 11V5L7 2l5 3v6" stroke="white" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" /><path d="M5 11V8h4v3" stroke="white" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: "var(--font-ui)", fontSize: 14, fontWeight: 600, color: "var(--ink)", letterSpacing: "-0.01em", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{ds.project.name || "My Property Search"}</div>
          {module && module !== "dossier" && (
            <div style={{ fontFamily: "var(--font-ui)", fontSize: 12, color: "var(--ink-3)", marginTop: 0.5 }}>{t(`workspace.modules.${module}`)}</div>
          )}
        </div>
        {isDossier && (
          <button onClick={() => handleModuleSelect("shortlist")} style={{ display: "flex", alignItems: "center", gap: 4, background: "transparent", border: "none", cursor: "pointer", color: "var(--ink-3)", fontFamily: "var(--font-ui)", fontSize: 14, padding: "4px 6px", borderRadius: 6 }}>
            <svg width="13" height="13" viewBox="0 0 14 14" fill="none"><path d="M9 3l-4 4 4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
            {t("workspace.dossier.back")}
          </button>
        )}
      </div>

      {/* Content — full width, scrolls internally */}
      <div style={{ flex: 1, overflow: "hidden", minHeight: 0, display: "flex" }}>
        {renderContent()}
      </div>

      {/* Bottom tab bar — hidden in dossier view. Uses the shorter
          workspace.tabs.* labels (e.g. "看房" instead of the full
          "看房记录") so 4-char Chinese labels and "Decision Board"
          stay on a single line at narrow widths. */}
      {!isDossier && (
        <div style={{ flexShrink: 0, width: "100%", borderTop: "1px solid var(--rule)", background: "var(--surface)", display: "flex", paddingBottom: "var(--sab, env(safe-area-inset-bottom, 0px))" }}>
          {MODULES.map(m => {
            const badge = m.id === "viewings" ? scheduledCount.toString() : m.badge;
            const isActive = module === m.id;
            return (
              <button key={m.id} data-tour-id={`workspace.nav.${m.id}`} onClick={() => handleModuleSelect(m.id)} style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", alignItems: "center", gap: 4, padding: "11px 4px 9px", background: "transparent", border: "none", cursor: "pointer", color: isActive ? "var(--accent)" : "var(--ink-3)", position: "relative" }}>
                {/* Active indicator — full-tab-width line anchored to
                    the bottom edge of the bar. Anchors the active tab
                    visually so the eye reads "this column is selected"
                    rather than "there's a stray line floating mid-bar". */}
                {isActive && <div style={{ position: "absolute", left: 0, right: 0, bottom: 0, height: 2, background: "var(--accent)" }} />}
                <div style={{ position: "relative" }}>
                  <ModuleIcon id={m.id} />
                  {badge && parseInt(badge) > 0 && (
                    <span style={{ position: "absolute", top: -4, right: -6, width: 13, height: 13, borderRadius: "50%", background: "var(--accent)", color: "white", fontSize: 7.5, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center" }}>{badge}</span>
                  )}
                </div>
                <span style={{ fontFamily: "var(--font-ui)", fontSize: 11, fontWeight: isActive ? 600 : 400, letterSpacing: "-0.01em", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "100%" }}>{t(`workspace.tabs.${m.id}`)}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* Floating AI button */}
      {!aiOpen && <AIWorkspacePanel module={aiModule} collapsed={true} onToggle={() => setAiOpen(true)} mobile />}

      {/* AI bottom sheet */}
      {aiOpen && <AIWorkspacePanel module={aiModule} collapsed={false} onToggle={() => setAiOpen(false)} mobile />}
    </div>
    </WsDataCtx.Provider>
  );
}

// ─── WorkspaceDesktop ─────────────────────────────────────────────────────────

function WorkspaceDesktop({ wsId }: { wsId?: string }) {
  const t = useWsT();
  const ds = useWsDataState(wsId);
  const navigate = useNavigate();
  const params = useParams<{ module: string; propId: string }>();
  const [modal, setModal] = useState<ModalKind | null>(null);
  const [navCollapsed, setNavCollapsed] = useState(false);
  const [aiCollapsed, setAiCollapsed] = useState(false);

  const dossierPropId = params.propId ?? null;
  const module = dossierPropId ? "dossier" : (params.module && VALID_MODULES.includes(params.module) ? params.module : "overview");

  const openProperty = (propId: string) => {
    if (wsId) navigate(`/workspace/${wsId}/shortlist/${propId}`);
  };

  const handleModuleSelect = (m: string) => {
    if (wsId) navigate(`/workspace/${wsId}/${m}`);
  };

  const ctxValue: WsData = {
    project: ds.project,
    properties: ds.properties, viewings: ds.viewings,
    openProperty, goToModule: handleModuleSelect, openModal: setModal,
    updateProject: ds.updateProject,
    addProperty: ds.addProperty, updateProperty: ds.updateProperty,
    addViewing: ds.addViewing, updateViewing: ds.updateViewing,
    addUnit: ds.addUnit, updateUnit: ds.updateUnit, deleteUnit: ds.deleteUnit, addQuestion: ds.addQuestion, updateQuestion: ds.updateQuestion,
    compareSummary: ds.compareSummary,
    compareUnitIds: ds.compareUnitIds,
    setCompareUnitIds: ds.setCompareUnitIds,
    refreshWorkspace: ds.refreshWorkspace,
  };

  const renderContent = () => {
    if (ds.wsMissing) return <EmptyWorkspaceCard onCreate={ds.createDefaultWorkspace} />;
    if (module === "dossier" && dossierPropId) {
      return <DossierView propId={dossierPropId} onBack={() => handleModuleSelect("shortlist")} />;
    }
    switch (module) {
      case "overview":  return <OverviewTab onOpenProperty={openProperty} />;
      case "shortlist": return <ShortlistTab onOpenProperty={openProperty} />;
      case "compare":   return <CompareTab />;
      case "viewings":  return <ViewingsTab onOpenProperty={openProperty} />;
      case "decision":  return <DecisionBoard onOpenProperty={openProperty} />;
      default:          return null;
    }
  };

  const aiModule = module === "dossier" ? "shortlist" : module;
  const activeNavModule = module === "dossier" ? "shortlist" : module;

  return (
    <WsDataCtx.Provider value={ctxValue}>
    <WorkspaceModals modal={modal} onClose={() => setModal(null)} />
    <div style={{ display: "flex", flex: 1, height: "100%", overflow: "hidden" }}>
      <ProjectNav active={activeNavModule} onSelect={handleModuleSelect} collapsed={navCollapsed} onToggle={() => setNavCollapsed(v => !v)} />
      <div style={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden" }}>
        {module !== "dossier" && (
          <div style={{ height: 48, flexShrink: 0, borderBottom: "1px solid var(--rule)", display: "flex", alignItems: "center", padding: "0 28px", gap: 12, background: "var(--bg)" }}>
            <div style={{ fontFamily: "var(--font-ui)", fontSize: 11, color: "var(--ink-3)", letterSpacing: "0.06em", textTransform: "uppercase" }}>{ds.project.name || "My Property Search"}</div>
            <span style={{ color: "var(--rule-strong)" }}>/</span>
            <div style={{ fontFamily: "var(--font-serif)", fontSize: 14, fontWeight: 500, color: "var(--ink)", letterSpacing: "-0.01em" }}>
              {t(`workspace.modules.${module}`)}
            </div>
          </div>
        )}
        <div style={{ flex: 1, overflow: "hidden", display: "flex" }}>{renderContent()}</div>
      </div>
      <AIWorkspacePanel module={aiModule} collapsed={aiCollapsed} onToggle={() => setAiCollapsed(v => !v)} />
    </div>
    </WsDataCtx.Provider>
  );
}

// ─── Workspace (entry point) ──────────────────────────────────────────────────

export function Workspace() {
  const { t } = useTranslation();
  const { wsId } = useParams<{ wsId: string }>();
  const isMobile = useIsMobile();
  return (
    <WsTCtx.Provider value={t as TFn}>
      <div style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
        {/* Switcher row sits ABOVE the prefs banner so users can swap
            workspaces even while the welcome / nudge banners are shown. */}
        <div className="flex items-center gap-2 border-b border-border bg-surface-2 px-3 py-1.5">
          <WorkspaceSwitcher />
        </div>
        <WorkspacePrefsBanner />
        <div style={{ flex: 1, minHeight: 0, display: "flex" }}>
          {isMobile ? <WorkspaceMobile wsId={wsId} /> : <WorkspaceDesktop wsId={wsId} />}
        </div>
      </div>
      <WorkspaceTour />
    </WsTCtx.Provider>
  );
}

// Need React for JSX Fragment in ShortlistTab
import React from "react";
